import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { money, money2 } from '../utils/currency';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLicence } from '../contexts/LicenceContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Plus, ChevronLeft, ChevronRight, Bell, Trash2, Check, X as XIcon, AlertTriangle, GripVertical, Clock, Receipt, RefreshCw, Send, CalendarDays, Repeat, MapPin, Search, UserPlus, ChevronDown, User, Mail, Phone, PlusCircle, Eye, Play, Ban, CircleDot, Users, LayoutGrid, Gift, ExternalLink, Pencil, MoreVertical, FileText, DollarSign, CreditCard, Settings2, XCircle, MessageSquare, Sparkles } from 'lucide-react';
import { format, addDays, subDays, startOfWeek } from 'date-fns';
import { vi as viLocale } from 'date-fns/locale/vi';
import AIAssistantPanel from '../components/AIAssistantPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const START_HOUR = 9;
const END_HOUR = 20;
const DEFAULT_SLOT_HEIGHT = 20;
const SLOT_MINUTES = 15;
const SNAP_MINUTES = 5;
const TOTAL_SLOTS = ((END_HOUR - START_HOUR) * 60) / SLOT_MINUTES;

const STATUS_COLORS = {
  confirmed: { bg: '#556B2F', text: '#fff', border: '#4A5D23' },
  completed: { bg: '#D5D5D0', text: '#6B6B66', border: '#B0B0AB' },
  cancelled: { bg: '#E2725B', text: '#fff', border: '#C95A44' },
  'no-show': { bg: '#C0392B', text: '#fff', border: '#A93226' },
  arrived: { bg: '#2E86C1', text: '#fff', border: '#2471A3' },
  started: { bg: '#8E44AD', text: '#fff', border: '#7D3C98' },
  hold: { bg: '#87CEEB', text: '#1A2421', border: '#5DADE2' },
};

const STATUS_OPTIONS = [
  { value: 'confirmed', label: 'statusBooked', icon: CalendarDays },
  { value: 'completed', label: 'statusCompleted', icon: Check },
  { value: 'no-show', label: 'statusNoShow', icon: Ban, color: '#C0392B' },
  { value: 'cancelled', label: 'statusCancel', icon: XIcon, color: '#E2725B' },
];

function timeToMinutes(t) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function minutesToTime(m) {
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

function getSlotTimes() {
  const slots = [];
  for (let i = 0; i < TOTAL_SLOTS; i++) {
    const mins = START_HOUR * 60 + i * SLOT_MINUTES;
    slots.push(minutesToTime(mins));
  }
  return slots;
}

// Cross-browser safe UUID v4 generator. `safeUUID()` is only available in Safari 15.4+,
// older browsers (e.g. iMac Safari 14) throw "crypto.randomUUID is not a function". This helper
// uses `crypto.getRandomValues()` (universally supported since 2014) to build an RFC4122 v4 UUID.
function safeUUID() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return safeUUID();
    }
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const buf = new Uint8Array(16);
      crypto.getRandomValues(buf);
      buf[6] = (buf[6] & 0x0f) | 0x40; // version 4
      buf[8] = (buf[8] & 0x3f) | 0x80; // variant 10xx
      const hex = Array.from(buf, b => b.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch { /* fall through to last-resort */ }
  // Last-resort fallback (non-cryptographic but unique enough for client-side grouping ids).
  return 'uid-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 11);
}


function ServiceSearchPicker({ services, staff, formStaffId, bookingServices, onSelect, t }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const active = services.filter(s => s.is_active !== false);
  const filtered = search ? active.filter(s => s.name.toLowerCase().includes(search.toLowerCase())) : active;
  const selectedStaff = staff.find(s => s.id === formStaffId);
  const staffAssigned = selectedStaff?.assigned_services || [];

  const cats = {};
  filtered.forEach(s => { const cat = s.category || 'Uncategorised'; if (!cats[cat]) cats[cat] = []; cats[cat].push(s); });
  const sortedCats = Object.keys(cats).sort((a, b) => a === 'Uncategorised' ? 1 : b === 'Uncategorised' ? -1 : a.localeCompare(b));

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(!open)} data-testid="select-service"
        className="w-full flex items-center justify-between h-9 px-3 text-sm border border-[#E5E7E1] rounded-md bg-white text-left hover:bg-[#F9F8F6] transition-colors">
        <span className={bookingServices.length > 0 ? 'text-[#1A2421]' : 'text-[#5C6656]'}>
          {bookingServices.length > 0 ? t('appointments.addAnother') : t('appointments.selectService')}
        </span>
        <ChevronDown size={14} className="text-[#5C6656]" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7E1] rounded-lg shadow-lg overflow-hidden" data-testid="service-search-dropdown">
          <div className="p-2 border-b border-[#E5E7E1]">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C6656]" />
              <input className="w-full pl-8 pr-3 py-1.5 text-sm border border-[#E5E7E1] rounded-md focus:outline-none focus:border-[#556B2F]"
                placeholder="Search treatments..." value={search} onChange={e => setSearch(e.target.value)}
                data-testid="service-search-input" autoFocus />
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {sortedCats.length === 0 && <p className="px-3 py-4 text-sm text-[#5C6656] text-center">No treatments found</p>}
            {sortedCats.map(cat => (
              <div key={cat}>
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[#556B2F] bg-[#F9F8F6] sticky top-0">{cat}</div>
                {cats[cat].map(s => {
                  const canDo = !formStaffId || staffAssigned.length === 0 || staffAssigned.includes(s.id);
                  return (
                    <button type="button" key={s.id} onClick={() => { onSelect(s); setOpen(false); setSearch(''); }}
                      className="w-full text-left px-3 py-2 hover:bg-[#E9EDDF] transition-colors border-b border-[#E5E7E1]/30 last:border-0"
                      data-testid={`svc-pick-${s.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#1A2421]">{s.name}</span>
                        <span className="text-xs text-[#5C6656] whitespace-nowrap ml-2">{s.duration}min · {money(s.price)}</span>
                      </div>
                      {!canDo && formStaffId && <span className="text-[10px] text-[#856404] bg-[#FFF3CD] px-1.5 py-0.5 rounded inline-block mt-0.5">Team member doesn't provide this</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


export default function AppointmentsPage() {
  const routeLocation = useLocation();
  const { user: authUser } = useAuth();
  const licence = useLicence();
  const isOwner = authUser?.role === 'admin';
  const [currentDate, setCurrentDate] = useState(new Date());
  // --- Per-view calendar data cache (localStorage) ---
  // Stores a separate snapshot for each (date-range + location) the user has visited.
  // Lets us hydrate INSTANTLY whenever the user revisits a previously-loaded view
  // (refresh, next/prev day, jumping back to today) instead of showing a blank skeleton.
  // Fresh data still loads in the background and replaces the snapshot atomically.
  const CAL_CACHE_KEY = `apptio_cal_cache_v2_${authUser?.id || 'anon'}`;
  const CAL_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h
  const CAL_CACHE_MAX_ENTRIES = 30;
  const readCalCacheMap = () => {
    try {
      const raw = localStorage.getItem(CAL_CACHE_KEY);
      if (!raw) return {};
      const map = JSON.parse(raw);
      return (map && typeof map === 'object') ? map : {};
    } catch { return {}; }
  };
  const readCalCacheEntry = (viewKey) => {
    const map = readCalCacheMap();
    const entry = map[viewKey];
    if (!entry || !entry.ts || (Date.now() - entry.ts) > CAL_CACHE_TTL_MS) return null;
    return entry;
  };
  const writeCalCacheEntry = (viewKey, entry) => {
    try {
      const map = readCalCacheMap();
      map[viewKey] = { ...entry, ts: Date.now() };
      // Evict oldest entries if we're over the cap.
      const entries = Object.entries(map);
      if (entries.length > CAL_CACHE_MAX_ENTRIES) {
        entries.sort((a, b) => (a[1].ts || 0) - (b[1].ts || 0));
        const toEvict = entries.slice(0, entries.length - CAL_CACHE_MAX_ENTRIES);
        for (const [k] of toEvict) delete map[k];
      }
      localStorage.setItem(CAL_CACHE_KEY, JSON.stringify(map));
    } catch { /* localStorage may be full / disabled */ }
  };
  // Compute the initial viewKey synchronously so we can hydrate state from cache before first paint.
  const initialViewDates = (() => {
    const cv = (typeof window !== 'undefined' && localStorage.getItem('cal_view')) || 'day';
    const days = cv === 'week' ? 7 : cv === '3day' ? 3 : 1;
    const today = new Date();
    const start = cv === 'week' ? startOfWeek(today, { weekStartsOn: 1 }) : today;
    return Array.from({ length: days }, (_, i) => format(addDays(start, i), 'yyyy-MM-dd'));
  })();
  const initialLocation = (typeof window !== 'undefined' && localStorage.getItem('cal_selected_location')) || '';
  const initialViewKey = `${initialViewDates.join(',')}|${initialLocation}`;
  const initialCache = readCalCacheEntry(initialViewKey);
  // Tracks the last viewKey we hydrated from cache, so we don't re-hydrate on every auto-refresh.
  const hydratedKeyRef = useRef(initialCache ? initialViewKey : null);
  const [nowMinutes, setNowMinutes] = useState(() => {
    // Seed in business-local time on first paint if we have a cached timezone, so the red line is correct
    // on hard refresh BEFORE the /settings/business response lands.
    try {
      const tz = localStorage.getItem('apptio_business_tz');
      if (tz) {
        const parts = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz }).formatToParts(new Date());
        const h = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
        const m = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
        return (h % 24) * 60 + m;
      }
    } catch {}
    const n = new Date();
    return n.getHours() * 60 + n.getMinutes();
  });
  const [slotHeight, setSlotHeight] = useState(() => {
    const saved = localStorage.getItem('cal_zoom');
    return saved ? parseInt(saved) : DEFAULT_SLOT_HEIGHT;
  });
  const SLOT_HEIGHT = slotHeight;
  const [calSettingsOpen, setCalSettingsOpen] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(null);
  const [cancelReason, setCancelReason] = useState('No reason provided');
  const [cancelNotify, setCancelNotify] = useState(true);
  const [noShowDialog, setNoShowDialog] = useState(null);
  const [noShowNotify, setNoShowNotify] = useState(true);
  const [calFontSize, setCalFontSize] = useState(() => parseInt(localStorage.getItem('cal_font_size') || '10'));
  const [calConfirmColor, setCalConfirmColor] = useState(() => localStorage.getItem('cal_confirm_color') || '#556B2F');
  // Sync calendar prefs from logged-in user profile (persists across devices & sessions)
  useEffect(() => {
    const prefs = authUser?.preferences;
    if (!prefs) return;
    if (prefs.cal_confirm_color && prefs.cal_confirm_color !== calConfirmColor) {
      setCalConfirmColor(prefs.cal_confirm_color);
      localStorage.setItem('cal_confirm_color', prefs.cal_confirm_color);
    }
    if (prefs.cal_font_size && parseInt(prefs.cal_font_size) !== calFontSize) {
      setCalFontSize(parseInt(prefs.cal_font_size));
      localStorage.setItem('cal_font_size', String(prefs.cal_font_size));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.preferences?.cal_confirm_color, authUser?.preferences?.cal_font_size]);
  // Debounced save to backend so rapid color picker changes don't spam the API
  const savePrefTimer = useRef(null);
  const savePref = useCallback((key, value) => {
    if (savePrefTimer.current) clearTimeout(savePrefTimer.current);
    savePrefTimer.current = setTimeout(() => {
      axios.patch(`${API}/user/preferences`, { [key]: value }, { withCredentials: true }).catch(() => {});
    }, 400);
  }, []);
  const statusColors = useMemo(() => ({
    ...STATUS_COLORS,
    confirmed: { bg: calConfirmColor, text: '#fff', border: calConfirmColor },
  }), [calConfirmColor]);
  const [appointments, setAppointments] = useState(() => initialCache?.appointments || []);
  const [services, setServices] = useState(() => initialCache?.services || []);
  const [staff, setStaff] = useState(() => initialCache?.staff || []);
  const [bookDialogOpen, setBookDialogOpen] = useState(false);
  const [checkoutDialog, setCheckoutDialog] = useState(false);
  const [checkoutAppt, setCheckoutAppt] = useState(null);
  const [checkoutReceipt, setCheckoutReceipt] = useState(null);
  const [receiptSent, setReceiptSent] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState('');
  const [rebookDate, setRebookDate] = useState('');
  const [rebookTime, setRebookTime] = useState('');
  const [dragAppt, setDragAppt] = useState(null);
  const lastDropRef = useRef(0);
  const dragSourceRef = useRef(null); // 'html5' or 'touch'
  const dragGrabOffsetRef = useRef(0); // px offset from block top where the user grabbed
  const touchActiveRef = useRef(false);
  const [resizing, setResizing] = useState(null); // { apptId, startY, origDuration }
  const [rebookMode, setRebookMode] = useState(null);
  const [blockPlacementMode, setBlockPlacementMode] = useState(false);
  const blockPlaceRef = useRef(null);
  const [apptPlacementMode, setApptPlacementMode] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [bookingServices, setBookingServices] = useState([]);
  const [sidebarServiceSearch, setSidebarServiceSearch] = useState('');
  const [popularServices, setPopularServices] = useState([]);
  const [sidebarGroup, setSidebarGroup] = useState([]);
  const [sidebarAppt, setSidebarAppt] = useState(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [clientDetail, setClientDetail] = useState(null);
  const [sidebarClientEdit, setSidebarClientEdit] = useState(false);
  const [sidebarClientSwap, setSidebarClientSwap] = useState(false);
  const [sidebarNewClient, setSidebarNewClient] = useState(null); // null | {name, last_name, email, phone}
  const [sidebarClientEditForm, setSidebarClientEditForm] = useState(null);
  const [sidebarClientProfile, setSidebarClientProfile] = useState(null);
  const [sidebarClientActivity, setSidebarClientActivity] = useState([]);
  const [profileTab, setProfileTab] = useState('overview');
  const [blockClientDialog, setBlockClientDialog] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [sidebarClientSearch, setSidebarClientSearch] = useState('');
  const [sidebarClientResults, setSidebarClientResults] = useState([]);
  const [sidebarReceiptEmail, setSidebarReceiptEmail] = useState('');
  const [sidebarShowReceipt, setSidebarShowReceipt] = useState(false);
  const [sidebarInvoice, setSidebarInvoice] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [calendarView, setCalendarView] = useState('day'); // 'day', '3day', 'week'
  const [form, setForm] = useState({ client_id: '', service_id: '', staff_id: '', date: '', start_time: '09:00', notes: '', recurrence: 'none', recurrence_count: 0 });
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientHistory, setClientHistory] = useState([]);
  const [isWalkIn, setIsWalkIn] = useState(false);
  const [walkInName, setWalkInName] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [showClientActions, setShowClientActions] = useState(false);
  const [showEditClient, setShowEditClient] = useState(false);
  const [editClientData, setEditClientData] = useState(null);
  const [newClientForm, setNewClientForm] = useState({ name: '', last_name: '', email: '', phone: '', birthday: '', gender: '' });
  const [staffFilterMode, setStaffFilterMode] = useState('scheduled'); // 'scheduled', 'all', 'custom'
  const [staffFilterOpen, setStaffFilterOpen] = useState(false);
  const [selectedStaffIds, setSelectedStaffIds] = useState(new Set());
  const [staffHeaderMenu, setStaffHeaderMenu] = useState(null); // { staffId, x, y }
  const [slotPopup, setSlotPopup] = useState(null); // { staffId, time, x, y }
  const [blockDialog, setBlockDialog] = useState(false);
  const [blockForm, setBlockForm] = useState({ staff_id: '', date: '', start_time: '', end_time: '', reason: '' });
  const [editingBlock, setEditingBlock] = useState(null); // Sidebar edit for existing block
  const [activeBlock, setActiveBlock] = useState(null); // Block in "hold" adjustment mode { id, original, current }
  const blockLongPressRef = useRef(null);
  const blockDragRef = useRef(null); // 'move' or 'resize'
  const [blockedTimes, setBlockedTimes] = useState(() => initialCache?.blockedTimes || []);
  const [schedOverrides, setSchedOverrides] = useState(() => initialCache?.schedOverrides || []);
  // Tracks the date-range + location the calendar data is currently loaded for.
  // Used to prevent stale staff-filtering during date navigation (avoids "wrong staff briefly appear" flash).
  const [loadedForKey, setLoadedForKey] = useState(() => initialCache?.loadedForKey || null);
  const [closedDates, setClosedDates] = useState([]);
  const [googleReviewUrl, setGoogleReviewUrl] = useState('');
  // Business timezone — used to keep the "now" red line on the calendar anchored to the business's local time
  // (so a salon admin travelling abroad still sees the UK calendar with a UK-local red line).
  const [businessTimezone, setBusinessTimezone] = useState(() => {
    try { return localStorage.getItem('apptio_business_tz') || ''; } catch { return ''; }
  });
  const computeNowMinutesInTz = useCallback((tz) => {
    try {
      if (!tz) {
        const n = new Date();
        return n.getHours() * 60 + n.getMinutes();
      }
      const parts = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz }).formatToParts(new Date());
      const h = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
      const m = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
      return (h % 24) * 60 + m;
    } catch {
      const n = new Date();
      return n.getHours() * 60 + n.getMinutes();
    }
  }, []);
  const [saleSidebar, setSaleSidebar] = useState(false);
  const [saleTab, setSaleTab] = useState('services');
  const [saleCart, setSaleCart] = useState([]);
  const [saleClient, setSaleClient] = useState(null);
  const [saleClientSearch, setSaleClientSearch] = useState('');
  const [saleClientResults, setSaleClientResults] = useState([]);
  const [saleGiftValue, setSaleGiftValue] = useState('');
  const [saleGiftRecipient, setSaleGiftRecipient] = useState('');
  const [saleGiftEmail, setSaleGiftEmail] = useState('');
  const [saleComplete, setSaleComplete] = useState(null); // { vouchers: [{code, value}...] }
  const [saleEmailTo, setSaleEmailTo] = useState('');
  // Notifications panel
  const [notifPanel, setNotifPanel] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [salePayStep, setSalePayStep] = useState(false);
  const clientSearchRef = useRef(null);
  const searchTimeout = useRef(null);
  const calRef = useRef(null);
  const calHeaderRef = useRef(null);
  const swipeRef = useRef({ startX: 0, startY: 0, startTime: 0, tracking: false, lastX: 0, lastTime: 0, velocity: 0 });
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeTransition, setSwipeTransition] = useState(false);

  const swipeStep = calendarView === 'day' ? 1 : calendarView === '3day' ? 3 : 7;

  // Fresha-style continuous swipe with momentum
  // When grid is horizontally scrollable (3+ staff), only allow date swipe at the edges:
  //   - Scrolled all the way RIGHT + swipe left → next date
  //   - Scrolled all the way LEFT + swipe right → previous date
  //   - Otherwise → let native horizontal scroll handle it
  const getGridScrollEdge = (dx) => {
    const grid = calRef.current;
    if (!grid || grid.scrollWidth <= grid.clientWidth + 10) return 'not-scrollable';
    const atLeft = grid.scrollLeft <= 2;
    const atRight = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 2;
    if (atLeft && dx > 0) return 'allow'; // at start, swiping right → prev date
    if (atRight && dx < 0) return 'allow'; // at end, swiping left → next date
    return 'block';
  };
  const handleCalendarTouchStart = (e) => {
    if (touchDrag?.active || resizing || activeBlock || touchEditMode) return;
    const t = e.touches[0];
    swipeRef.current = { startX: t.clientX, startY: t.clientY, startTime: Date.now(), tracking: false, locked: false, lastX: t.clientX, lastTime: Date.now(), velocity: 0, edgeChecked: false };
  };
  const handleCalendarTouchMove = (e) => {
    if (touchDrag?.active || resizing || activeBlock || swipeRef.current.locked || touchEditMode) return;
    const t = e.touches[0];
    const dx = t.clientX - swipeRef.current.startX;
    const dy = t.clientY - swipeRef.current.startY;
    if (!swipeRef.current.tracking) {
      if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        // Check edge state at the moment we decide this is a horizontal swipe
        if (!swipeRef.current.edgeChecked) {
          swipeRef.current.edgeChecked = true;
          const edge = getGridScrollEdge(dx);
          if (edge === 'block') {
            // Grid is scrollable and NOT at the relevant edge — let browser scroll the grid
            swipeRef.current.locked = true;
            return;
          }
          // 'allow' or 'not-scrollable' → proceed with date swipe
        }
        swipeRef.current.tracking = true;
      } else if (Math.abs(dy) > 12) {
        swipeRef.current.locked = true;
        return;
      } else { return; }
    }
    e.preventDefault();
    // Track velocity
    const now = Date.now();
    const dt = now - swipeRef.current.lastTime;
    if (dt > 0) swipeRef.current.velocity = (t.clientX - swipeRef.current.lastX) / dt;
    swipeRef.current.lastX = t.clientX;
    swipeRef.current.lastTime = now;
    setSwipeOffset(dx);
  };
  const handleCalendarTouchEnd = () => {
    if (!swipeRef.current.tracking) { setSwipeOffset(0); return; }
    const width = window.innerWidth;
    const threshold = width * 0.3;
    const velocity = swipeRef.current.velocity; // px/ms
    // Trigger page change if: dragged >30% OR velocity is high enough (fast flick)
    if (Math.abs(swipeOffset) > threshold || Math.abs(velocity) > 0.5) {
      const direction = (Math.abs(swipeOffset) > threshold) ? (swipeOffset > 0 ? 1 : -1) : (velocity > 0 ? 1 : -1);
      setSwipeTransition(true);
      setSwipeOffset(direction * width);
      setTimeout(() => {
        setSwipeTransition(false);
        setSwipeOffset(0);
        if (direction > 0) setCurrentDate(d => subDays(d, swipeStep));
        else setCurrentDate(d => addDays(d, swipeStep));
      }, 180);
    } else {
      setSwipeTransition(true);
      setSwipeOffset(0);
      setTimeout(() => setSwipeTransition(false), 180);
    }
    swipeRef.current.tracking = false;
  };

  const dateStr = format(currentDate, 'yyyy-MM-dd');
  const slotTimes = getSlotTimes();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'vi' ? { locale: viLocale } : {};
  const fmt = (date, pattern) => format(date, pattern, dateLocale);

  // Block placement mode: document-level touch handlers for drag-to-select
  useEffect(() => {
    if (!blockPlacementMode) return;
    const sh = SLOT_HEIGHT;
    const onMove = (e) => {
      const ref = blockPlaceRef.current;
      if (!ref) return;
      e.preventDefault();
      const t = e.touches[0];
      const dy = Math.max(0, t.clientY - ref.startY);
      const slots = Math.max(1, Math.round(dy / (sh / 3)));
      const durMins = slots * 5;
      const newHeight = Math.max(sh, (durMins / 15) * sh);
      const endMins = ref.startMins + durMins;
      const endTime = minutesToTime(endMins);
      const preview = document.getElementById('block-place-preview');
      if (preview) {
        preview.style.height = `${newHeight}px`;
        preview.innerHTML = `<span style="font-size:11px;font-weight:700;color:#3A3D45;white-space:nowrap;">${ref.startTime} - ${endTime}</span>`;
      }
      ref._endMins = endMins;
    };
    const onEnd = () => {
      const ref = blockPlaceRef.current;
      const preview = document.getElementById('block-place-preview');
      if (preview) preview.remove();
      if (ref) {
        const endMins = ref._endMins || (ref.startMins + 15);
        const endTime = minutesToTime(Math.max(endMins, ref.startMins + 15));
        setBlockForm({ staff_id: ref.staffId, date: dateStr, start_time: ref.startTime, end_time: endTime, reason: '' });
        setBlockDialog(true);
        setBlockPlacementMode(false);
      }
      blockPlaceRef.current = null;
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    return () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      const p = document.getElementById('block-place-preview');
      if (p) p.remove();
    };
  }, [blockPlacementMode, dateStr]);


  const viewDays = calendarView === 'week' ? 7 : calendarView === '3day' ? 3 : 1;
  // Week view anchors on Monday (ISO weekStart=1). Day and 3-day views use the picked date directly.
  const viewStartDate = calendarView === 'week' ? startOfWeek(currentDate, { weekStartsOn: 1 }) : currentDate;
  const viewDates = Array.from({ length: viewDays }, (_, i) => format(addDays(viewStartDate, i), 'yyyy-MM-dd'));

  // Optimistic hydration: when the visible date-range or location changes, instantly paint any
  // cached snapshot we have for that view BEFORE the browser paints. This eliminates the blank
  // "loading skeleton" flash when the user navigates to a previously-viewed date.
  // useLayoutEffect ensures the cached state is committed before the first paint of the new view.
  useLayoutEffect(() => {
    const currentKey = `${viewDates.join(',')}|${selectedLocation || ''}`;
    if (hydratedKeyRef.current === currentKey) return;
    const cached = readCalCacheEntry(currentKey);
    if (cached) {
      if (Array.isArray(cached.staff)) setStaff(cached.staff);
      if (Array.isArray(cached.services)) setServices(cached.services);
      if (Array.isArray(cached.schedOverrides)) setSchedOverrides(cached.schedOverrides);
      if (Array.isArray(cached.appointments)) setAppointments(cached.appointments);
      if (Array.isArray(cached.blockedTimes)) setBlockedTimes(cached.blockedTimes);
      setLoadedForKey(currentKey);
    }
    hydratedKeyRef.current = currentKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr, selectedLocation, calendarView]);

  const fetchAll = useCallback(async () => {
    try {
      const locParam = selectedLocation ? `&location_id=${selectedLocation}` : '';
      const baseFetches = [
        axios.get(`${API}/services`, { withCredentials: true }),
        axios.get(`${API}/staff`, { withCredentials: true }),
        axios.get(`${API}/locations`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/closed-dates`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/business`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/services/popular`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/staff/schedule-overrides?date_from=${viewDates[0]}&date_to=${viewDates[viewDates.length-1]}`, { withCredentials: true }).catch(() => ({ data: [] }))
      ];
      // Fetch appointments and blocks for all view days
      const apptFetches = viewDates.map(d => axios.get(`${API}/appointments?date=${d}${locParam}`, { withCredentials: true }).catch(() => ({ data: [] })));
      const blockFetches = viewDates.map(d => axios.get(`${API}/blocked-times?date=${d}`, { withCredentials: true }).catch(() => ({ data: [] })));
      const results = await Promise.all([...baseFetches, ...apptFetches, ...blockFetches]);
      // Set schedule overrides FIRST so isStaffScheduledForDate computes correctly
      // when staff state updates in the same React batch (prevents greyed-out flicker on refresh).
      setSchedOverrides(Array.isArray(results[6].data) ? results[6].data : []);
      setServices(results[0].data);
      setStaff(results[1].data);
      setLocations(results[2].data);
      setClosedDates(Array.isArray(results[3].data) ? results[3].data.map(d => d.date) : []);
      setGoogleReviewUrl(results[4].data?.google_review_url || '');
      // Pick up business timezone for the "now" red line so it stays in business-local time.
      const tz = results[4].data?.timezone || '';
      if (tz) {
        setBusinessTimezone(tz);
        try { localStorage.setItem('apptio_business_tz', tz); } catch {}
        setNowMinutes(computeNowMinutesInTz(tz));
      }
      setPopularServices(Array.isArray(results[5].data) ? results[5].data : []);
      const allAppts = [];
      for (let i = 0; i < viewDays; i++) allAppts.push(...results[7 + i].data);
      setAppointments(allAppts);
      const allBlocks = [];
      for (let i = 0; i < viewDays; i++) allBlocks.push(...results[7 + viewDays + i].data);
      // Dedupe blocks: collapse exact duplicates (same staff_id+date+start_time+end_time) caused by
      // accidental double-submits or legacy data, so the calendar doesn't render two tiles side by side.
      const seenBlockKeys = new Set();
      const dedupedBlocks = [];
      for (const b of allBlocks) {
        const k = `${b.staff_id || ''}|${b.date || ''}|${b.start_time || ''}|${b.end_time || ''}|${b.reason || ''}`;
        if (seenBlockKeys.has(k)) continue;
        seenBlockKeys.add(k);
        dedupedBlocks.push(b);
      }
      setBlockedTimes(dedupedBlocks);
      // Mark calendar data as loaded for this date-range + location.
      // Render code reads this to avoid filtering stale staff during in-flight date navigations.
      const newKey = `${viewDates.join(',')}|${selectedLocation || ''}`;
      setLoadedForKey(newKey);
      // Persist this view's snapshot so navigating back (or refreshing) hydrates instantly
      // without a "No scheduled team members" / blank-grid flash.
      writeCalCacheEntry(newKey, {
        staff: results[1].data,
        services: results[0].data,
        schedOverrides: Array.isArray(results[6].data) ? results[6].data : [],
        appointments: allAppts,
        blockedTimes: dedupedBlocks,
      });
    } catch (err) {
      console.error(err);
    }
  }, [dateStr, selectedLocation, calendarView]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Background pre-fetch of adjacent views (±1 nav step, plus ±2/±3 for power-users) so day-by-day
  // navigation hydrates instantly from cache. Reuses in-memory staff (date-independent) to keep prefetch lean.
  useEffect(() => {
    const currentKey = `${viewDates.join(',')}|${selectedLocation || ''}`;
    if (loadedForKey !== currentKey) return; // wait until current view is loaded
    const step = calendarView === 'week' ? 7 : calendarView === '3day' ? 3 : 1;
    // Pre-fetch only ±1 nav step to keep server load reasonable. The wider window (±2/±3) was
    // flooding the backend with ~18 requests per view change and made the calendar feel sluggish.
    const offsets = [-step, step];
    const locParam = selectedLocation ? `&location_id=${selectedLocation}` : '';
    let cancelled = false;
    const snapshotStaff = staff;
    const snapshotServices = services;
    const prefetch = async (offset) => {
      if (cancelled) return;
      try {
        const start = calendarView === 'week' ? startOfWeek(addDays(currentDate, offset), { weekStartsOn: 1 }) : addDays(currentDate, offset);
        const dates = Array.from({ length: viewDays }, (_, i) => format(addDays(start, i), 'yyyy-MM-dd'));
        const key = `${dates.join(',')}|${selectedLocation || ''}`;
        if (readCalCacheEntry(key)) return; // already cached
        // Only fetch date-dependent endpoints (staff list is reused from in-memory state).
        const ovP = axios.get(`${API}/staff/schedule-overrides?date_from=${dates[0]}&date_to=${dates[dates.length-1]}`, { withCredentials: true }).catch(() => ({ data: [] }));
        const apptPs = dates.map(d => axios.get(`${API}/appointments?date=${d}${locParam}`, { withCredentials: true }).catch(() => ({ data: [] })));
        const blockPs = dates.map(d => axios.get(`${API}/blocked-times?date=${d}`, { withCredentials: true }).catch(() => ({ data: [] })));
        const [ov, ...rest] = await Promise.all([ovP, ...apptPs, ...blockPs]);
        if (cancelled) return;
        const apptRes = rest.slice(0, viewDays);
        const blockRes = rest.slice(viewDays);
        const allAppts = [];
        for (let i = 0; i < viewDays; i++) allAppts.push(...apptRes[i].data);
        const allBlocks = [];
        for (let i = 0; i < viewDays; i++) allBlocks.push(...blockRes[i].data);
        writeCalCacheEntry(key, {
          staff: snapshotStaff,
          services: snapshotServices,
          schedOverrides: Array.isArray(ov.data) ? ov.data : [],
          appointments: allAppts,
          blockedTimes: allBlocks,
        });
      } catch { /* silent */ }
    };
    // Fire all offsets in parallel after one rAF (frame yield) so we don't block the main thread paint of the current view.
    const raf = requestAnimationFrame(() => {
      offsets.forEach(prefetch);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedForKey, dateStr, selectedLocation, calendarView]);

  // Handle mobile bottom nav "Add" actions
  useEffect(() => {
    if (routeLocation.state?.openBooking) {
      setApptPlacementMode(true);
      window.history.replaceState({}, document.title);
    } else if (routeLocation.state?.openBlocked) {
      setBlockPlacementMode(true);
      window.history.replaceState({}, document.title);
    } else if (routeLocation.state?.openSale) {
      setSaleSidebar(true); setSaleTab('services'); setSaleCart([]); setSaleClient(null); setSalePayStep(false); setSaleComplete(null);
      window.history.replaceState({}, document.title);
    }
  }, [routeLocation.state]);

  // Sync horizontal scroll between header and calendar grid
  useEffect(() => {
    const grid = calRef.current;
    if (!grid) return;
    const syncScroll = () => {
      if (calHeaderRef.current) calHeaderRef.current.scrollLeft = grid.scrollLeft;
    };
    grid.addEventListener('scroll', syncScroll, { passive: true });
    return () => grid.removeEventListener('scroll', syncScroll);
  });

  // Lock native scrolling during touch edit mode (iOS Safari)
  const touchEditActiveRef = useRef(false);
  useEffect(() => {
    const isActive = !!touchEditMode;
    if (isActive === touchEditActiveRef.current) return;
    touchEditActiveRef.current = isActive;
  });

  // Poll notifications every 30 seconds
  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch {}
  }, []);
  useEffect(() => { fetchNotifications(); const interval = setInterval(fetchNotifications, 30000); return () => clearInterval(interval); }, [fetchNotifications]);
  // Initial seed of nowMinutes from any cached business timezone (before the first fetch resolves).
  useEffect(() => {
    if (businessTimezone) setNowMinutes(computeNowMinutesInTz(businessTimezone));
  }, [businessTimezone, computeNowMinutesInTz]);

  // Fetch business timezone eagerly on mount so the red "now" line corrects itself within ~150ms,
  // not whenever the full calendar fetch finishes.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/settings/business`, { withCredentials: true })
      .then(({ data }) => {
        if (cancelled) return;
        const tz = data?.timezone || '';
        if (tz && tz !== businessTimezone) {
          setBusinessTimezone(tz);
          try { localStorage.setItem('apptio_business_tz', tz); } catch {}
          setNowMinutes(computeNowMinutesInTz(tz));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const t = setInterval(() => {
      setNowMinutes(computeNowMinutesInTz(businessTimezone));
    }, 60000);
    return () => clearInterval(t);
  }, [businessTimezone, computeNowMinutesInTz]);

  // Auto-refresh appointments every 20s so new online bookings appear without a manual reload
  // Also refresh immediately when the window regains focus / tab becomes visible
  useEffect(() => {
    const shouldSkip = () => bookDialogOpen || apptPlacementMode || blockPlacementMode || saleSidebar;
    const interval = setInterval(() => {
      if (shouldSkip()) return;
      fetchAll();
    }, 20000);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !shouldSkip()) fetchAll();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [fetchAll, bookDialogOpen, apptPlacementMode, blockPlacementMode, saleSidebar]);

  const searchClients = (q) => {
    setClientSearch(q);
    setSelectedClient(null);
    setForm(p => ({ ...p, client_id: '' }));
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!q || q.length < 1) { setClientResults([]); setShowClientDropdown(false); return; }
    searchTimeout.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/clients?search=${encodeURIComponent(q)}&limit=15`, { withCredentials: true });
        setClientResults(data.clients || data);
        setShowClientDropdown(true);
      } catch { setClientResults([]); setShowClientDropdown(true); }
    }, 200);
  };

  const selectClient = (c) => {
    setSelectedClient(c);
    setClientSearch(c.name);
    setForm(p => ({ ...p, client_id: c.id }));
    setShowClientDropdown(false);
    setIsWalkIn(false);
    // Fetch fresh client doc to pick up latest no_show_count / is_blocked
    // (the search-list payload may be slightly stale right after a no-show was just recorded).
    if (c.id) {
      axios.get(`${API}/clients/${c.id}`, { withCredentials: true })
        .then(({ data }) => {
          if (data && data.id === c.id) {
            setSelectedClient(prev => prev && prev.id === data.id ? { ...prev, ...data } : prev);
          }
        })
        .catch(() => {});
    }
    // Fetch the client's last appointments to suggest re-booking
    setClientHistory([]);
    if (c.id) {
      axios.get(`${API}/clients/${c.id}/history`, { withCredentials: true })
        .then(({ data }) => setClientHistory((data || []).slice(0, 3)))
        .catch(() => {
          // Fallback: query appointments directly
          axios.get(`${API}/appointments?client_id=${c.id}&limit=20`, { withCredentials: true })
            .then(({ data }) => {
              const past = (data || [])
                .filter(a => ['completed', 'confirmed'].includes(a.status))
                .sort((x, y) => (y.date || '').localeCompare(x.date || '') || (y.start_time || '').localeCompare(x.start_time || ''))
                .slice(0, 3);
              setClientHistory(past);
            })
            .catch(() => setClientHistory([]));
        });
    }
  };

  const toggleWalkIn = () => {
    setIsWalkIn(!isWalkIn);
    setSelectedClient(null);
    setClientSearch('');
    setWalkInName('');
    setForm(p => ({ ...p, client_id: '' }));
    setShowClientDropdown(false);
  };

  const initialScrollDone = useRef(false);
  useEffect(() => {
    if (calRef.current && !initialScrollDone.current && staff.length > 0) {
      const offset = ((10 - START_HOUR) * 60 / SLOT_MINUTES) * SLOT_HEIGHT;
      calRef.current.scrollTop = offset;
      initialScrollDone.current = true;
    }
  }, [staff]);

  const openBookDialog = async (staffId, time, forDate) => {
    setForm({ client_id: '', service_id: '', staff_id: staffId || '', date: forDate || dateStr, start_time: time || '09:00', notes: '', recurrence: 'none', recurrence_count: 0, client_requested: false });
    setBookingServices([]);
    setClientSearch('');
    setSelectedClient(null);
    setClientHistory([]);
    setIsWalkIn(false);
    setWalkInName('');
    setClientResults([]);
    setShowClientDropdown(false);
    setShowNewClientForm(false);
    setShowClientActions(false);
    setShowEditClient(false);
    setEditClientData(null);
    setBookDialogOpen(true);
  };

  const [submitting, setSubmitting] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const svcsToBook = bookingServices.length > 0 ? bookingServices : (form.service_id ? [services.find(s => s.id === form.service_id)].filter(Boolean) : []);
      if (svcsToBook.length === 0) { toast.error(t('appointments.selectService')); setSubmitting(false); return; }
      // Generate a shared booking_group_id so all services belong to the same booking
      const sharedGroupId = svcsToBook.length > 1 ? safeUUID() : '';
      // Auto walk-in if no client selected
      const treatAsWalkIn = isWalkIn || !form.client_id;
      // Build all per-service payloads up-front so we have predictable start times BEFORE any network call.
      // Previously the loop advanced currentTime in-flight, but if a per-service start_time was set the advance was skipped,
      // which could leave a stale time if services were added in mixed ways. Pre-computing is more predictable.
      const payloads = [];
      let currentTime = form.start_time;
      for (const svc of svcsToBook) {
        const svcTime = svc.start_time || currentTime;
        const svcStaff = svc.staff_id || form.staff_id;
        if (!svcStaff) {
          toast.error('Please pick a staff member for every service');
          setSubmitting(false);
          return;
        }
        const payload = { ...form, service_id: svc.id, start_time: svcTime, staff_id: svcStaff, booking_group_id: sharedGroupId, client_requested: form.client_requested || false };
        if (svc.price_override) payload.price_override = Number(svc.price) || 0;
        if (treatAsWalkIn) { payload.client_id = '__walk_in__'; payload.walk_in_name = walkInName || 'Walk-in'; }
        payloads.push(payload);
        // Always advance currentTime by this service's duration so the NEXT service-with-no-start_time
        // gets the correct slot.
        const [h, m] = svcTime.split(':').map(Number);
        const nextMins = h * 60 + m + (svc.duration || 30);
        currentTime = `${String(Math.floor(nextMins / 60)).padStart(2, '0')}:${String(nextMins % 60).padStart(2, '0')}`;
      }
      let totalCreated = 0;
      const errors = [];
      for (let i = 0; i < payloads.length; i++) {
        try {
          const res = await axios.post(`${API}/appointments`, payloads[i], { withCredentials: true });
          totalCreated += (res.data.count || 1);
        } catch (err) {
          console.error(`Booking failed for service ${i + 1}/${payloads.length}:`, err, 'payload:', payloads[i]);
          errors.push(err.response?.data?.detail || err.message || 'Unknown error');
        }
      }
      if (errors.length > 0 && totalCreated === 0) {
        toast.error(`Could not book: ${errors[0]}`);
        return;
      }
      if (errors.length > 0) {
        toast.error(`Booked ${totalCreated}/${payloads.length} services. Issue: ${errors[0]}`);
      } else {
        toast.success(totalCreated > 1 ? `${totalCreated} services booked` : t('appointments.bookAppointment'));
      }
      setBookDialogOpen(false);
      setBookingServices([]);
      fetchAll();
    } catch (err) {
      console.error('Booking dialog failed:', err);
      toast.error(err.response?.data?.detail || err.message || 'Failed to book');
    } finally {
      setSubmitting(false);
    }
  };

  const [paymentMethods, setPaymentMethods] = useState(['cash', 'card']);
  const [checkoutPaymentMethod, setCheckoutPaymentMethod] = useState('cash');
  // PayLink (Apptio Stripe Checkout link sent to customer)
  const [payLinkPanel, setPayLinkPanel] = useState(false); // false | 'compose' | 'sent'
  const [payLinkChannels, setPayLinkChannels] = useState({ email: true, sms: false });
  const [payLinkEmail, setPayLinkEmail] = useState('');
  const [payLinkPhone, setPayLinkPhone] = useState('');
  const [payLinkResult, setPayLinkResult] = useState(null); // { session_id, url, status, ... }
  const [payLinkSending, setPayLinkSending] = useState(false);
  const [collectedPayments, setCollectedPayments] = useState([]);
  const [cashDialogOpen, setCashDialogOpen] = useState(false);
  const [cashInputValue, setCashInputValue] = useState('');
  const [checkoutTip, setCheckoutTip] = useState(0);
  const [checkoutDiscount, setCheckoutDiscount] = useState(0);
  const [checkoutVoucherCode, setCheckoutVoucherCode] = useState('');
  const [checkoutVoucherInfo, setCheckoutVoucherInfo] = useState(null);
  const [sidebarEditPrice, setSidebarEditPrice] = useState(false);
  const [sidebarPriceVal, setSidebarPriceVal] = useState('');
  const [sidebarEditService, setSidebarEditService] = useState(false);
  const [sidebarAddService, setSidebarAddService] = useState(false);
  const [sidebarEditingApptId, setSidebarEditingApptId] = useState(null);
  const [sidebarPdfUrl, setSidebarPdfUrl] = useState(null);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const [activityLog, setActivityLog] = useState(null); // null = hidden, [] = loading/empty, [...] = data
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [calPickerOpen, setCalPickerOpen] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(() => new Date());
  const [preCheckoutOpen, setPreCheckoutOpen] = useState(false);
  const [preCheckoutAppt, setPreCheckoutAppt] = useState(null);
  const [checkoutClientSearch, setCheckoutClientSearch] = useState('');
  const [checkoutClientResults, setCheckoutClientResults] = useState([]);
  const [showCheckoutClientSwap, setShowCheckoutClientSwap] = useState(false);
  const [partPaidConfirm, setPartPaidConfirm] = useState(false);

  // Fetch payment methods + licence status from settings
  const [licenceInfo, setLicenceInfo] = useState(null);
  useEffect(() => {
    axios.get(`${API}/licence/status`, { withCredentials: true })
      .then(res => {
        const methods = res.data.payment_methods || ['cash', 'card'];
        if (!methods.includes('voucher')) methods.push('voucher');
        setPaymentMethods(methods);
        setLicenceInfo(res.data);
      })
      .catch(() => {});
  }, []);

  const openCheckout = (appt) => {
    // Calculate group total if this is a multi-service booking.
    // Prefer sidebarGroup (always fresh from backend /group) over the calendar's appointments state
    // which can be stale right after add-service / reschedule operations.
    // IMPORTANT: only include services on the SAME DATE as the clicked appointment — a booking group
    // can span multiple days (e.g. mani today + brows next week), and checking out everything at once
    // would wrongly charge for services that haven't happened yet.
    const groupId = appt.booking_group_id;
    let groupAppts = [];
    if (groupId) {
      const fromSidebar = sidebarGroup.filter(g => g.booking_group_id === groupId
        && g.status !== 'cancelled'
        && g.date === appt.date
        && (g.client_id || '') === (appt.client_id || ''));
      if (fromSidebar.length > 1) {
        groupAppts = fromSidebar;
      } else {
        groupAppts = appointments.filter(a => a.booking_group_id === groupId
          && a.status !== 'cancelled'
          && a.date === appt.date
          && (a.client_id || '') === (appt.client_id || ''));
      }
    }
    const groupTotal = groupAppts.length > 1 ? groupAppts.reduce((sum, a) => sum + (a.price || 0), 0) : appt.price || 0;
    setPreCheckoutAppt({ ...appt, price: groupTotal, _groupAppts: groupAppts });
    setCheckoutPaymentMethod(paymentMethods[0] || 'cash');
    setCollectedPayments([]);
    setCashDialogOpen(false);
    setCashInputValue('');
    setCheckoutTip(0);
    setCheckoutDiscount(0);
    setCheckoutClientSearch('');
    setCheckoutClientResults([]);
    setShowCheckoutClientSwap(appt.client_id === '__walk_in__');
    setPreCheckoutOpen(true);
  };

  const swapWalkInClient = async (client) => {
    if (!preCheckoutAppt) return;
    try {
      // For walk-in multi-service bookings, apply the client change to the WHOLE group
      const isGroup = preCheckoutAppt.booking_group_id && (preCheckoutAppt._groupAppts || []).length > 1;
      await axios.put(`${API}/appointments/${preCheckoutAppt.id}/update-client`, {
        client_id: client.id,
        apply_to_group: isGroup,
        preserve_group: isGroup,
      }, { withCredentials: true });
      setPreCheckoutAppt(prev => prev ? { ...prev, client_name: client.name, client_id: client.id } : null);
      setShowCheckoutClientSwap(false);
      setCheckoutClientSearch('');
      setCheckoutClientResults([]);
      toast.success(`Client changed to ${client.name}`);
    } catch { toast.error('Failed to update client'); }
  };

  // ─── PayLink ────────────────────────────────────────────────────────────
  const sendPayLink = async (channels) => {
    if (!preCheckoutAppt) return;
    if (channels.includes('email') && !payLinkEmail.trim()) { toast.error('Email required'); return; }
    if (channels.includes('sms') && !payLinkPhone.trim()) { toast.error('Phone required'); return; }
    setPayLinkSending(true);
    try {
      const { data } = await axios.post(`${API}/paylink/create`, {
        appointment_id: preCheckoutAppt.id,
        channels,
        email: payLinkEmail.trim(),
        phone: payLinkPhone.trim(),
        origin_url: window.location.origin,
      }, { withCredentials: true });
      setPayLinkResult(data);
      setPayLinkPanel('sent');
      // Show toast based on what actually sent
      if (data.send_results?.email && !data.send_results.email.ok) toast.error(`Email failed: ${data.send_results.email.msg}`);
      if (data.send_results?.sms && !data.send_results.sms.ok) toast.error(`SMS failed: ${data.send_results.sms.msg}`);
      const okMsgs = [];
      if (data.send_results?.email?.ok) okMsgs.push('email');
      if (data.send_results?.sms?.ok) okMsgs.push('SMS');
      if (okMsgs.length) toast.success(`PayLink sent via ${okMsgs.join(' and ')}`);
      else if (channels.length === 0) toast.success('PayLink ready — share the QR or copy the link');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create PayLink');
    } finally {
      setPayLinkSending(false);
    }
  };

  const cancelPayLink = async () => {
    if (!payLinkResult?.session_id) return;
    if (!window.confirm('Cancel this PayLink so the customer can no longer pay?')) return;
    try {
      await axios.post(`${API}/paylink/${payLinkResult.session_id}/cancel`, {}, { withCredentials: true });
      toast.success('PayLink cancelled');
      setPayLinkPanel(false);
      setPayLinkResult(null);
      fetchAll();
    } catch (err) { toast.error('Failed to cancel'); }
  };

  // Polling: when a PayLink is awaiting payment, poll status every 4s
  React.useEffect(() => {
    if (payLinkPanel !== 'sent' || !payLinkResult?.session_id || payLinkResult.status !== 'pending') return;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/paylink/${payLinkResult.session_id}/status`, { withCredentials: true });
        setPayLinkResult(prev => ({ ...(prev || {}), ...data }));
        if (data.status === 'paid') {
          toast.success(`PayLink paid — ${preCheckoutAppt?.client_name || 'Customer'} £${(data.amount || 0).toFixed(2)}`);
          setPayLinkPanel(false);
          setPreCheckoutOpen(false);
          setPayLinkResult(null);
          fetchAll();
          clearInterval(t);
        } else if (data.status === 'expired' || data.status === 'cancelled') {
          toast.error(`PayLink ${data.status}`);
          fetchAll();
          clearInterval(t);
        }
      } catch {}
    }, 4000);
    return () => clearInterval(t);
  }, [payLinkPanel, payLinkResult?.session_id, payLinkResult?.status]);
  // ────────────────────────────────────────────────────────────────────────

  const searchCheckoutClients = (q) => {
    setCheckoutClientSearch(q);
    if (!q || q.length < 2) { setCheckoutClientResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/clients?search=${encodeURIComponent(q)}&limit=8`, { withCredentials: true });
        setCheckoutClientResults(data.clients || data);
      } catch { setCheckoutClientResults([]); }
    }, 250);
    return () => clearTimeout(t);
  };

  const handleCheckout = async () => {
    if (!preCheckoutAppt) return;
    try {
      const finalTotal = Math.max((preCheckoutAppt.price || 0) - checkoutDiscount + checkoutTip, 0);
      const totalCollected = collectedPayments.reduce((s, p) => s + p.amount, 0);
      const isPartial = collectedPayments.length > 0 && (finalTotal - totalCollected) > 0.01;
      // If partial payment and not yet confirmed, show confirmation dialog
      if (isPartial && !partPaidConfirm) {
        setPartPaidConfirm(true);
        return;
      }
      // If no payments added yet, use selected method for full amount
      let paymentMethod = checkoutPaymentMethod;
      let splitPaymentsPayload = null;
      if (collectedPayments.length > 0) {
        if (collectedPayments.length === 1) {
          paymentMethod = collectedPayments[0].method;
        } else {
          paymentMethod = 'split';
          splitPaymentsPayload = collectedPayments;
        }
      }
      if (paymentMethod === 'voucher' && checkoutVoucherCode) {
        await axios.post(`${API}/vouchers/redeem`, { code: checkoutVoucherCode, amount: finalTotal, appointment_id: preCheckoutAppt.id }, { withCredentials: true });
      }
      const payload = { payment_method: paymentMethod, tip: checkoutTip, discount: checkoutDiscount, partial: isPartial };
      if (splitPaymentsPayload) {
        payload.split_payments = splitPaymentsPayload.filter(sp => sp.amount > 0);
      } else if (isPartial) {
        payload.split_payments = collectedPayments.filter(sp => sp.amount > 0);
      }
      const { data } = await axios.post(`${API}/appointments/${preCheckoutAppt.id}/checkout`, payload, { withCredentials: true });
      setPreCheckoutOpen(false);
      setCollectedPayments([]);
      setPartPaidConfirm(false);
      setCheckoutAppt(preCheckoutAppt);
      setCheckoutReceipt(data);
      setReceiptSent(false);
      setReceiptEmail('');
      setRebookDate('');
      setRebookTime('');
      setCheckoutDialog(true);
      setCheckoutVoucherCode('');
      setCheckoutVoucherInfo(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
    }
  };

  const handleSendReceipt = async (emailOverride) => {
    if (!checkoutAppt) return;
    try {
      const payload = emailOverride ? { email: emailOverride } : {};
      const { data } = await axios.post(`${API}/appointments/${checkoutAppt.id}/send-receipt`, payload, { withCredentials: true });
      setReceiptSent(true);
      if (data.status === 'sent') {
        toast.success(`Receipt sent to ${data.sent_to}`);
      } else {
        toast.error('No email on file');
      }
    } catch {
      toast.error('Failed to send receipt');
    }
  };

  const handleRebook = async () => {
    if (!checkoutAppt || !rebookDate || !rebookTime) {
      toast.error('Please select date and time for next visit');
      return;
    }
    try {
      await axios.post(`${API}/appointments/${checkoutAppt.id}/rebook`, { date: rebookDate, start_time: rebookTime }, { withCredentials: true });
      toast.success('Next visit booked!');
      setCheckoutDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rebook');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/appointments/${id}/status`, { status }, { withCredentials: true });
      toast.success(`Marked as ${status}`);
      fetchAll();
    } catch {
      toast.error('Failed to update');
    }
  };

  const deleteAppt = async (id) => {
    try {
      await axios.delete(`${API}/appointments/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const sendReminder = async (id, type) => {
    try {
      await axios.post(`${API}/reminders/send`, { appointment_id: id, type }, { withCredentials: true });
      toast.success(`${type} reminder sent (mocked)`);
    } catch {
      toast.error('Failed');
    }
  };

  const openSlotPopup = (e, staffId, time, forDate) => {
    if (Date.now() - lastDropRef.current < 1000) return;
    e.stopPropagation();
    setCalPickerOpen(false);
    // Rebook mode: clicking a slot creates the rebook immediately
    if (rebookMode) {
      handleRebookSlotClick(staffId, time);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setSlotPopup({ staffId, time, x: rect.left, y: rect.top + rect.height, date: forDate || dateStr });
  };

  const handleRebookSlotClick = async (staffId, time) => {
    const rb = rebookMode;
    if (!rb) return;
    // Reschedule existing appointment(s) instead of creating new ones
    if (rb._isReschedule && rb._appointmentId) {
      const timeChanged = (rb.start_time !== time) || (rb.date !== dateStr);
      const staffChanged = rb.staff_id !== staffId;
      const doReschedule = async (notifyClient) => {
        try {
          const { data } = await axios.put(`${API}/appointments/${rb._appointmentId}/reschedule`, { date: dateStr, start_time: time, staff_id: staffId, cascade_group: true, notify_client: !!notifyClient }, { withCredentials: true });
          toast.success(`Rescheduled to ${dateStr} at ${time}`);
          if (data?.cascade_warnings?.length) showCascadeWarnings(data.cascade_warnings);
          setRebookMode(null);
          fetchAll();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to reschedule');
        }
      };
      if (timeChanged && rb.client_id && rb.client_id !== '__walk_in__') {
        setNotifyDialog({
          apptId: rb._appointmentId,
          clientName: rb.client_name || 'client',
          newTime: time,
          newDate: dateStr,
          newStaffName: staffChanged ? (staff.find(s => s.id === staffId)?.name || '') : '',
          staffChanged,
          onConfirm: doReschedule,
          onCancel: () => setRebookMode(null),
        });
      } else {
        await doReschedule(false);
      }
      return;
    }
    try {
      const svcs = rb.services || [rb.appt || rb];
      const sharedGroupId = svcs.length > 1 ? safeUUID() : '';
      let currentTime = time;
      for (const svc of svcs) {
        const payload = {
          client_id: rb.appt?.client_id || rb.client_id || '__walk_in__',
          service_id: svc.service_id || svc.id,
          staff_id: staffId,
          date: dateStr,
          start_time: currentTime,
          booking_group_id: sharedGroupId,
          notes: '',
          location_id: rb.appt?.location_id || rb.location_id || '',
          walk_in_name: rb.appt?.client_name || rb.client_name || 'Walk-in',
        };
        if (payload.client_id === '__walk_in__') payload.walk_in_name = rb.appt?.client_name || 'Walk-in';
        await axios.post(`${API}/appointments`, payload, { withCredentials: true });
        const dur = svc.duration || 30;
        const [h, m] = currentTime.split(':').map(Number);
        const nextM = h * 60 + m + dur;
        currentTime = `${String(Math.floor(nextM / 60)).padStart(2, '0')}:${String(nextM % 60).padStart(2, '0')}`;
      }
      toast.success('Appointment rebooked!');
      setRebookMode(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rebook');
    }
  };

  const closeSlotPopup = () => setSlotPopup(null);

  const openBlockFromSlot = () => {
    if (!slotPopup) return;
    const targetDate = slotPopup.date || dateStr;
    // If no specific time (week-view cell click), default to 09:00 for a 30-min block
    const startTime = slotPopup.time || '09:00';
    const [h, m] = startTime.split(':').map(Number);
    const endM = h * 60 + m + 30;
    setBlockForm({ staff_id: slotPopup.staffId, date: targetDate, start_time: startTime, end_time: `${Math.floor(endM / 60).toString().padStart(2, '0')}:${(endM % 60).toString().padStart(2, '0')}`, reason: '' });
    setBlockDialog(true);
    closeSlotPopup();
  };

  const saveBlock = async () => {
    if (!blockForm.staff_id) { toast.error('Please select a team member'); return; }
    try {
      await axios.post(`${API}/blocked-times`, blockForm, { withCredentials: true });
      toast.success('Time blocked');
      setBlockDialog(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteBlock = async (blockId) => {
    if (!blockId) { toast.error('Block ID missing'); return; }
    if (!window.confirm('Delete this blocked time?')) return;
    try {
      await axios.delete(`${API}/blocked-times/${blockId}`, { withCredentials: true });
      toast.success('Block removed');
      setEditingBlock(null);
      setActiveBlock(null);
      // Optimistic update so the block disappears immediately
      setBlockedTimes(prev => prev.filter(b => b.id !== blockId));
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to delete (${err.response?.status || 'network'})`);
    }
  };

  const saveEditBlock = async () => {
    if (!editingBlock) return;
    try {
      const noteVal = (editingBlock.notes ?? editingBlock.description ?? '');
      await axios.put(`${API}/blocked-times/${editingBlock.id}`, {
        reason: editingBlock.reason || '',
        start_time: editingBlock.start_time,
        end_time: editingBlock.end_time,
        staff_id: editingBlock.staff_id || '',
        date: editingBlock.date,
        // Persist note under both keys so legacy reads / new reads both pick it up.
        description: noteVal,
        notes: noteVal
      }, { withCredentials: true });
      toast.success('Block updated');
      setEditingBlock(null);
      fetchAll();
    } catch (err) {
      console.error('saveEditBlock failed', err);
      toast.error(err.response?.data?.detail || 'Failed to update block');
    }
  };

  // ─── Fresha-style block adjustment: hold 800ms → activate → drag to resize/move → Cancel/Save ───
  const activateBlock = (block) => {
    setActiveBlock({ id: block.id, original: { ...block }, current: { ...block } });
    if (navigator.vibrate) navigator.vibrate(30);
  };

  const saveActiveBlock = async () => {
    if (!activeBlock) return;
    const c = activeBlock.current;
    try {
      await axios.put(`${API}/blocked-times/${activeBlock.id}`, {
        staff_id: c.staff_id, start_time: c.start_time, end_time: c.end_time, date: c.date
      }, { withCredentials: true });
      toast.success('Block updated');
      setActiveBlock(null);
      fetchAll();
    } catch { toast.error('Failed to save'); }
  };

  const cancelActiveBlock = () => {
    // Revert to original
    if (activeBlock) {
      setBlockedTimes(prev => prev.map(b => b.id === activeBlock.id ? activeBlock.original : b));
    }
    setActiveBlock(null);
  };

  // Mouse: 800ms hold → activate block
  const handleBlockMouseDown = (e, block) => {
    if (e.button !== 0 || activeBlock) return;
    const startX = e.clientX;
    const startY = e.clientY;
    let moved = false;
    const onMove = (me) => { if (Math.abs(me.clientX - startX) > 5 || Math.abs(me.clientY - startY) > 5) { moved = true; clearTimeout(blockLongPressRef.current); blockLongPressRef.current = null; } };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); if (blockLongPressRef.current) { clearTimeout(blockLongPressRef.current); blockLongPressRef.current = null; } };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    blockLongPressRef.current = setTimeout(() => {
      blockLongPressRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (!moved) activateBlock(block);
    }, 800);
  };

  // Touch: 800ms hold → activate block
  const handleBlockTouchStart = (e, block) => {
    if (activeBlock) return;
    touchActiveRef.current = true;
    const touch = e.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;
    blockLongPressRef.current = setTimeout(() => {
      blockLongPressRef.current = null;
      activateBlock(block);
    }, 800);
    const onMove = (me) => {
      me.preventDefault();
      const t = me.touches[0];
      if (Math.abs(t.clientX - startX) > 10 || Math.abs(t.clientY - startY) > 10) {
        if (blockLongPressRef.current) { clearTimeout(blockLongPressRef.current); blockLongPressRef.current = null; }
      }
    };
    const onEnd = () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      if (blockLongPressRef.current) { clearTimeout(blockLongPressRef.current); blockLongPressRef.current = null; }
      setTimeout(() => { touchActiveRef.current = false; }, 300);
    };
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  };

  // Active block: drag to move (mouse)
  const handleActiveBlockDragStart = (e, blockId) => {
    if (!activeBlock || activeBlock.id !== blockId) return;
    e.preventDefault();
    blockDragRef.current = 'move';
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
    const onMove = (me) => {
      const el = document.elementFromPoint(me.clientX, me.clientY);
      const column = el?.closest?.('[data-staff-column]');
      if (!column) return;
      const staffId = column.getAttribute('data-staff-column');
      const rect = column.getBoundingClientRect();
      const y = me.clientY - rect.top;
      const totalMin = (y / SLOT_HEIGHT) * SLOT_MINUTES + START_HOUR * 60;
      const snapped = Math.round(totalMin / SNAP_MINUTES) * SNAP_MINUTES;
      const clamped = Math.max(START_HOUR * 60, Math.min(END_HOUR * 60 - SNAP_MINUTES, snapped));
      const newStart = minutesToTime(clamped);
      const origDur = timeToMinutes(activeBlock.original.end_time) - timeToMinutes(activeBlock.original.start_time);
      const newEnd = minutesToTime(clamped + origDur);
      setActiveBlock(prev => prev ? { ...prev, current: { ...prev.current, staff_id: staffId, start_time: newStart, end_time: newEnd } } : null);
      setBlockedTimes(prev => prev.map(b => b.id === activeBlock.id ? { ...b, staff_id: staffId, start_time: newStart, end_time: newEnd } : b));
    };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); document.body.style.userSelect = ''; document.body.style.cursor = ''; blockDragRef.current = null; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Active block: drag to move (touch)
  const handleActiveBlockTouchMove = (e) => {
    if (!activeBlock) return;
    e.preventDefault();
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const column = el?.closest?.('[data-staff-column]');
    if (!column) return;
    const staffId = column.getAttribute('data-staff-column');
    const rect = column.getBoundingClientRect();
    const y = touch.clientY - rect.top;
    const totalMin = (y / SLOT_HEIGHT) * SLOT_MINUTES + START_HOUR * 60;
    const snapped = Math.round(totalMin / SNAP_MINUTES) * SNAP_MINUTES;
    const clamped = Math.max(START_HOUR * 60, Math.min(END_HOUR * 60 - SNAP_MINUTES, snapped));
    const isDragFromBottom = blockDragRef.current === 'resize';
    if (isDragFromBottom) {
      const startMins = timeToMinutes(activeBlock.current.start_time);
      if (clamped > startMins + 15) {
        const newEnd = minutesToTime(clamped);
        setActiveBlock(prev => prev ? { ...prev, current: { ...prev.current, end_time: newEnd } } : null);
        setBlockedTimes(prev => prev.map(b => b.id === activeBlock.id ? { ...b, end_time: newEnd } : b));
      }
    } else {
      blockDragRef.current = 'move';
      const newStart = minutesToTime(clamped);
      const origDur = timeToMinutes(activeBlock.current.end_time) - timeToMinutes(activeBlock.current.start_time);
      const newEnd = minutesToTime(clamped + origDur);
      setActiveBlock(prev => prev ? { ...prev, current: { ...prev.current, staff_id: staffId, start_time: newStart, end_time: newEnd } } : null);
      setBlockedTimes(prev => prev.map(b => b.id === activeBlock.id ? { ...b, staff_id: staffId, start_time: newStart, end_time: newEnd } : b));
    }
  };

  // Active block: resize from bottom edge (mouse) - uses ref for immediate access
  const activeBlockRef = useRef(null);
  useEffect(() => { activeBlockRef.current = activeBlock; }, [activeBlock]);

  const handleActiveBlockResizeStart = (e, blockOverride) => {
    const ab = blockOverride || activeBlockRef.current;
    if (!ab) return;
    e.stopPropagation();
    e.preventDefault();
    blockDragRef.current = 'resize';
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';
    const onMove = (me) => {
      const cur = activeBlockRef.current;
      if (!cur) return;
      const column = document.querySelector(`[data-staff-column="${cur.current.staff_id}"]`);
      if (!column) return;
      const rect = column.getBoundingClientRect();
      const y = me.clientY - rect.top;
      const totalMin = (y / SLOT_HEIGHT) * SLOT_MINUTES + START_HOUR * 60;
      const snapped = Math.round(totalMin / SNAP_MINUTES) * SNAP_MINUTES;
      const startMins = timeToMinutes(cur.current.start_time);
      if (snapped > startMins + 15) {
        const newEnd = minutesToTime(snapped);
        setActiveBlock(prev => prev ? { ...prev, current: { ...prev.current, end_time: newEnd } } : null);
        setBlockedTimes(prev => prev.map(b => b.id === cur.id ? { ...b, end_time: newEnd } : b));
      }
    };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); document.body.style.userSelect = ''; document.body.style.cursor = ''; blockDragRef.current = null; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Block time resize drag
  const [resizingBlock, setResizingBlock] = useState(null);
  const resizingBlockRef = useRef(null);
  useEffect(() => {
    if (!resizingBlock) return;
    resizingBlockRef.current = resizingBlock;
    const updateBlockEnd = (clientY) => {
      const rb = resizingBlockRef.current;
      if (!rb) return;
      const colEl = document.querySelector(`[data-staff-column="${rb.staff_id}"]`);
      if (!colEl) return;
      const rect = colEl.getBoundingClientRect();
      const relY = clientY - rect.top + colEl.scrollTop;
      const totalMin = (relY / SLOT_HEIGHT) * 15 + START_HOUR * 60;
      const snapped = Math.round(totalMin / 15) * 15;
      const newEnd = `${String(Math.floor(snapped / 60)).padStart(2, '0')}:${String(snapped % 60).padStart(2, '0')}`;
      const [sh, sm] = rb.start_time.split(':').map(Number);
      if (snapped > sh * 60 + sm + 15) {
        resizingBlockRef.current = { ...rb, end_time: newEnd };
        setResizingBlock(prev => prev ? { ...prev, end_time: newEnd } : null);
      }
    };
    const handleMouseMove = (e) => updateBlockEnd(e.clientY);
    const handleTouchMove = (e) => { e.preventDefault(); if (e.touches[0]) updateBlockEnd(e.touches[0].clientY); };
    const finishBlockResize = async () => {
      const rb = resizingBlockRef.current;
      if (rb) {
        try {
          await axios.put(`${API}/blocked-times/${rb.id}`, { end_time: rb.end_time }, { withCredentials: true });
          fetchAll();
        } catch { toast.error('Failed to resize'); }
      }
      setResizingBlock(null);
      resizingBlockRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.overflow = '';
    };
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.overflow = 'hidden';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', finishBlockResize);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', finishBlockResize);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', finishBlockResize); window.removeEventListener('touchmove', handleTouchMove); window.removeEventListener('touchend', finishBlockResize); };
  }, [resizingBlock?.id]);

  const openSidebar = async (appt) => {
    setSidebarAppt(appt);
    setSidebarGroup([appt]);
    setStatusDropdownOpen(false);
    setSidebarClientEdit(false);
    setSidebarShowReceipt(false);
    setSidebarInvoice(null);
    setSidebarReceiptEmail('');
    // Fetch booking group (multi-service booking + any guests). For a guest-inclusive group,
    // client_ids differ per person, so we keep the ENTIRE group and rely on the shared
    // booking_group_id — the UI marks each row with the client name so staff can tell them apart.
    try {
      const { data } = await axios.get(`${API}/appointments/${appt.id}/group`, { withCredentials: true });
      if (Array.isArray(data) && data.length > 0) {
        setSidebarGroup(data);
      }
    } catch {}
    if (appt.client_id && appt.client_id !== '__walk_in__') {
      try {
        // Fetch by ID directly (more reliable than search-by-name which can miss accented/spaced names)
        const { data } = await axios.get(`${API}/clients/${appt.client_id}`, { withCredentials: true });
        setClientDetail(data || null);
        if (data && data.email) setSidebarReceiptEmail(data.email);
      } catch {
        // Fallback: search by name
        try {
          const r = await axios.get(`${API}/clients?search=${encodeURIComponent(appt.client_name || '')}&limit=1`, { withCredentials: true });
          const clients = r.data.clients || r.data;
          setClientDetail(clients.length > 0 ? clients[0] : null);
          if (clients.length > 0 && clients[0].email) setSidebarReceiptEmail(clients[0].email);
        } catch { setClientDetail(null); }
      }
    } else {
      setClientDetail(null);
    }
    if (appt.status === 'completed') {
      try {
        const { data } = await axios.get(`${API}/invoices/by-appointment/${appt.id}`, { withCredentials: true });
        if (data.found) setSidebarInvoice(data.invoice);
      } catch {}
    }
  };

  const closeSidebar = () => { setSidebarAppt(null); setSidebarGroup([]); setClientDetail(null); setStatusDropdownOpen(false); setSidebarClientEdit(false); setSidebarClientSwap(false); setSidebarClientEditForm(null); setSidebarClientProfile(null); setSidebarClientSearch(''); setSidebarClientResults([]); setSidebarShowReceipt(false); setSidebarInvoice(null); setSidebarEditPrice(false); setSidebarEditService(false); setSidebarAddService(false); setSidebarEditingApptId(null); setQuickActionsOpen(false); setRescheduleOpen(false); setActivityLog(null); };

  // Surface a warning when a cascaded reschedule lands sibling appointments on dates
  // where their assigned staff isn't scheduled to work. Owner can then manually re-assign
  // those services (drag to a different staff column) — the reschedule itself still succeeds.
  const showCascadeWarnings = (warnings) => {
    if (!Array.isArray(warnings) || warnings.length === 0) return;
    const lines = warnings.map(w => `• ${w.service_name || 'Service'} with ${w.staff_name || 'staff'} — ${w.date} ${w.start_time}`).join('\n');
    toast.warning(
      `${warnings.length} service${warnings.length > 1 ? 's' : ''} landed on a day their staff isn't working`,
      { description: lines + '\n\nYou can drag them to a working staff member.', duration: 12000 }
    );
  };

  // Add a service to the current booking group (Fresha-style multi-service)
  const [addingServiceId, setAddingServiceId] = useState(null);
  const addServiceToBooking = async (serviceId) => {
    if (!sidebarAppt) return;
    if (addingServiceId) return; // guard against double-click / rapid taps
    setAddingServiceId(serviceId);
    setSidebarAddService(false); // close panel immediately for visual feedback
    try {
      // Get or create booking_group_id
      let groupId = sidebarAppt.booking_group_id;
      if (!groupId) {
        groupId = safeUUID();
        // Update the original appointment with the new group ID
        await axios.put(`${API}/appointments/${sidebarAppt.id}/reschedule`, { booking_group_id: groupId }, { withCredentials: true });
        setSidebarAppt(prev => prev ? { ...prev, booking_group_id: groupId } : null);
      }
      const lastInGroup = sidebarGroup.length > 0 ? sidebarGroup[sidebarGroup.length - 1] : sidebarAppt;
      await axios.post(`${API}/appointments`, {
        client_id: sidebarAppt.client_id, service_id: serviceId, staff_id: sidebarAppt.staff_id,
        date: sidebarAppt.date, start_time: lastInGroup.end_time,
        location_id: sidebarAppt.location_id || '', notes: '',
        booking_group_id: groupId
      }, { withCredentials: true });
      const svc = services.find(s => s.id === serviceId);
      toast.success(`${svc?.name || 'Service'} added to booking`);
      fetchAll();
      // Re-fetch group (defensively filter by client_id)
      const { data } = await axios.get(`${API}/appointments/${sidebarAppt.id}/group`, { withCredentials: true });
      if (Array.isArray(data)) {
        const filtered = data.filter(g => (g.client_id || '') === (sidebarAppt.client_id || ''));
        setSidebarGroup(filtered);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add service');
      setSidebarAddService(true); // reopen if failed
    } finally {
      setAddingServiceId(null);
    }
  };

  const searchSidebarClient = (q) => {
    setSidebarClientSearch(q);
    if (!q || q.length < 2) { setSidebarClientResults([]); return; }
    setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/clients?search=${encodeURIComponent(q)}&limit=8`, { withCredentials: true });
        setSidebarClientResults(data.clients || data);
      } catch { setSidebarClientResults([]); }
    }, 250);
  };

  const assignSidebarClient = async (client) => {
    if (!sidebarAppt) return;
    try {
      // For multi-service bookings, reassign EVERY service in the booking group to the new client,
      // and KEEP them in the same group_id (preserve_group=true). This avoids the bug where
      // changing the client on one service splits the group into separate single-service bookings.
      const isGroup = sidebarAppt.booking_group_id && sidebarGroup.length > 1;
      await axios.put(`${API}/appointments/${sidebarAppt.id}/update-client`, {
        client_id: client.id,
        apply_to_group: isGroup,
        preserve_group: isGroup,
      }, { withCredentials: true });
      setSidebarAppt(prev => prev ? { ...prev, client_name: client.name, client_id: client.id } : null);
      if (isGroup) {
        setSidebarGroup(prev => prev.map(g => ({ ...g, client_id: client.id, client_name: client.name })));
      }
      setClientDetail(client);
      setSidebarClientEdit(false);
      setSidebarClientSearch('');
      setSidebarClientResults([]);
      toast.success(`Client set to ${client.name}`);
      fetchAll();
    } catch { toast.error('Failed to update client'); }
  };

  const changeSidebarStatus = async (newStatus) => {
    if (!sidebarAppt) return;
    setStatusDropdownOpen(false);
    if (newStatus === sidebarAppt.status) return;
    if (newStatus === 'cancelled') {
      setCancelDialog(sidebarAppt);
      setCancelReason('No reason provided');
      setCancelNotify(sidebarAppt.source === 'online');
      return;
    }
    if (newStatus === 'no-show') {
      setNoShowDialog(sidebarAppt);
      setNoShowNotify(sidebarAppt.source === 'online');
      return;
    }
    try {
      await axios.put(`${API}/appointments/${sidebarAppt.id}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`Status: ${newStatus}`);
      setSidebarAppt(prev => prev ? { ...prev, status: newStatus } : null);
      fetchAll();
    } catch { toast.error('Failed to update status'); }
  };

  const confirmCancel = async () => {
    if (!cancelDialog) return;
    try {
      const res = await axios.put(`${API}/appointments/${cancelDialog.id}/status`, {
        status: 'cancelled', reason: cancelReason, send_notification: cancelNotify
      }, { withCredentials: true });
      const count = res.data?.cancelled_count || 1;
      toast.success(count > 1 ? `${count} appointments cancelled` : 'Appointment cancelled');
      setSidebarAppt(prev => prev ? { ...prev, status: 'cancelled' } : null);
      setSidebarGroup([]);
      setCancelDialog(null);
      fetchAll();
    } catch { toast.error('Failed to cancel'); }
  };

  const confirmNoShow = async () => {
    if (!noShowDialog) return;
    try {
      await axios.put(`${API}/appointments/${noShowDialog.id}/status`, {
        status: 'no-show', send_notification: noShowNotify
      }, { withCredentials: true });
      toast.success('Marked as no-show');
      setSidebarAppt(prev => prev ? { ...prev, status: 'no-show' } : null);
      setNoShowDialog(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to mark no-show'); }
  };


  const handleDragStart = (e, appt) => {
    // Block HTML5 drag if touch was used recently (iPad fires both)
    if (touchActiveRef.current) { e.preventDefault(); return; }
    setDragAppt(appt);
    dragSourceRef.current = 'html5';
    // Record grab offset from block's top edge — used for ghost image offset so the ghost
    // visually follows the cursor at the same point the user clicked.
    const blockRect = e.currentTarget.getBoundingClientRect();
    dragGrabOffsetRef.current = Math.max(0, e.clientY - blockRect.top);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', appt.id);
    // Create ghost element
    const ghost = e.target.cloneNode(true);
    ghost.style.opacity = '0.8';
    ghost.style.transform = 'rotate(2deg) scale(1.02)';
    ghost.style.position = 'absolute';
    ghost.style.left = '-9999px';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 60, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };
  const handleDragEnd = () => { setDragAppt(null); setDragOverInfo(null); dragSourceRef.current = null; dragGrabOffsetRef.current = 0; };

  // Resize appointment duration by dragging bottom edge (MOUSE/DESKTOP only — touch is blocked)
  const handleResizeStart = (e, appt) => {
    // Block touch entirely — users must use the "Change duration" dialog on iPad/iPhone
    if (e.type === 'touchstart' || e.pointerType === 'touch') return;
    e.stopPropagation();
    e.preventDefault();
    const startY = e.clientY;
    setResizing({ apptId: appt.id, startY, origDuration: appt.duration || 30 });
    const onMove = (me) => {
      me.preventDefault();
      const currentY = me.type === 'touchmove' ? me.touches[0].clientY : me.clientY;
      const deltaY = currentY - startY;
      const pxPer5Min = SLOT_HEIGHT / (SLOT_MINUTES / 5);
      const deltaMins = Math.round(deltaY / pxPer5Min) * 5;
      const newDur = Math.max(5, (appt.duration || 30) + deltaMins);
      setResizing(prev => prev ? { ...prev, currentDuration: newDur } : null);
    };
    const onUp = async (ue) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
      const endY = ue.type === 'touchend' ? (ue.changedTouches?.[0]?.clientY || startY) : ue.clientY;
      const deltaY = endY - startY;
      const pxPer5Min = SLOT_HEIGHT / (SLOT_MINUTES / 5);
      const deltaMins = Math.round(deltaY / pxPer5Min) * 5;
      const newDur = Math.max(5, (appt.duration || 30) + deltaMins);
      setResizing(null);
      if (newDur !== appt.duration) {
        try {
          await axios.put(`${API}/appointments/${appt.id}/reschedule`, { duration: newDur }, { withCredentials: true });
          fetchAll();
        } catch { toast.error('Failed to resize'); }
      }
    };
    document.addEventListener('mousemove', onMove, { passive: false });
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onUp);
  };
  const [dragOverInfo, setDragOverInfo] = useState(null);
  const [touchDrag, setTouchDrag] = useState(null); // legacy ghost drag - keeping for desktop DnD compat
  const [touchEditMode, setTouchEditMode] = useState(null); // Fresha-style: { appt, origTime, origDuration, newTime, newDuration, staffId }
  const [notifyDialog, setNotifyDialog] = useState(null); // { apptId, clientName, staffName, time, date, changes: {...}, onConfirm }
  const touchTimerRef = useRef(null);
  const touchFeedbackRef = useRef(null);
  const touchEditRef = useRef(null); // mutable ref for touch move handlers

  // Fresha-style touch: long-press 830ms → floating overlay, direct DOM for 60fps
  const handleTouchDragStart = (e, appt) => {
    touchActiveRef.current = true;
    const touch = e.touches[0];
    const startY = touch.clientY;
    const startX = touch.clientX;
    const target = e.target.closest('[data-testid^="appt-block-"], [data-testid^="block-"]') || e.target;
    target.style.userSelect = 'none';
    target.style.webkitUserSelect = 'none';
    // Visual feedback at 400ms: ONLY box-shadow (no transform — transforms break position:fixed on iOS Safari)
    const feedbackTimer = setTimeout(() => {
      target.style.transition = 'box-shadow 0.2s';
      target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
    }, 400);
    touchFeedbackRef.current = feedbackTimer;
    const moveThreshold = { moved: false };
    const earlyMoveCheck = (me) => {
      if (Math.abs(me.touches[0].clientY - startY) > 10 || Math.abs(me.touches[0].clientX - startX) > 10) {
        moveThreshold.moved = true;
        clearTimeout(touchTimerRef.current);
        clearTimeout(touchFeedbackRef.current);
        target.style.transition = ''; target.style.boxShadow = '';
        document.removeEventListener('touchmove', earlyMoveCheck);
      }
    };
    document.addEventListener('touchmove', earlyMoveCheck, { passive: true });
    touchTimerRef.current = setTimeout(() => {
      document.removeEventListener('touchmove', earlyMoveCheck);
      clearTimeout(touchFeedbackRef.current);
      // Clear visual feedback instantly — no transition, no transform residue
      target.style.transition = 'none';
      target.style.boxShadow = '';
      void target.offsetHeight; // force reflow
      if (moveThreshold.moved) return;
      if (navigator.vibrate) navigator.vibrate(30);
      const dur = appt.duration || (appt.end_time ? (timeToMinutes(appt.end_time) - timeToMinutes(appt.start_time)) : 30);
      const computedEnd = minutesToTime(timeToMinutes(appt.start_time) + dur);
      // Use target's OFFSET position (relative to its offsetParent = staff column). This uses the exact
      // same coordinate system as the block itself, immune to viewport-vs-scroll and iOS Safari containing-block quirks.
      const cardOffsetTop = target.offsetTop;
      const cardOffsetHeight = target.offsetHeight;
      const cardOffsetLeft = target.offsetLeft;
      const cardOffsetWidth = target.offsetWidth;
      const offsetParent = target.offsetParent; // The staff column
      // Also capture viewport rect for drop-target detection via elementFromPoint
      const cardRect = target.getBoundingClientRect();
      // Build floating overlay by cloning the original block's inner HTML — preserves all styling and details
      const isBlock = !!appt._isBlock;
      const cs = window.getComputedStyle(target);
      const overlay = document.createElement('div');
      overlay.id = 'touch-edit-overlay';
      const bgColor = cs.backgroundColor || '#E9EDDF';
      const borderColor = cs.borderLeftColor || '#556B2F';
      // Clone the block's inner content exactly as rendered
      overlay.innerHTML = target.innerHTML;
      // Bottom resize grip — only available AFTER the 830ms long-press (since the overlay only exists post-hold)
      const resizeHandle = document.createElement('div');
      resizeHandle.id = 'touch-resize-handle';
      resizeHandle.style.cssText = 'position:absolute;bottom:0;left:0;right:0;height:20px;display:flex;align-items:center;justify-content:center;cursor:ns-resize;z-index:3;touch-action:none;';
      resizeHandle.innerHTML = '<div style="width:44px;height:5px;border-radius:4px;background:rgba(255,255,255,0.85);box-shadow:0 1px 2px rgba(0,0,0,0.3);pointer-events:none;"></div>';
      overlay.appendChild(resizeHandle);
      overlay.style.cssText = `position:absolute;left:${cardOffsetLeft}px;top:${cardOffsetTop}px;width:${cardOffsetWidth}px;height:${cardOffsetHeight}px;z-index:9998;pointer-events:auto;box-shadow:0 8px 32px rgba(0,0,0,0.3);border-radius:4px;background:${bgColor};border-left:3px solid ${borderColor};will-change:top,left,width,height;`;
      // Append into the SAME offsetParent as the block — same coordinate system, no viewport math
      (offsetParent || document.body).appendChild(overlay);
      target.style.opacity = '0.4';
      const pxPer5Min = SLOT_HEIGHT / (SLOT_MINUTES / 5);
      const origMins = timeToMinutes(appt.start_time);
      const st = {
        appt: { ...appt }, origTime: appt.start_time, origDuration: dur,
        newTime: appt.start_time, newDuration: dur, staffId: appt.staff_id,
        mode: 'move',
        // Drift-free tracking: totalDy = accumulated dy since drag start (move mode)
        //                     totalDurDy = accumulated dy since resize started
        totalDy: 0, totalDurDy: 0,
        lastY: startY, targetEl: target,
        origCardTop: cardOffsetTop, origCardHeight: cardOffsetHeight,
        overlayLeft: cardOffsetLeft, overlayWidth: cardOffsetWidth,
        // Track current offsetParent so we can re-parent when crossing staff columns
        offsetParent: offsetParent,
      };
      touchEditRef.current = st;
      setTouchEditMode({ newTime: appt.start_time, newDuration: dur, staffId: appt.staff_id, appt: { ...appt } });
      document.body.style.overflow = 'hidden';
      document.body.classList.add('touch-edit-active');
      // Resize grip: switches to resize mode when touched (only reachable AFTER 830ms hold since overlay only exists post-long-press)
      const rhEl = document.getElementById('touch-resize-handle');
      if (rhEl) {
        rhEl.addEventListener('touchstart', (re) => {
          re.stopPropagation();
          re.preventDefault();
          const rs = touchEditRef.current;
          if (rs) { rs.mode = 'resize'; rs.lastY = re.touches[0].clientY; rs.totalDurDy = 0; }
        }, { passive: false });
      }
      // Touch on overlay body: switch to move mode (skip when touch started on resize grip)
      overlay.addEventListener('touchstart', (oe) => {
        if (oe.target.id === 'touch-resize-handle' || oe.target.closest?.('#touch-resize-handle')) return;
        oe.preventDefault();
        const os = touchEditRef.current;
        if (os) { os.mode = 'move'; os.lastY = oe.touches[0].clientY; }
      }, { passive: false });
      // All movement via non-passive document listener — locks scroll + moves overlay
      const onMove = (me) => {
        me.preventDefault();
        const t = me.touches[0];
        const s = touchEditRef.current;
        if (!s) return;
        const ov = document.getElementById('touch-edit-overlay');
        if (!ov) return;
        const dy = t.clientY - s.lastY;
        s.lastY = t.clientY;
        if (s.mode === 'move') {
          s.totalDy += dy;
          // Deadzone: require at least 6px of intentional movement before any step registers
          const adjustedDy = Math.abs(s.totalDy) < 6 ? 0 : s.totalDy;
          // Snap to 5-minute grid based on total delta from drag start
          const steps = Math.round(adjustedDy / pxPer5Min);
          const origMinsClamp = Math.max(START_HOUR * 60, Math.min(END_HOUR * 60 - 5, origMins + steps * 5));
          const effectiveSteps = (origMinsClamp - origMins) / 5;
          const snappedPx = effectiveSteps * pxPer5Min;
          ov.style.top = `${s.origCardTop + snappedPx}px`;
          // Detect staff column
          const col = document.elementFromPoint(t.clientX, t.clientY)?.closest?.('[data-staff-column]');
          if (col) {
            const sid = col.getAttribute('data-staff-column');
            if (sid && sid !== s.staffId) {
              s.staffId = sid;
              // Re-parent overlay into the new staff column so position:absolute remains correct
              if (col !== s.offsetParent) {
                s.offsetParent = col;
                col.appendChild(ov);
                // After re-parenting, use the column's inner dimensions for left/width (full column minus margin)
                s.overlayLeft = 1;
                s.overlayWidth = col.clientWidth - 2;
                ov.style.left = `${s.overlayLeft}px`;
                ov.style.width = `${s.overlayWidth}px`;
              }
              if (navigator.vibrate) navigator.vibrate(15);
              setTouchEditMode(prev => prev ? { ...prev, staffId: sid } : null);
            }
          }
          // Update time state when the snapped time changes
          const newTime = minutesToTime(origMinsClamp);
          if (newTime !== s.newTime) {
            s.newTime = newTime;
            if (navigator.vibrate) navigator.vibrate(10);
            setTouchEditMode(prev => prev ? { ...prev, newTime } : null);
            const newEnd = minutesToTime(origMinsClamp + s.newDuration);
            ov.querySelectorAll('[data-block-time]').forEach(el => { el.textContent = `${newTime}-${newEnd}`; });
          }
        } else {
          // Resize: total dy since resize mode started
          s.totalDurDy += dy;
          const steps = Math.round(s.totalDurDy / pxPer5Min);
          const newDur = Math.max(5, dur + steps * 5);
          const snappedPx = (newDur - dur) * (pxPer5Min / 5);
          ov.style.height = `${Math.max(pxPer5Min, s.origCardHeight + snappedPx)}px`;
          if (newDur !== s.newDuration) {
            s.newDuration = newDur;
            if (navigator.vibrate) navigator.vibrate(10);
            setTouchEditMode(prev => prev ? { ...prev, newDuration: newDur } : null);
            const newEnd = minutesToTime(timeToMinutes(s.newTime) + newDur);
            ov.querySelectorAll('[data-block-time]').forEach(el => { el.textContent = `${s.newTime}-${newEnd}`; });
          }
        }
      };
      document.addEventListener('touchmove', onMove, { passive: false });
      st._moveHandler = onMove;
    }, 830);
    const earlyEnd = () => {
      clearTimeout(touchTimerRef.current); clearTimeout(touchFeedbackRef.current);
      target.style.transform = ''; target.style.transition = ''; target.style.boxShadow = '';
      document.removeEventListener('touchmove', earlyMoveCheck);
      document.removeEventListener('touchend', earlyEnd);
      // Quick tap — if the appt has an _onTap callback, call it
      if (!moveThreshold.moved && appt._onTap) {
        appt._onTap();
      }
    };
    document.addEventListener('touchend', earlyEnd, { once: true });
  };

  const handleTouchDragMove = () => {};
  const handleTouchDragEnd = () => { if (touchTimerRef.current) { clearTimeout(touchTimerRef.current); touchTimerRef.current = null; } };

  // Save edit mode changes — works for both appointments and blocked times
  const saveTouchEdit = async () => {
    const state = touchEditRef.current;
    if (!state) return;
    const { appt, origTime, origDuration, newTime, newDuration, staffId } = state;
    const isBlock = !!appt._isBlock;
    cancelTouchEdit();
    if (isBlock) {
      const changes = {};
      if (newTime !== origTime) { changes.start_time = newTime; const [h,m] = newTime.split(':').map(Number); const endM = h*60+m+newDuration; changes.end_time = `${String(Math.floor(endM/60)).padStart(2,'0')}:${String(endM%60).padStart(2,'0')}`; }
      if (newDuration !== origDuration && !changes.end_time) { const [h,m] = newTime.split(':').map(Number); const endM = h*60+m+newDuration; changes.end_time = `${String(Math.floor(endM/60)).padStart(2,'0')}:${String(endM%60).padStart(2,'0')}`; }
      if (staffId !== appt.staff_id) changes.staff_id = staffId;
      changes.date = dateStr;
      if (Object.keys(changes).length === 0) return;
      try { await axios.put(`${API}/blocked-times/${appt.id}`, changes, { withCredentials: true }); toast.success(`Block saved: ${newTime}`); fetchAll(); }
      catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); fetchAll(); }
    } else {
      const changes = {};
      if (newTime !== origTime) changes.start_time = newTime;
      if (newDuration !== origDuration) changes.duration = newDuration;
      if (staffId !== appt.staff_id) changes.staff_id = staffId;
      changes.date = dateStr;
      if (Object.keys(changes).length === 0) return;
      const timeChanged = changes.start_time != null || changes.date !== appt.date;
      const doSave = async (notifyClient) => {
        try {
          await axios.put(`${API}/appointments/${appt.id}/reschedule`, { ...changes, notify_client: !!notifyClient }, { withCredentials: true });
          toast.success(`Saved: ${newTime}, ${newDuration}min`);
          fetchAll();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); fetchAll(); }
      };
      if (timeChanged && appt.client_id && appt.client_id !== '__walk_in__') {
        setNotifyDialog({
          apptId: appt.id,
          clientName: appt.client_name || 'client',
          newTime,
          newDate: dateStr,
          newStaffName: staffId !== appt.staff_id ? (staff.find(s => s.id === staffId)?.name || '') : '',
          staffChanged: staffId !== appt.staff_id,
          onConfirm: doSave,
        });
      } else {
        await doSave(false);
      }
    }
  };

  const cancelTouchEdit = () => {
    const st = touchEditRef.current;
    if (st) {
      if (st._moveHandler) document.removeEventListener('touchmove', st._moveHandler);
      if (st.targetEl) st.targetEl.style.opacity = '';
    }
    const ov = document.getElementById('touch-edit-overlay');
    if (ov) ov.remove();
    touchEditRef.current = null;
    setTouchEditMode(null);
    document.body.style.overflow = '';
    document.body.classList.remove('touch-edit-active');
  };

  const startTouchResize = (e) => {
    e.stopPropagation();
    const state = touchEditRef.current;
    if (!state) return;
    state.mode = 'resize';
    state.lastY = e.touches[0].clientY;
    state.accumY = 0;
    setTouchEditMode(prev => prev ? { ...prev, mode: 'resize' } : null);
  };

  const getTimeFromDragEvent = (e) => {
    const column = e.currentTarget;
    const rect = column.getBoundingClientRect();
    // Drop position = cursor position (block's TOP snaps to where the cursor is)
    // This is simpler and matches Fresha/Square Appointments — user gets predictable behaviour.
    const y = e.clientY - rect.top;
    const pixelsPerMinute = SLOT_HEIGHT / SLOT_MINUTES;
    const minutesFromStart = y / pixelsPerMinute;
    const totalMinutes = START_HOUR * 60 + minutesFromStart;
    const snappedMinutes = Math.round(totalMinutes / SNAP_MINUTES) * SNAP_MINUTES;
    const clampedMinutes = Math.max(START_HOUR * 60, Math.min(END_HOUR * 60 - SNAP_MINUTES, snappedMinutes));
    return minutesToTime(clampedMinutes);
  };

  const handleDragOver = (e, staffId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const time = getTimeFromDragEvent(e);
    const topMinutes = timeToMinutes(time) - START_HOUR * 60;
    const top = (topMinutes / SLOT_MINUTES) * SLOT_HEIGHT;
    setDragOverInfo({ staffId, time, top });
  };
  const handleDragLeave = (e) => {
    const column = e.currentTarget;
    const related = e.relatedTarget;
    if (column && column.contains(related)) return;
    setDragOverInfo(null);
  };
  const handleDrop = async (e, staffId) => {
    e.preventDefault();
    setDragOverInfo(null);
    // Only process drops from HTML5 drag (not touch)
    if (dragSourceRef.current !== 'html5' || !dragAppt) { setDragAppt(null); return; }
    const time = getTimeFromDragEvent(e);
    const movedItem = dragAppt;
    setDragAppt(null);
    dragSourceRef.current = null;
    lastDropRef.current = Date.now();
    if (movedItem.staff_id === staffId && movedItem.start_time === time) return;
    // Handle blocked time move — no longer uses appointment drag system
    if (movedItem._isBlock) { return; }
    const timeChanged = movedItem.start_time !== time;
    const staffChanged = movedItem.staff_id !== staffId;
    const doReschedule = async (notifyClient) => {
      try {
        await axios.put(`${API}/appointments/${movedItem.id}/reschedule`, { staff_id: staffId, start_time: time, date: dateStr, notify_client: !!notifyClient }, { withCredentials: true });
        setAppointments(prev => prev.map(a => a.id === movedItem.id ? { ...a, staff_id: staffId, start_time: time, date: dateStr, staff_name: staff.find(s => s.id === staffId)?.name || a.staff_name, end_time: (() => { const [h,m] = time.split(':').map(Number); const endM = h*60+m+(a.duration||30); return `${String(Math.floor(endM/60)).padStart(2,'0')}:${String(endM%60).padStart(2,'0')}`; })() } : a));
        const staffName = staff.find(s => s.id === staffId)?.name || 'staff';
        toast.success(timeChanged ? `Moved to ${staffName} at ${time}` : `Moved to ${staffName}`);
        fetchAll();
      } catch { toast.error('Failed to reschedule'); }
    };
    if (timeChanged && movedItem.client_id && movedItem.client_id !== '__walk_in__') {
      // Prompt for notify like Fresha — staff-only moves skip the prompt
      setNotifyDialog({
        apptId: movedItem.id,
        clientName: movedItem.client_name || 'client',
        newTime: time,
        newDate: dateStr,
        newStaffName: staff.find(s => s.id === staffId)?.name || '',
        staffChanged,
        onConfirm: doReschedule,
      });
    } else {
      // Staff-only move, or walk-in (no client) → no prompt, no notification
      await doReschedule(false);
    }
  };

  const getAppointmentsForStaff = (staffId, forDate) => appointments.filter(a => a.staff_id === staffId && (!forDate || a.date === forDate) && a.status !== 'cancelled');

  // Compute overlapping positions for appointments
  const getOverlapLayout = (staffAppts) => {
    const sorted = [...staffAppts].sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));
    const layout = {};
    const columns = [];
    for (const appt of sorted) {
      const start = timeToMinutes(appt.start_time);
      const end = timeToMinutes(appt.end_time);
      let col = 0;
      while (col < columns.length && columns[col] > start) col++;
      if (col >= columns.length) columns.push(end);
      else columns[col] = end;
      layout[appt.id] = { col, totalCols: 0 };
    }
    // Assign total columns per group
    for (const appt of sorted) {
      const start = timeToMinutes(appt.start_time);
      const end = timeToMinutes(appt.end_time);
      let maxCol = layout[appt.id].col;
      for (const other of sorted) {
        if (other.id === appt.id) continue;
        const os = timeToMinutes(other.start_time);
        const oe = timeToMinutes(other.end_time);
        if (os < end && oe > start) {
          maxCol = Math.max(maxCol, layout[other.id].col);
        }
      }
      layout[appt.id].totalCols = maxCol + 1;
    }
    return layout;
  };

  const getApptPosition = (appt) => {
    const startMins = timeToMinutes(appt.start_time);
    const endMins = timeToMinutes(appt.end_time);
    const gridStart = START_HOUR * 60;
    const topSlots = (startMins - gridStart) / SLOT_MINUTES;
    const heightSlots = (endMins - startMins) / SLOT_MINUTES;
    const minPx = Math.max((SNAP_MINUTES / SLOT_MINUTES) * SLOT_HEIGHT - 2, 8);
    return { top: topSlots * SLOT_HEIGHT, height: Math.max(heightSlots * SLOT_HEIGHT - 2, minPx) };
  };

  // Get working hours for current day per staff
  const dayName = format(currentDate, 'EEEE').toLowerCase();

  // Check if staff is scheduled for a specific date (override > weekly pattern)
  const isStaffScheduledForDate = useCallback((staffId, date) => {
    const override = schedOverrides.find(o => o.staff_id === staffId && o.date === date);
    if (override) return override.enabled;
    const s = staff.find(st => st.id === staffId);
    if (!s) return false;
    const dn = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][new Date(date + 'T12:00:00').getDay()];
    return s.working_hours?.[dn]?.enabled === true;
  }, [staff, schedOverrides]);

  const getStaffWorkRangeForDate = useCallback((s, date) => {
    const override = schedOverrides.find(o => o.staff_id === s.id && o.date === date);
    if (override) {
      if (!override.enabled) return null;
      return { start: timeToMinutes(override.start), end: timeToMinutes(override.end) };
    }
    const dn = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][new Date(date + 'T12:00:00').getDay()];
    const wh = s.working_hours?.[dn];
    if (!wh?.enabled) return null;
    return { start: timeToMinutes(wh.start), end: timeToMinutes(wh.end) };
  }, [staff, schedOverrides]);

  const getStaffWorkRange = (s) => getStaffWorkRangeForDate(s, dateStr);

  // True only when in-state data (staff/schedOverrides/appointments) matches the currently visible date-range + location.
  // While false (initial mount or just after date navigation), we must not run the "scheduled" filter against stale overrides.
  const currentKey = `${viewDates.join(',')}|${selectedLocation || ''}`;
  const calendarDataReady = loadedForKey === currentKey;

  const activeStaff = staff.filter(s => {
    if (!s.is_active) return false;
    if (selectedLocation && s.location_ids && s.location_ids.length > 0 && !s.location_ids.includes(selectedLocation)) return false;
    if (staffFilterMode === 'scheduled') {
      // In week view, include staff scheduled on ANY day of the visible week
      const scheduledInRange = calendarView === 'week'
        ? viewDates.some(vd => isStaffScheduledForDate(s.id, vd))
        : isStaffScheduledForDate(s.id, dateStr);
      if (!scheduledInRange) {
        // Keep the column only if they have a NON-CANCELLED appointment on a visible date.
        // Cancelled / no-show appointments shouldn't keep a not-scheduled staff visible.
        const visibleDateSet = new Set(calendarView === 'week' ? viewDates.map(d => format(d, 'yyyy-MM-dd')) : [dateStr]);
        const hasApptsInRange = appointments.some(a => a.staff_id === s.id && visibleDateSet.has(a.date) && a.status !== 'cancelled' && a.status !== 'no-show');
        if (!hasApptsInRange) return false;
      }
    } else if (staffFilterMode === 'custom') {
      if (!selectedStaffIds.has(s.id)) return false;
    }
    return true;
  });

  // Check if any staff is working today
  const anyStaffWorking = activeStaff.some(s => getStaffWorkRange(s) !== null);

  // Check if a time slot is within working hours for a staff member
  const isWithinWorkHours = (staffId, slotTime) => {
    const s = activeStaff.find(st => st.id === staffId);
    if (!s) return false;
    const range = getStaffWorkRange(s);
    if (!range) return false;
    const mins = timeToMinutes(slotTime);
    return mins >= range.start && mins < range.end;
  };

  return (
    <div className="fade-in flex flex-col h-[calc(100vh-4rem)] lg:h-[calc(100vh-4rem)] pb-[72px] lg:pb-0" data-testid="appointments-page">
      {/* Touch Edit Mode Banner (Fresha-style) */}
      {touchEditMode && (
        <div className="bg-[#556B2F] text-white px-4 py-2.5 flex items-center justify-between flex-shrink-0 z-20" data-testid="touch-edit-banner">
          <div className="flex-1 text-center">
            <p className="text-sm font-semibold">{touchEditMode.appt?._isBlock ? 'Move blocked time' : 'Reschedule'} to {fmt(currentDate, 'EEE d MMM, yyyy')}</p>
            <p className="text-xs opacity-80">{touchEditMode.newTime} · {touchEditMode.newDuration}min{touchEditMode.staffId !== touchEditMode.appt?.staff_id ? ` · ${touchEditMode.staffName || staff.find(s => s.id === touchEditMode.staffId)?.name || ''}` : ''}</p>
          </div>
          <button onClick={cancelTouchEdit} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20"><XIcon size={18} /></button>
        </div>
      )}
      {/* Header - Fresha style single row */}
      <div className="flex items-center px-2 py-1.5 border-b border-[#E5E7E1] flex-shrink-0 gap-1.5 overflow-visible" style={{ WebkitOverflowScrolling: 'touch' }}>
        <button onClick={() => setCurrentDate(new Date())} className="px-2 py-1 text-xs font-medium text-[#556B2F] border border-[#C5CDB8] hover:bg-[#E9EDDF] rounded-md transition-colors flex-shrink-0 whitespace-nowrap" data-testid="today-btn">{t('appointments.today')}</button>
        <Button size="sm" variant="ghost" onClick={() => setCurrentDate(d => subDays(d, calendarView === 'week' ? 7 : calendarView === '3day' ? 3 : 1))} data-testid="prev-day-btn" className="h-7 w-7 p-0 flex-shrink-0"><ChevronLeft size={16} /></Button>
        <div className="relative flex-shrink-0">
          <button onClick={() => { setCalPickerOpen(!calPickerOpen); setPickerMonth(new Date(currentDate)); }} className="text-sm font-semibold text-[#1A2421] hover:text-[#556B2F] transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap" data-testid="date-display">
            {calendarView === 'day' ? fmt(currentDate, 'EEE d MMM') :
             calendarView === '3day' ? `${fmt(currentDate, 'd MMM')} - ${fmt(addDays(currentDate, 2), 'd MMM')}` :
             `${fmt(startOfWeek(currentDate, { weekStartsOn: 1 }), 'd MMM')} - ${fmt(addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), 6), 'd MMM')}`}
            <ChevronDown size={14} className={`text-[#5C6656] transition-transform ${calPickerOpen ? 'rotate-180' : ''}`} />
          </button>
          {calPickerOpen && (
            <div onClick={e => e.stopPropagation()} className="absolute top-9 left-0 z-[200] bg-white border border-[#E5E7E1] rounded-xl shadow-2xl p-4" style={{ width: '540px' }} data-testid="cal-picker-dropdown">
              <div className="flex gap-6">
                {/* Left month */}
                {[0, 1].map(offset => {
                  const monthDate = new Date(pickerMonth);
                  monthDate.setMonth(monthDate.getMonth() + offset);
                  const year = monthDate.getFullYear();
                  const month = monthDate.getMonth();
                  const firstDay = new Date(year, month, 1);
                  const lastDay = new Date(year, month + 1, 0);
                  let startDow = firstDay.getDay();
                  startDow = startDow === 0 ? 6 : startDow - 1;
                  const cells = [];
                  for (let i = 0; i < startDow; i++) {
                    const d = new Date(year, month, -(startDow - i - 1));
                    cells.push({ date: d, isOtherMonth: true });
                  }
                  for (let i = 1; i <= lastDay.getDate(); i++) {
                    cells.push({ date: new Date(year, month, i), isOtherMonth: false });
                  }
                  const rem = cells.length % 7;
                  if (rem > 0) for (let i = 1; i <= 7 - rem; i++) cells.push({ date: new Date(year, month + 1, i), isOtherMonth: true });
                  const todayStr = fmt(new Date(), 'yyyy-MM-dd');
                  const selectedStr = fmt(currentDate, 'yyyy-MM-dd');
                  return (
                    <div key={offset} className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        {offset === 0 ? (
                          <button onClick={() => setPickerMonth(prev => { const d = new Date(prev); d.setMonth(d.getMonth() - 1); return d; })} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#E9EDDF]"><ChevronLeft size={16} /></button>
                        ) : <div className="w-7" />}
                        <span className="text-sm font-bold text-[#1A2421]">{fmt(monthDate, 'MMMM yyyy')}</span>
                        {offset === 1 ? (
                          <button onClick={() => setPickerMonth(prev => { const d = new Date(prev); d.setMonth(d.getMonth() + 1); return d; })} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#E9EDDF]"><ChevronRight size={16} /></button>
                        ) : <div className="w-7" />}
                      </div>
                      <div className="grid grid-cols-7 gap-0">
                        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
                          <div key={d} className="text-[10px] font-bold text-[#5C6656] text-center py-1.5">{d}</div>
                        ))}
                        {cells.map((cell, idx) => {
                          const cellStr = fmt(cell.date, 'yyyy-MM-dd');
                          const isToday = cellStr === todayStr;
                          const isSelected = cellStr === selectedStr;
                          return (
                            <button key={idx} onClick={() => { setCurrentDate(cell.date); setCalPickerOpen(false); }}
                              className={`w-9 h-9 text-xs rounded-full flex items-center justify-center transition-all mx-auto
                                ${isSelected ? 'bg-[#556B2F] text-white font-bold' : isToday ? 'ring-1 ring-[#556B2F] text-[#556B2F] font-bold' : cell.isOtherMonth ? 'text-[#D0D3CC]' : 'text-[#1A2421] hover:bg-[#F5F7F0]'}`}>
                              {cell.date.getDate()}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={() => setCurrentDate(d => addDays(d, calendarView === 'week' ? 7 : calendarView === '3day' ? 3 : 1))} data-testid="next-day-btn" className="h-7 w-7 p-0 flex-shrink-0"><ChevronRight size={16} /></Button>
        {locations.length > 1 && (
          <select value={selectedLocation} onChange={e => setSelectedLocation(e.target.value)}
            className="h-7 text-xs border border-[#E5E7E1] rounded-lg px-1.5 bg-white text-[#1A2421] flex-shrink-0" data-testid="location-filter">
            <option value="">{t('calendar.allLocations')}</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}
        <div className="relative flex-shrink-0">
          <button onClick={() => setStaffFilterOpen(!staffFilterOpen)} data-testid="staff-filter-btn"
            className="h-7 text-xs border border-[#E5E7E1] rounded-lg px-2 bg-white text-[#1A2421] hover:border-[#556B2F] transition-colors flex items-center gap-1 whitespace-nowrap">
            {staffFilterMode === 'scheduled' ? t('calendar.scheduledTeam') : staffFilterMode === 'all' ? t('calendar.allTeam') : `${selectedStaffIds.size} selected`}
            <ChevronDown size={12} className="text-[#5C6656]" />
          </button>
          {staffFilterOpen && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-[#E5E7E1] rounded-xl shadow-xl z-50" data-testid="staff-filter-dropdown">
              <div className="p-3 border-b border-[#E5E7E1]">
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C6656]" />
                  <input className="w-full pl-8 pr-3 py-1.5 text-sm border border-[#E5E7E1] rounded-lg" placeholder="Search" />
                </div>
              </div>
              <div className="py-1">
                <button onClick={() => { setStaffFilterMode('scheduled'); setStaffFilterOpen(false); }} className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-[#F5F7F0] ${staffFilterMode === 'scheduled' ? 'text-[#556B2F] font-semibold' : 'text-[#1A2421]'}`}>
                  <CalendarDays size={14} /> Scheduled team
                </button>
                <button onClick={() => { setStaffFilterMode('all'); setStaffFilterOpen(false); }} className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-[#F5F7F0] ${staffFilterMode === 'all' ? 'text-[#556B2F] font-semibold' : 'text-[#1A2421]'}`}>
                  <Users size={14} /> All team
                </button>
              </div>
              <div className="border-t border-[#E5E7E1] py-1 max-h-[240px] overflow-y-auto">
                <button onClick={() => {
                  if (selectedStaffIds.size === staff.filter(s => s.is_active).length) {
                    setSelectedStaffIds(new Set());
                  } else {
                    setSelectedStaffIds(new Set(staff.filter(s => s.is_active).map(s => s.id)));
                  }
                  setStaffFilterMode('custom');
                }} className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-[#F5F7F0] text-[#1A2421]">
                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedStaffIds.size === staff.filter(s => s.is_active).length && staffFilterMode === 'custom' ? 'bg-[#556B2F] border-[#556B2F]' : 'border-[#B0B5A8]'}`}>
                    {selectedStaffIds.size === staff.filter(s => s.is_active).length && staffFilterMode === 'custom' && <Check size={10} className="text-white" />}
                  </div>
                  All team members
                </button>
                {staff.filter(s => s.is_active).map(s => (
                  <button key={s.id} onClick={() => {
                    const next = new Set(selectedStaffIds);
                    if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                    setSelectedStaffIds(next);
                    setStaffFilterMode('custom');
                  }} className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-[#F5F7F0]">
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedStaffIds.has(s.id) && staffFilterMode === 'custom' ? 'bg-[#556B2F] border-[#556B2F]' : 'border-[#B0B5A8]'}`}>
                      {selectedStaffIds.has(s.id) && staffFilterMode === 'custom' && <Check size={10} className="text-white" />}
                    </div>
                    <div className="w-6 h-6 rounded-full bg-[#E9EDDF] flex items-center justify-center text-[9px] font-bold text-[#556B2F]">{s.name?.charAt(0)?.toUpperCase()}</div>
                    <span className="text-[#1A2421] font-medium">{s.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Right side: spacer then icons and actions */}
        <div className="flex-1" />
        <button onClick={() => setCalSettingsOpen(!calSettingsOpen)} className="p-1.5 rounded-lg hover:bg-[#E9EDDF] transition-colors flex-shrink-0 hidden lg:block" data-testid="cal-settings-btn">
          <Settings2 size={16} className="text-[#5C6656]" />
        </button>
        <div className="relative flex-shrink-0">
          <button onClick={() => { setNotifPanel(!notifPanel); if (!notifPanel) fetchNotifications(); }} className="relative p-1.5 rounded-lg hover:bg-[#E9EDDF] transition-colors" data-testid="notification-bell">
            <Bell size={16} className="text-[#5C6656]" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#E2725B] text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center" data-testid="notif-badge">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        </div>
        {isOwner && <button onClick={() => setAiPanelOpen(prev => !prev)} className="p-1.5 rounded-lg hover:bg-[#E9EDDF] transition-colors flex-shrink-0" data-testid="ai-assistant-btn">
          <Sparkles size={16} className={aiPanelOpen ? 'text-[#556B2F]' : 'text-[#5C6656]'} />
        </button>}
        <select value={calendarView} onChange={e => setCalendarView(e.target.value)} className="hidden lg:block h-7 text-xs border border-[#E5E7E1] rounded-lg px-1.5 bg-white text-[#1A2421] flex-shrink-0" data-testid="view-selector">
          <option value="day">{t('calendar.day')}</option>
          <option value="3day">3-day</option>
          <option value="week">{t('calendar.week')}</option>
        </select>
        <Button data-testid="new-appointment-btn" onClick={() => openBookDialog('', '09:00')} className="hidden lg:flex bg-[#556B2F] hover:bg-[#4A5D23] text-white h-7 text-xs px-2 flex-shrink-0"><Plus size={12} className="mr-0.5" /> {t('appointments.book')}</Button>
        <Button data-testid="add-sale-btn" onClick={() => { setSaleSidebar(true); setSaleTab('services'); setSaleCart([]); setSaleClient(null); setSalePayStep(false); setSaleComplete(null); }} variant="outline" className="hidden lg:flex border-[#1A2421] text-[#1A2421] hover:bg-[#1A2421] hover:text-white h-7 text-xs px-3 flex-shrink-0 transition-colors"><Receipt size={12} className="mr-1" /> {t('calendar.addSale')}</Button>
      </div>

      {/* Trial countdown banner */}
      {licenceInfo?.licence_status === 'trial' && licenceInfo?.trial_remaining_days !== undefined && (
        <div className="flex items-center justify-between px-4 py-2 bg-[#FFF8E1] border-b border-[#F0E0A0] flex-shrink-0" data-testid="trial-banner">
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-[#D4A373]" />
            <span className="text-sm font-medium text-[#8B6914]">
              {licenceInfo.trial_remaining_days > 0
                ? `Free trial: ${licenceInfo.trial_remaining_days} ${licenceInfo.trial_remaining_days === 1 ? 'day' : 'days'} remaining`
                : 'Your free trial has expired'}
            </span>
          </div>
          <Button size="sm" onClick={() => window.location.href = '/settings'} className="bg-[#556B2F] hover:bg-[#4A5D23] text-white h-7 text-xs" data-testid="subscribe-btn">
            {licenceInfo.trial_remaining_days > 0 ? t('settings.subscribe') : t('settings.subscribe')}
          </Button>
        </div>
      )}
      {/* Trial expired lockout */}
      {licenceInfo?.trial_expired && (
        <div className="flex items-center justify-center px-4 py-3 bg-[#FDE8E8] border-b border-[#F5C6C6] flex-shrink-0" data-testid="trial-expired-banner">
          <span className="text-sm font-semibold text-[#C0392B]">Your 14-day free trial has ended. Subscribe to continue using Apptio.</span>
          <Button size="sm" onClick={() => window.location.href = '/settings'} className="bg-[#C0392B] hover:bg-[#A93226] text-white h-7 text-xs ml-3">{t('settings.subscribe')}</Button>
        </div>
      )}

      {/* Rebook Mode Banner (Fresha-style) */}
      {rebookMode && (
        <div className="flex items-center justify-center px-6 py-3.5 flex-shrink-0 z-50 relative" style={{ backgroundColor: '#6C63FF' }} data-testid="rebook-banner">
          <span className="text-white font-semibold text-base tracking-wide">{rebookMode._isReschedule ? t('appointments.selectTimeToReschedule') : t('appointments.selectTimeToBook')}</span>
          <button onClick={() => { setRebookMode(null); toast.info(rebookMode._isReschedule ? t('appointments.reschedCancelled') : t('appointments.rebookCancelled')); }} className="absolute right-4 text-white/80 hover:text-white" data-testid="cancel-rebook-btn">
            <XIcon size={20} />
          </button>
        </div>
      )}

      {/* Calendar Grid + Sidebar */}
      <div className="flex-1 overflow-hidden flex" onTouchStart={handleCalendarTouchStart} onTouchMove={handleCalendarTouchMove} onTouchEnd={handleCalendarTouchEnd}>
        {/* 3-panel swipe container */}
        <div className="flex-1 overflow-hidden relative min-w-0">
          {/* Prev page peek (visible when swiping right) */}
          {swipeOffset > 0 && (
            <div className="absolute inset-0 bg-[#FAFAF8] border-r border-[#E5E7E1] flex flex-col" style={{ transform: `translateX(${swipeOffset - window.innerWidth}px)`, transition: swipeTransition ? 'transform 0.18s ease-out' : 'none' }}>
              <div className="flex border-b border-[#E5E7E1] flex-shrink-0 bg-white px-4 py-3">
                <span className="text-sm font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{format(subDays(currentDate, swipeStep), calendarView === 'day' ? 'EEEE, d MMMM' : 'd MMM')} {calendarView !== 'day' && `- ${format(subDays(currentDate, 1), 'd MMM')}`}</span>
              </div>
              <div className="flex-1 flex">
                <div className="w-12 flex-shrink-0 border-r border-[#E5E7E1] bg-[#FAFAF8]">
                  {Array.from({ length: END_HOUR - START_HOUR }, (_, i) => i + START_HOUR).map(h => (
                    <div key={h} className="relative" style={{ height: 4 * SLOT_HEIGHT }}>
                      <span className="absolute -top-2 left-1 text-[10px] text-[#B0B5A8]">{h > 12 ? h - 12 : h}:00</span>
                    </div>
                  ))}
                </div>
                <div className="flex-1 bg-white" style={{ backgroundImage: `repeating-linear-gradient(to bottom, transparent, transparent ${SLOT_HEIGHT - 1}px, #F0F0EC ${SLOT_HEIGHT - 1}px, #F0F0EC ${SLOT_HEIGHT}px)` }} />
              </div>
            </div>
          )}
          {/* Next page peek (visible when swiping left) */}
          {swipeOffset < 0 && (
            <div className="absolute inset-0 bg-[#FAFAF8] border-l border-[#E5E7E1] flex flex-col" style={{ transform: `translateX(${swipeOffset + window.innerWidth}px)`, transition: swipeTransition ? 'transform 0.18s ease-out' : 'none' }}>
              <div className="flex border-b border-[#E5E7E1] flex-shrink-0 bg-white px-4 py-3">
                <span className="text-sm font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{format(addDays(currentDate, swipeStep), calendarView === 'day' ? 'EEEE, d MMMM' : 'd MMM')} {calendarView !== 'day' && `- ${format(addDays(currentDate, swipeStep * 2 - 1), 'd MMM')}`}</span>
              </div>
              <div className="flex-1 flex">
                <div className="w-12 flex-shrink-0 border-r border-[#E5E7E1] bg-[#FAFAF8]">
                  {Array.from({ length: END_HOUR - START_HOUR }, (_, i) => i + START_HOUR).map(h => (
                    <div key={h} className="relative" style={{ height: 4 * SLOT_HEIGHT }}>
                      <span className="absolute -top-2 left-1 text-[10px] text-[#B0B5A8]">{h > 12 ? h - 12 : h}:00</span>
                    </div>
                  ))}
                </div>
                <div className="flex-1 bg-white" style={{ backgroundImage: `repeating-linear-gradient(to bottom, transparent, transparent ${SLOT_HEIGHT - 1}px, #F0F0EC ${SLOT_HEIGHT - 1}px, #F0F0EC ${SLOT_HEIGHT}px)` }} />
              </div>
            </div>
          )}
          {/* Current page */}
          <div className="absolute inset-0 flex flex-col" style={{ transform: `translateX(${swipeOffset}px)`, transition: swipeTransition ? 'transform 0.18s ease-out' : 'none' }}>
        {/* Staff/Day header - hide on closed dates; week view has its own header below */}
        {!closedDates.includes(dateStr) && calendarView !== 'week' && (
        <div ref={calHeaderRef} className="overflow-hidden border-b border-[#E5E7E1] flex-shrink-0 bg-white sticky top-0 z-10">
          <div className="flex w-full" style={calendarView === 'day' && activeStaff.length > 0 ? { minWidth: `${activeStaff.length * 140 + 48}px` } : undefined}>
          <div className="w-12 flex-shrink-0 border-r border-[#E5E7E1]" />
          {calendarView === 'day' ? (
            <>
              {activeStaff.map(s => {
                const scheduled = isStaffScheduledForDate(s.id, dateStr);
                return (
                <div key={s.id} className={`flex-1 min-w-[120px] px-2 py-1.5 border-r border-[#E5E7E1] text-center relative ${!scheduled ? 'bg-[#F0F0EC]' : ''}`}>
                  <button onClick={(e) => { e.stopPropagation(); setStaffHeaderMenu(staffHeaderMenu?.staffId === s.id ? null : { staffId: s.id, x: e.currentTarget.getBoundingClientRect().left, y: e.currentTarget.getBoundingClientRect().bottom }); }}
                    className="inline-flex flex-col items-center hover:opacity-80 transition-opacity" data-testid={`staff-header-${s.id}`}>
                    <div className={`w-6 h-6 rounded-full mx-auto mb-0.5 flex items-center justify-center text-[10px] font-bold overflow-hidden ${scheduled ? 'bg-[#E9EDDF] text-[#556B2F]' : 'bg-[#D1D5CB] text-[#8B9A6B]'}`}>
                      {s.avatar_url ? <img src={s.avatar_url} alt="" className="w-full h-full object-cover" /> : s.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="flex items-center gap-0.5">
                      <p className={`text-[11px] font-medium truncate ${scheduled ? 'text-[#1A2421]' : 'text-[#8B9A6B]'}`}>{s.name}{s.last_name ? ` ${s.last_name}` : ''}</p>
                      <ChevronDown size={10} className="text-[#5C6656]" />
                    </div>
                    {!scheduled && <p className="text-[8px] text-[#B0B5A8]">Not scheduled</p>}
                  </button>
                </div>
                );
              })}
            </>
          ) : (
            <>
              {viewDates.map(d => {
                const dt = new Date(d + 'T12:00:00');
                const isToday = d === format(new Date(), 'yyyy-MM-dd');
                return (
                  <div key={d} className={`flex-1 min-w-[100px] px-2 py-1.5 border-r border-[#E5E7E1] text-center ${isToday ? 'bg-[#F5F7F0]' : ''}`}>
                    <p className="text-[10px] font-bold uppercase text-[#5C6656]">{fmt(dt, 'EEE')}</p>
                    <p className={`text-sm font-semibold ${isToday ? 'text-[#556B2F]' : 'text-[#1A2421]'}`}>{fmt(dt, 'd')}</p>
                  </div>
                );
              })}
            </>
          )}
          {activeStaff.length === 0 && calendarView === 'day' && <div className="flex-1 px-3 py-3 text-center text-sm text-[#5C6656]"></div>}
          </div>
        </div>
        )}

        {/* Business Closed overlay - full area, no time grid */}
        {closedDates.includes(dateStr) && (
          <div className="flex-1 flex items-center justify-center bg-[#F9F8F6]" data-testid="business-closed-overlay">
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E5E7E1] flex items-center justify-center"><CalendarDays size={28} className="text-[#5C6656]" /></div>
              <h3 className="text-xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('calendar.businessClosedTitle')}</h3>
              <p className="text-sm text-[#5C6656] mt-2">{t('calendar.businessClosedDesc')}</p>
              <p className="text-xs text-[#B0B5A8] mt-1">{t('calendar.manageClosedDates')}</p>
            </div>
          </div>
        )}

        {/* Subtle loading indicator: thin animated bar at the top of the calendar surface while a fetch is in flight.
            Avoids the blank "white screen" / "No scheduled team members" flash during date navigation. */}
        {!closedDates.includes(dateStr) && !calendarDataReady && (
          <div className="absolute top-0 left-0 right-0 h-[2px] z-40 overflow-hidden pointer-events-none" data-testid="calendar-loading-bar">
            <div className="h-full w-1/3 bg-[#556B2F]/70 animate-[loadingBar_1.2s_ease-in-out_infinite]" />
          </div>
        )}

        {/* No scheduled team members - Fresha-style with time gutter (day/3-day only) */}
        {/* Only show the empty-state CTA when data is ACTUALLY loaded for this date — never during the initial fetch.
            Otherwise users see a "No scheduled team members" flash for 1-2s on hard refresh. */}
        {!closedDates.includes(dateStr) && !anyStaffWorking && calendarView !== 'week' && calendarDataReady && (
          <div className="flex-1 flex overflow-auto" data-testid="no-scheduled-overlay">
            {/* Time gutter */}
            <div className="w-12 flex-shrink-0 border-r border-[#E5E7E1] bg-[#FAFAF8]">
              {Array.from({ length: 15 }, (_, i) => i + 7).map(h => (
                <div key={h} className="relative" style={{ height: 4 * SLOT_HEIGHT }}>
                  <span className="absolute -top-2 left-1 text-[10px] text-[#B0B5A8]">{h > 12 ? h - 12 : h}:00{h >= 12 ? 'pm' : 'am'}</span>
                </div>
              ))}
            </div>
            {/* Centered overlay message */}
            <div className="flex-1 flex items-center justify-center bg-white">
              <div className="text-center">
                <CalendarDays size={48} className="mx-auto mb-4 text-[#B0B5A8]" />
                <h3 className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('calendar.noScheduledTeam')}</h3>
                <p className="text-sm text-[#5C6656] mt-1 mb-4">{t('calendar.addAvailability')}</p>
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" onClick={() => window.location.href = '/staff'} className="border-[#1A2421] text-[#1A2421]">{t('calendar.scheduledShifts')}</Button>
                  <Button onClick={() => window.location.href = '/staff'} className="bg-[#1A2421] text-white">{t('calendar.viewAllTeam')}</Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!closedDates.includes(dateStr) && anyStaffWorking && calendarView !== 'week' && (
        <div ref={calRef} className="flex-1 overflow-auto" data-testid="calendar-grid">
          <div className="flex relative w-full" style={{ minHeight: TOTAL_SLOTS * SLOT_HEIGHT, minWidth: calendarView === 'day' && activeStaff.length > 0 ? `${activeStaff.length * 140 + 48}px` : '100%' }}>
            {/* Current time red line (cruiser) — compares to TODAY in the business's timezone
                so an admin travelling abroad still sees the line on the correct date. */}
            {(() => {
              try {
                if (!businessTimezone) return format(currentDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                const parts = new Intl.DateTimeFormat('en-CA', { timeZone: businessTimezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
                const y = parts.find(p => p.type === 'year')?.value;
                const m = parts.find(p => p.type === 'month')?.value;
                const d = parts.find(p => p.type === 'day')?.value;
                return format(currentDate, 'yyyy-MM-dd') === `${y}-${m}-${d}`;
              } catch { return format(currentDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd'); }
            })() && nowMinutes >= START_HOUR * 60 && nowMinutes <= END_HOUR * 60 && (() => {
              const topPx = ((nowMinutes - START_HOUR * 60) / SLOT_MINUTES) * SLOT_HEIGHT;
              const hh = Math.floor(nowMinutes / 60);
              const mm = nowMinutes % 60;
              const timeLabel = `${hh}:${String(mm).padStart(2, '0')}`;
              return (
                <div className="absolute left-0 right-0 z-30 pointer-events-none" style={{ top: topPx }} data-testid="time-cruiser">
                  <div className="flex items-center">
                    <span className="text-[9px] font-bold text-[#E74C3C] bg-[#E74C3C] text-white rounded-full px-1.5 py-0.5 ml-0.5 leading-none z-10">{timeLabel}</span>
                    <div className="flex-1 h-[2px] bg-[#E74C3C]" />
                  </div>
                </div>
              );
            })()}
            {/* Time gutter */}
            <div className="w-12 flex-shrink-0 border-r border-[#E5E7E1] bg-[#FAFAF8]">
              {slotTimes.map((time, i) => {
                const isHour = i % 4 === 0;
                const isBeforeHour = i % 4 === 3;
                return (
                  <div key={time} className="flex items-start justify-end pr-1" style={{ height: SLOT_HEIGHT, borderTop: isHour ? '1px solid #D0D2CB' : 'none', borderBottom: isBeforeHour ? '1px solid #D0D2CB' : '1px dashed #DDDFD8' }}>
                    {isHour && <span className="text-[9px] font-medium text-[#8A8E84] -mt-0.5">{time}</span>}
                  </div>
                );
              })}
            </div>

            {/* Day view: Staff columns */}
            {calendarView === 'day' && activeStaff.map(s => {
              const staffAppts = getAppointmentsForStaff(s.id, dateStr);
              const staffBlocks = blockedTimes.filter(b => b.staff_id === s.id && b.date === dateStr);
              const allEvents = [...staffAppts, ...staffBlocks.map(b => ({ ...b, _isBlock: true }))];
              const overlapLayout = getOverlapLayout(allEvents);
              const scheduled = isStaffScheduledForDate(s.id, dateStr);
              return (
                <div key={s.id} data-staff-column={s.id} className={`flex-1 min-w-[120px] border-r border-[#E5E7E1] relative`}
                  onDragOver={(e) => handleDragOver(e, s.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, s.id)}>
                  {slotTimes.map((time, i) => {
                    const isHour = i % 4 === 0;
                    const isBeforeHour = i % 4 === 3;
                    const inWork = scheduled ? isWithinWorkHours(s.id, time) : false;
                    return (
                      <div key={time}
                        className={`cursor-pointer transition-colors ${(blockPlacementMode || apptPlacementMode) ? 'hover:bg-[#556B2F]/10' : ''} ${!scheduled ? 'bg-[#E8E8E4]' : inWork ? 'bg-white hover:bg-[#F5F7F0]' : 'bg-[#F0F0EC]'}`}
                        style={{ height: SLOT_HEIGHT, borderTop: isHour ? '1px solid #D0D2CB' : 'none', borderBottom: isBeforeHour ? '1px solid #D0D2CB' : '1px dashed #DDDFD8' }}
                        onClick={(e) => {
                          if (blockPlacementMode) {
                            // Desktop click — place 30min block
                            const [h, m] = time.split(':').map(Number);
                            const endM = h * 60 + m + 30;
                            setBlockForm({ staff_id: s.id, date: dateStr, start_time: time, end_time: `${Math.floor(endM / 60).toString().padStart(2, '0')}:${(endM % 60).toString().padStart(2, '0')}`, reason: '' });
                            setBlockDialog(true);
                            setBlockPlacementMode(false);
                            return;
                          }
                          if (apptPlacementMode) {
                            setForm(prev => ({ ...prev, staff_id: s.id, date: dateStr, start_time: time }));
                            setBookDialogOpen(true);
                            setApptPlacementMode(false);
                            return;
                          }
                          openSlotPopup(e, s.id, time);
                        }}
                        onTouchStart={(e) => {
                          if (!blockPlacementMode) return;
                          const touch = e.touches[0];
                          const col = e.target.closest('[data-staff-column]');
                          if (!col) return;
                          const colRect = col.getBoundingClientRect();
                          // Create visual preview block
                          const preview = document.createElement('div');
                          preview.id = 'block-place-preview';
                          preview.style.cssText = `position:fixed;left:${colRect.left + 1}px;width:${colRect.width - 2}px;top:${touch.clientY}px;height:${SLOT_HEIGHT}px;background:rgba(142,145,154,0.5);border-left:3px solid #6B6F78;border-radius:3px;z-index:9990;pointer-events:none;display:flex;align-items:center;padding:0 8px;`;
                          const [sh, sm] = time.split(':').map(Number);
                          const startMins = sh * 60 + sm;
                          preview.innerHTML = `<span style="font-size:11px;font-weight:700;color:#3A3D45;white-space:nowrap;">${time} - ${minutesToTime(startMins + 15)}</span>`;
                          document.body.appendChild(preview);
                          blockPlaceRef.current = {
                            staffId: s.id, startTime: time, startMins,
                            startY: touch.clientY, colLeft: colRect.left, colWidth: colRect.width,
                            slotHeight: SLOT_HEIGHT
                          };
                        }}
                        data-testid={`slot-${s.id}-${time}`} />
                    );
                  })}
                  {dragOverInfo && dragOverInfo.staffId === s.id && (
                    <div className="absolute left-0 right-0 z-30 pointer-events-none" style={{ top: dragOverInfo.top }}>
                      <div className="h-0.5 bg-[#556B2F] w-full shadow-sm" />
                      <span className="absolute -top-3 left-1 text-[9px] font-bold bg-[#556B2F] text-white px-1.5 py-0.5 rounded shadow-sm">{dragOverInfo.time}</span>
                    </div>
                  )}
                  {staffAppts.map(appt => {
                    const pos = getApptPosition(appt);
                    const colors = statusColors[appt.status] || statusColors.confirmed;
                    const ol = overlapLayout[appt.id] || { col: 0, totalCols: 1 };
                    const widthPct = 100 / ol.totalCols;
                    const leftPct = ol.col * widthPct;
                    // Render at exact duration height — no minimum floor so 5/10-min appts
                    // occupy their true proportional space (matches Fresha exactly).
                    const renderedHeight = resizing?.apptId === appt.id ? Math.max(Math.max((SNAP_MINUTES / SLOT_MINUTES) * SLOT_HEIGHT - 2, 8), ((resizing.currentDuration || resizing.origDuration) / SLOT_MINUTES) * SLOT_HEIGHT - 1) : pos.height;
                    // isShort: tile too small for a second line. Anchor on appointment duration
                    // (under 30 mins) so 30-min tiles always get the standard two-row layout (client + service on its own line).
                    const apptDur = appt.duration || (appt.end_time ? (timeToMinutes(appt.end_time) - timeToMinutes(appt.start_time)) : 30);
                    const isShort = apptDur < 30;
                    // isMedium: 30-min slots are tight — fit time+client+service in two rows, drop the dedicated price footer
                    // (inline a price/status chip on the top row instead). Keeps the service name visible without clipping.
                    const isMedium = !isShort && apptDur < 45;
                    return (
                      <div key={appt.id} draggable onDragStart={(e) => handleDragStart(e, appt)} onDragEnd={handleDragEnd}
                        onTouchStart={(e) => handleTouchDragStart(e, appt)}
                        onClick={(e) => { e.stopPropagation(); if (touchEditMode) return; if (Date.now() - lastDropRef.current < 1000) return; openSidebar(appt); }}
                        data-testid={`appt-block-${appt.id}`}
                        className={`absolute rounded cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md hover:z-20 transition-all overflow-visible z-[5] group select-none ${rebookMode ? 'opacity-40 pointer-events-none' : ''}`}
                        style={{ top: pos.top, height: renderedHeight, backgroundColor: colors.bg, borderLeft: `3px solid ${colors.border}`, left: `calc(${leftPct}% + 1px)`, width: `calc(${widthPct}% - 2px)`, borderBottom: appt.notes ? '2px solid #E2725B' : 'none', opacity: dragAppt?.id === appt.id ? 0.3 : 1, WebkitUserSelect: 'none', touchAction: 'pan-y' }}>
                        <div className={`px-1.5 h-full flex flex-col overflow-hidden ${isShort ? 'py-0 justify-center' : isMedium ? 'py-0 justify-start' : 'py-0.5 justify-center'}`}>
                          {isShort ? (
                            <div className="flex items-center gap-1 overflow-hidden">
                              <span data-block-time className="flex-shrink-0 font-medium whitespace-nowrap" style={{ fontSize: `${calFontSize - 2}px`, color: colors.text, opacity: 0.8 }}>{appt.start_time}-{appt.end_time}</span>
                              <p className="font-semibold truncate leading-none min-w-0" style={{ fontSize: `${calFontSize - 1}px`, color: colors.text }}>{appt.client_name}</p>
                              {appt.service_name && (
                                <p className="truncate leading-none uppercase flex-1 min-w-0" style={{ fontSize: `${calFontSize - 2}px`, color: colors.text, opacity: 0.75 }}>· {appt.service_name}</p>
                              )}
                              <div className="flex items-center gap-0.5 flex-shrink-0">
                                {appt.notes && <MessageSquare size={calFontSize - 2} className="flex-shrink-0" style={{ color: colors.text, opacity: 0.7 }} />}
                                {appt.client_requested && <span style={{ fontSize: `${Math.max(calFontSize - 1, 10)}px`, lineHeight: 1 }}>❤️</span>}
                                {appt.source === 'online' && <span style={{ fontSize: `${Math.max(calFontSize - 1, 10)}px`, lineHeight: 1 }}>☁️</span>}
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-1">
                                <span data-block-time className="flex-shrink-0 font-medium whitespace-nowrap" style={{ fontSize: `${calFontSize - 1}px`, color: colors.text, opacity: 0.8 }}>{appt.start_time}-{appt.end_time}</span>
                                <p className="font-semibold truncate leading-tight flex-1 min-w-0" style={{ fontSize: `${calFontSize}px`, color: colors.text }}>{appt.client_name}</p>
                                <div className="flex items-center gap-0.5 flex-shrink-0">
                                  {appt.notes && <MessageSquare size={calFontSize} className="flex-shrink-0" style={{ color: colors.text, opacity: 0.7 }} />}
                                  {appt.client_requested && <span style={{ fontSize: `${Math.max(calFontSize, 11)}px`, lineHeight: 1 }}>❤️</span>}
                                  {appt.source === 'online' && <span style={{ fontSize: `${Math.max(calFontSize, 11)}px`, lineHeight: 1 }}>☁️</span>}
                                  {isMedium && (
                                    appt.status === 'completed' ? (
                                      <span className={`text-[7px] font-bold px-1 py-0.5 rounded ${appt.payment_status === 'partial' ? 'bg-[#E2725B] text-white' : ''}`} style={appt.payment_status !== 'partial' ? { backgroundColor: 'rgba(0,0,0,0.1)', color: colors.text } : {}}>{appt.payment_status === 'partial' ? 'Part-paid' : 'Paid'}</span>
                                    ) : appt.payment_status === 'pending_paylink' ? (
                                      <span className="text-[7px] font-bold px-1 py-0.5 rounded bg-[#D4A373] text-white animate-pulse">PayLink</span>
                                    ) : (
                                      <span className="text-[8px] font-medium" style={{ color: colors.text, opacity: 0.8 }}>{'\u00A3'}{appt.price?.toFixed(0)}</span>
                                    )
                                  )}
                                </div>
                              </div>
                              <p className="truncate leading-tight uppercase" style={{ fontSize: `${calFontSize - 1}px`, color: colors.text, opacity: 0.8 }}>{appt.service_name}</p>
                              {!isMedium && (
                                <div className="mt-auto flex items-center justify-end">
                                  {appt.status === 'completed' ? (
                                    <span className={`text-[7px] font-bold px-1 py-0.5 rounded ${appt.payment_status === 'partial' ? 'bg-[#E2725B] text-white' : ''}`} style={appt.payment_status !== 'partial' ? { backgroundColor: 'rgba(0,0,0,0.1)', color: colors.text } : {}}>{appt.payment_status === 'partial' ? 'Part-paid' : 'Paid'}</span>
                                  ) : appt.payment_status === 'pending_paylink' ? (
                                    <span className="text-[7px] font-bold px-1 py-0.5 rounded bg-[#D4A373] text-white animate-pulse">PayLink</span>
                                  ) : (
                                    <span className="text-[8px] font-medium" style={{ color: colors.text, opacity: 0.8 }}>{'\u00A3'}{appt.price?.toFixed(0)}</span>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        {/* Resize handle — mouse only. Touch resizing disabled; use Change duration dialog. */}
                        {appt.status !== 'completed' && appt.status !== 'cancelled' && (
                          <div className="absolute bottom-0 left-0 right-0 h-3 cursor-ns-resize opacity-0 group-hover:opacity-100 active:opacity-100 flex items-center justify-center z-30 hidden md:flex"
                            onMouseDown={(e) => handleResizeStart(e, appt)} data-testid={`resize-${appt.id}`}>
                            <div className="w-8 h-1 rounded-full bg-white/60" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {staffBlocks.map(block => {
                    const bStart = timeToMinutes(block.start_time) - START_HOUR * 60;
                    const bEnd = timeToMinutes(block.end_time) - START_HOUR * 60;
                    const top = (bStart / SLOT_MINUTES) * SLOT_HEIGHT;
                    const height = Math.max(((bEnd - bStart) / SLOT_MINUTES) * SLOT_HEIGHT - 1, SLOT_HEIGHT - 1);
                    const ol = overlapLayout[block.id] || { col: 0, totalCols: 1 };
                    const widthPct = 100 / ol.totalCols;
                    const leftPct = ol.col * widthPct;
                    const isActive = activeBlock?.id === block.id;
                    return (
                      <div key={block.id}
                        onMouseDown={(e) => { if (!isActive) handleBlockMouseDown(e, block); else handleActiveBlockDragStart(e, block.id); }}
                        onTouchStart={(e) => { if (touchEditMode) return; handleTouchDragStart(e, { ...block, _isBlock: true, _onTap: () => setEditingBlock({ ...block }) }); }}
                        className={`absolute z-[6] group select-none ${isActive ? 'cursor-grab z-20' : 'cursor-pointer'}`}
                        style={{ top, height: Math.max(height, 28), left: `calc(${leftPct}% + 1px)`, width: `calc(${widthPct}% - 2px)`, WebkitUserSelect: 'none', touchAction: 'manipulation', boxShadow: isActive ? '0 0 0 2px #556B2F, 0 4px 12px rgba(0,0,0,0.15)' : 'none' }}
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); if (touchEditMode || isActive || Date.now() - lastDropRef.current < 1000) return; setEditingBlock({ ...block }); }}
                        data-testid={`block-${block.id}`}>
                        <div className="w-full h-full rounded-sm border-l-[3px] border-[#8E919A] flex flex-col justify-start px-2 py-1 overflow-hidden select-none pointer-events-auto"
                          style={{ background: '#B0B4BE', WebkitUserSelect: 'none', touchAction: 'manipulation' }}>
                          <span className="text-[10px] text-[#3A3D45] font-bold leading-tight truncate select-none">{block.start_time} - {block.end_time} {block.reason || 'Blocked time'}</span>
                          {(block.notes || block.description) && <span className="text-[9px] text-[#4A4D55] leading-tight select-none mt-0.5 line-clamp-3">{block.notes || block.description}</span>}
                        </div>
                        {/* Desktop: hover resize handle. Touch edit: always visible */}
                        <div className={`absolute bottom-0 left-0 right-0 h-4 cursor-ns-resize flex items-center justify-center z-30 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                          style={{ background: isActive ? 'linear-gradient(transparent, rgba(85,107,47,0.15))' : 'linear-gradient(transparent, rgba(0,0,0,0.08))' }}
                          onMouseDown={(e) => {
                            e.stopPropagation(); e.preventDefault();
                            if (!isActive) activateBlock(block);
                            const ab = { id: block.id, original: { ...block }, current: { ...block } };
                            activeBlockRef.current = ab;
                            handleActiveBlockResizeStart(e, ab);
                          }}
                          data-testid={`block-resize-${block.id}`}>
                          <div className={`w-8 h-1 rounded-full ${isActive ? 'bg-[#556B2F]/60' : 'bg-[#6B6F78]/40'}`} />
                        </div>
                      </div>
                    );
                  })}

                </div>
              );
            })}

            {/* Multi-day view: Date columns (3-day / week) */}
            {calendarView !== 'day' && viewDates.map(d => {
              const dayAppts = appointments.filter(a => a.date === d && a.status !== 'cancelled');
              const dayBlocks = blockedTimes.filter(b => b.date === d);
              const allDayEvents = [...dayAppts, ...dayBlocks.map(b => ({ ...b, _isBlock: true }))];
              const overlapLayout = getOverlapLayout(allDayEvents);
              const dn = format(new Date(d + 'T12:00:00'), 'EEEE').toLowerCase();
              const firstStaff = activeStaff[0];
              const wh = firstStaff?.working_hours?.[dn];
              const workRange = wh?.enabled ? { start: timeToMinutes(wh.start), end: timeToMinutes(wh.end) } : null;
              const dayOff = !workRange;  // Staff not working this day
              return (
                <div key={d} className={`flex-1 min-w-[100px] border-r border-[#E5E7E1] relative ${dayOff ? 'bg-[#E5E7E1]' : ''}`}
                  style={dayOff ? { backgroundImage: 'repeating-linear-gradient(-45deg, rgba(0,0,0,0.04), rgba(0,0,0,0.04) 4px, transparent 4px, transparent 8px)' } : {}}>
                  {slotTimes.map((time, i) => {
                    const isHour = i % 4 === 0;
                    const isBeforeHour = i % 4 === 3;
                    const mins = timeToMinutes(time);
                    const inWork = workRange ? mins >= workRange.start && mins < workRange.end : false;
                    return (
                      <div key={time}
                        className={`transition-colors ${dayOff ? 'cursor-not-allowed' : inWork ? 'cursor-pointer bg-white hover:bg-[#F5F7F0]' : 'cursor-pointer bg-[#F0F0EC]'}`}
                        style={{ height: SLOT_HEIGHT, borderTop: isHour ? '1px solid #D0D2CB' : 'none', borderBottom: isBeforeHour ? '1px solid #D0D2CB' : '1px dashed #DDDFD8', backgroundColor: dayOff ? 'transparent' : undefined }}
                        onClick={(e) => { if (dayOff) return; if (firstStaff) openSlotPopup(e, firstStaff.id, time); }} />
                    );
                  })}
                  {dayAppts.map(appt => {
                    const pos = getApptPosition(appt);
                    const colors = statusColors[appt.status] || statusColors.confirmed;
                    const ol = overlapLayout[appt.id] || { col: 0, totalCols: 1 };
                    const widthPct = 100 / ol.totalCols;
                    const leftPct = ol.col * widthPct;
                    return (
                      <div key={appt.id} onClick={(e) => { e.stopPropagation(); openSidebar(appt); }}
                        data-testid={`appt-block-${appt.id}`}
                        className="absolute rounded cursor-pointer shadow-sm hover:shadow-md hover:z-20 transition-all overflow-hidden z-[5]"
                        style={{ top: pos.top, height: pos.height, backgroundColor: colors.bg, borderLeft: `3px solid ${colors.border}`, left: `calc(${leftPct}% + 1px)`, width: `calc(${widthPct}% - 2px)` }}>
                        <div className="px-1.5 py-0.5 h-full flex flex-col justify-center">
                          <p className="text-[9px] font-semibold text-white truncate">{appt.start_time} {appt.client_name}</p>
                          <p className="text-[8px] text-white/80 truncate">{appt.service_name}</p>
                        </div>
                      </div>
                    );
                  })}
                  {dayBlocks.map(block => {
                    const bStart = timeToMinutes(block.start_time) - START_HOUR * 60;
                    const bEnd = timeToMinutes(block.end_time) - START_HOUR * 60;
                    const top = (bStart / SLOT_MINUTES) * SLOT_HEIGHT;
                    const height = Math.max(((bEnd - bStart) / SLOT_MINUTES) * SLOT_HEIGHT - 1, SLOT_HEIGHT - 1);
                    const ol = overlapLayout[block.id] || { col: 0, totalCols: 1 };
                    const widthPct = 100 / ol.totalCols;
                    const leftPct = ol.col * widthPct;
                    return (
                      <div key={block.id} className="absolute z-[4] cursor-pointer" style={{ top, height, left: `calc(${leftPct}% + 1px)`, width: `calc(${widthPct}% - 2px)` }}
                        onClick={(e) => { e.stopPropagation(); setEditingBlock({ ...block }); }}>
                        <div className="w-full h-full rounded-sm border-l-[3px] border-[#8E919A] flex flex-col justify-center px-2 overflow-hidden select-none"
                          style={{ background: '#B0B4BE', WebkitUserSelect: 'none' }}>
                          <span className="text-[9px] text-[#3A3D45] font-bold truncate select-none">{block.start_time} - {block.end_time} {block.reason || 'Blocked time'}</span>
                          {(block.notes || block.description) && <span className="text-[8px] text-[#4A4D55] select-none truncate">{block.notes || block.description}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
        </div>
      </div>
      )}

      {/* === Fresha-style Week View (Single-staff): time-grid with 7 day columns === */}
      {!closedDates.includes(dateStr) && calendarView === 'week' && activeStaff.length === 1 && (() => {
        const theStaff = activeStaff[0];
        const STAFF_COL_W = 84;
        return (
          <div className="flex-1 flex flex-col overflow-hidden" data-testid="week-view-single">
            {/* Header: staff label column + 7 day headers (sticky top) */}
            <div className="flex border-b border-[#E5E7E1] bg-white flex-shrink-0" style={{ minWidth: `${STAFF_COL_W + viewDates.length * 120}px` }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  setStaffHeaderMenu({ staffId: theStaff.id, x: rect.left, y: rect.top + rect.height });
                }}
                className="flex-shrink-0 border-r border-[#E5E7E1] flex flex-col items-center justify-center py-2 hover:bg-[#F5F7F0] transition-colors cursor-pointer"
                style={{ width: STAFF_COL_W }}
                data-testid={`week-single-staff-label-${theStaff.id}`}>
                <div className="w-9 h-9 rounded-full bg-[#E9EDDF] flex items-center justify-center text-xs font-bold text-[#556B2F] overflow-hidden ring-1 ring-[#556B2F]/30">
                  {theStaff.avatar_url ? <img src={theStaff.avatar_url} alt="" className="w-full h-full object-cover" /> : theStaff.name?.charAt(0)?.toUpperCase()}
                </div>
                <p className="text-[10px] font-bold text-[#1A2421] mt-1 text-center uppercase tracking-wide max-w-[80px] truncate" title={theStaff.name}>{theStaff.name}</p>
              </button>
              {viewDates.map(d => {
                const dt = new Date(d + 'T12:00:00');
                const isToday = d === format(new Date(), 'yyyy-MM-dd');
                return (
                  <div key={d} className={`flex-1 min-w-[120px] px-2 py-2.5 border-r border-[#E5E7E1] ${isToday ? 'bg-[#F5F7F0]' : ''}`}>
                    <p className={`text-sm ${isToday ? 'text-[#556B2F] font-semibold' : 'text-[#1A2421]'}`} style={{ fontFamily: 'Outfit' }}>
                      <span className={`text-base font-bold mr-1.5 ${isToday ? 'text-[#556B2F]' : 'text-[#1A2421]'}`}>{fmt(dt, 'd')}</span>
                      <span className={isToday ? 'text-[#556B2F]' : 'text-[#5C6656]'}>{fmt(dt, 'EEEE')}</span>
                    </p>
                  </div>
                );
              })}
            </div>
            {/* Scrollable body: time gutter + day columns */}
            <div className="flex-1 overflow-auto" data-testid="week-single-scroll">
              <div className="flex relative" style={{ minHeight: TOTAL_SLOTS * SLOT_HEIGHT, minWidth: `${STAFF_COL_W + viewDates.length * 120}px` }}>
                {/* Time gutter (same width as staff label for column alignment) */}
                <div className="flex-shrink-0 border-r border-[#E5E7E1] bg-[#FAFAF8]" style={{ width: STAFF_COL_W }}>
                  {slotTimes.map((time, i) => {
                    const isHour = i % 4 === 0;
                    const isBeforeHour = i % 4 === 3;
                    return (
                      <div key={time} className="flex items-start justify-end pr-2" style={{ height: SLOT_HEIGHT, borderTop: isHour ? '1px solid #D0D2CB' : 'none', borderBottom: isBeforeHour ? '1px solid #D0D2CB' : '1px dashed #DDDFD8' }}>
                        {isHour && <span className="text-[10px] font-medium text-[#8A8E84] -mt-0.5">{time}</span>}
                      </div>
                    );
                  })}
                </div>
                {/* 7 day columns */}
                {viewDates.map(d => {
                  const scheduled = isStaffScheduledForDate(theStaff.id, d);
                  const workRange = getStaffWorkRangeForDate(theStaff, d);
                  const dayClosed = closedDates.includes(d);
                  const dayAppts = appointments.filter(a => a.staff_id === theStaff.id && a.date === d && a.status !== 'cancelled');
                  const dayBlocks = blockedTimes.filter(b => b.staff_id === theStaff.id && b.date === d);
                  const allDayEvents = [...dayAppts, ...dayBlocks.map(b => ({ ...b, _isBlock: true }))];
                  const dayOverlapLayout = getOverlapLayout(allDayEvents);
                  const cantSchedule = dayClosed || !scheduled || !workRange;
                  const isToday = d === format(new Date(), 'yyyy-MM-dd');
                  return (
                    <div key={d} className={`flex-1 min-w-[120px] border-r border-[#E5E7E1] relative ${isToday && !cantSchedule ? 'bg-[#FBFCF7]' : ''}`} data-testid={`week-day-col-${d}`}>
                      {slotTimes.map((time, i) => {
                        const isHour = i % 4 === 0;
                        const isBeforeHour = i % 4 === 3;
                        const mins = timeToMinutes(time);
                        const inWork = workRange ? (mins >= workRange.start && mins < workRange.end) : false;
                        const hatched = cantSchedule || !inWork;
                        return (
                          <div key={time}
                            className={`transition-colors ${cantSchedule ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-[#F5F7F0]'} ${inWork && !cantSchedule ? 'bg-white' : ''}`}
                            style={{
                              height: SLOT_HEIGHT,
                              borderTop: isHour ? '1px solid #D0D2CB' : 'none',
                              borderBottom: isBeforeHour ? '1px solid #D0D2CB' : '1px dashed #DDDFD8',
                              ...(hatched ? { backgroundImage: 'repeating-linear-gradient(-45deg, rgba(0,0,0,0.04), rgba(0,0,0,0.04) 4px, transparent 4px, transparent 8px)', backgroundColor: cantSchedule ? '#F2F2EE' : '#F0F0EC' } : {}),
                            }}
                            onClick={(e) => { if (cantSchedule) return; openSlotPopup(e, theStaff.id, time, d); }}
                            data-testid={`week-slot-${d}-${time}`} />
                        );
                      })}
                      {/* Appointments for that day */}
                      {dayAppts.map(appt => {
                        const pos = getApptPosition(appt);
                        const colors = statusColors[appt.status] || statusColors.confirmed;
                        const ol = dayOverlapLayout[appt.id] || { col: 0, totalCols: 1 };
                        const widthPct = 100 / ol.totalCols;
                        const leftPct = ol.col * widthPct;
                        const renderedHeight = pos.height;
                        const apptDur = appt.duration || (appt.end_time ? (timeToMinutes(appt.end_time) - timeToMinutes(appt.start_time)) : 30);
                        const isShort = apptDur < 30;
                        return (
                          <div key={appt.id}
                            onClick={(e) => { e.stopPropagation(); openSidebar(appt); }}
                            className="absolute rounded cursor-pointer shadow-sm hover:shadow-md hover:z-20 transition-all overflow-hidden z-[5]"
                            style={{ top: pos.top, height: renderedHeight, backgroundColor: colors.bg, borderLeft: `3px solid ${colors.border}`, left: `calc(${leftPct}% + 1px)`, width: `calc(${widthPct}% - 2px)`, borderBottom: appt.notes ? '2px solid #E2725B' : 'none' }}
                            data-testid={`week-appt-${appt.id}`}>
                            <div className={`px-1.5 h-full flex flex-col overflow-hidden ${isShort ? 'py-0 justify-center' : 'py-0.5'}`}>
                              <div className="flex items-center gap-1">
                                <span className="flex-shrink-0 font-medium whitespace-nowrap" style={{ fontSize: `${calFontSize - 1}px`, color: colors.text, opacity: 0.85 }}>{appt.start_time} - {appt.end_time}</span>
                                <span className="font-semibold truncate min-w-0" style={{ fontSize: `${calFontSize}px`, color: colors.text }}>{appt.client_name}</span>
                                {isShort && appt.service_name && (
                                  <span className="truncate uppercase flex-1 min-w-0" style={{ fontSize: `${calFontSize - 2}px`, color: colors.text, opacity: 0.75 }}>· {appt.service_name}</span>
                                )}
                                <div className="flex items-center gap-0.5 flex-shrink-0">
                                  {appt.client_requested && <span style={{ fontSize: `${Math.max(calFontSize, 10)}px`, lineHeight: 1 }}>❤️</span>}
                                  {appt.source === 'online' && <span style={{ fontSize: `${Math.max(calFontSize, 10)}px`, lineHeight: 1 }}>☁️</span>}
                                </div>
                              </div>
                              {!isShort && appt.service_name && (
                                <p className="truncate leading-tight uppercase mt-0.5" style={{ fontSize: `${calFontSize - 1}px`, color: colors.text, opacity: 0.8 }}>{appt.service_name}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {/* Blocks for that day */}
                      {dayBlocks.map(block => {
                        const bStart = timeToMinutes(block.start_time) - START_HOUR * 60;
                        const bEnd = timeToMinutes(block.end_time) - START_HOUR * 60;
                        const top = (bStart / SLOT_MINUTES) * SLOT_HEIGHT;
                        const height = Math.max(((bEnd - bStart) / SLOT_MINUTES) * SLOT_HEIGHT - 1, SLOT_HEIGHT - 1);
                        const ol = dayOverlapLayout[block.id] || { col: 0, totalCols: 1 };
                        const widthPct = 100 / ol.totalCols;
                        const leftPct = ol.col * widthPct;
                        return (
                          <div key={block.id}
                            onClick={(e) => { e.stopPropagation(); setEditingBlock({ ...block }); }}
                            className="absolute z-[4] cursor-pointer"
                            style={{ top, height, left: `calc(${leftPct}% + 1px)`, width: `calc(${widthPct}% - 2px)` }}>
                            <div className="w-full h-full rounded-sm border-l-[3px] border-[#8E919A] flex flex-col justify-start px-2 py-1 overflow-hidden"
                              style={{ background: '#B0B4BE' }}>
                              <span className="text-[10px] text-[#3A3D45] font-bold leading-tight truncate">{block.start_time} - {block.end_time} {block.reason || 'Blocked time'}</span>
                              {(block.notes || block.description) && <span className="text-[9px] text-[#4A4D55] leading-tight mt-0.5 line-clamp-2">{block.notes || block.description}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* === Fresha-style Week View (Multi-staff): staff rows × day columns === */}
      {!closedDates.includes(dateStr) && calendarView === 'week' && activeStaff.length !== 1 && (
        <div className="flex-1 overflow-auto bg-white" data-testid="week-view-fresha">
          {/* Day header row (sticky top) */}
          <div className="flex sticky top-0 z-20 bg-white border-b border-[#E5E7E1]" style={{ minWidth: `${100 + viewDates.length * 160}px` }}>
            <div className="w-[100px] flex-shrink-0 border-r border-[#E5E7E1] bg-white sticky left-0 z-10" />
            {viewDates.map(d => {
              const dt = new Date(d + 'T12:00:00');
              const isToday = d === format(new Date(), 'yyyy-MM-dd');
              return (
                <div key={d} className={`flex-1 min-w-[160px] px-3 py-2.5 border-r border-[#E5E7E1] ${isToday ? 'bg-[#F5F7F0]' : ''}`}>
                  <p className={`text-xs ${isToday ? 'text-[#556B2F] font-semibold' : 'text-[#5C6656]'}`} style={{ fontFamily: 'Outfit' }}>
                    <span className={`text-base font-bold mr-1.5 ${isToday ? 'text-[#556B2F]' : 'text-[#1A2421]'}`}>{fmt(dt, 'd')}</span>
                    {fmt(dt, 'EEEE')}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Empty state: no staff matching filters — only show when data is actually loaded. */}
          {activeStaff.length === 0 && calendarDataReady && (
            <div className="flex-1 flex items-center justify-center py-20" data-testid="week-no-staff">
              <div className="text-center">
                <CalendarDays size={48} className="mx-auto mb-4 text-[#B0B5A8]" />
                <h3 className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('calendar.noScheduledTeam')}</h3>
                <p className="text-sm text-[#5C6656] mt-1">{t('calendar.addAvailability')}</p>
              </div>
            </div>
          )}

          {/* Staff rows */}
          {activeStaff.map(s => (
            <div key={s.id} className="flex border-b border-[#E5E7E1] items-stretch" style={{ minHeight: 200, minWidth: `${100 + viewDates.length * 160}px` }} data-testid={`week-staff-row-${s.id}`}>
              {/* Sticky left staff label (clickable → open staff menu) */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  setStaffHeaderMenu({ staffId: s.id, x: rect.left, y: rect.top + rect.height });
                }}
                className="w-[100px] flex-shrink-0 border-r border-[#E5E7E1] bg-white sticky left-0 z-[5] flex flex-col items-center justify-start pt-5 pb-3 hover:bg-[#F5F7F0] transition-colors cursor-pointer"
                data-testid={`week-staff-label-${s.id}`}>
                <div className="w-11 h-11 rounded-full bg-[#E9EDDF] flex items-center justify-center text-base font-bold text-[#556B2F] overflow-hidden ring-1 ring-[#556B2F]/20">
                  {s.avatar_url ? <img src={s.avatar_url} alt="" className="w-full h-full object-cover" /> : s.name?.charAt(0)?.toUpperCase()}
                </div>
                <p className="text-[11px] font-bold text-[#1A2421] mt-2 text-center uppercase tracking-wide leading-tight max-w-[90px] truncate" title={s.name}>
                  {s.name}
                </p>
              </button>
              {/* 7 day cells */}
              {viewDates.map(d => {
                const scheduled = isStaffScheduledForDate(s.id, d);
                const dayClosed = closedDates.includes(d);
                const staffAppts = appointments.filter(a => a.staff_id === s.id && a.date === d && a.status !== 'cancelled');
                const staffBlocks = blockedTimes.filter(b => b.staff_id === s.id && b.date === d);
                const events = [
                  ...staffAppts.map(a => ({ ...a, _type: 'appt' })),
                  ...staffBlocks.map(b => ({ ...b, _type: 'block' })),
                ].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
                const MAX_VISIBLE = 5;
                const visible = events.slice(0, MAX_VISIBLE);
                const hiddenCount = events.length - visible.length;
                const hatched = !scheduled || dayClosed;
                const isToday = d === format(new Date(), 'yyyy-MM-dd');
                return (
                  <div key={d}
                    className={`flex-1 min-w-[160px] border-r border-[#E5E7E1] p-1.5 relative ${hatched ? '' : 'cursor-pointer hover:bg-[#FAFAF8]'} ${isToday && !hatched ? 'bg-[#FBFCF7]' : ''}`}
                    style={hatched ? { backgroundImage: 'repeating-linear-gradient(-45deg, rgba(0,0,0,0.05), rgba(0,0,0,0.05) 4px, transparent 4px, transparent 8px)', backgroundColor: '#F2F2EE' } : {}}
                    onClick={(e) => {
                      if (hatched) return;
                      if (e.target.closest('[data-week-chip]')) return;
                      openSlotPopup(e, s.id, null, d);
                    }}
                    data-testid={`week-cell-${s.id}-${d}`}>
                    {visible.map(ev => {
                      if (ev._type === 'block') {
                        return (
                          <div key={ev.id} data-week-chip
                            onClick={(e) => { e.stopPropagation(); setEditingBlock({ ...ev }); }}
                            className="mb-1 px-1.5 py-1 rounded border-l-[3px] border-[#8E919A] text-[11px] cursor-pointer hover:shadow-sm transition-shadow overflow-hidden"
                            style={{ background: '#D5D8DE' }}>
                            <div className="flex items-center gap-1 min-w-0">
                              <span className="font-semibold text-[#3A3D45] flex-shrink-0">{ev.start_time}</span>
                              <span className="text-[#3A3D45] truncate">{ev.reason || 'Blocked time'}</span>
                            </div>
                          </div>
                        );
                      }
                      const colors = statusColors[ev.status] || statusColors.confirmed;
                      return (
                        <div key={ev.id} data-week-chip
                          onClick={(e) => { e.stopPropagation(); openSidebar(ev); }}
                          className="mb-1 px-1.5 py-1 rounded border-l-[3px] text-[11px] cursor-pointer hover:shadow-sm transition-shadow overflow-hidden"
                          style={{ backgroundColor: colors.bg, borderLeftColor: colors.border }}
                          data-testid={`week-chip-${ev.id}`}>
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="font-semibold flex-shrink-0" style={{ color: colors.text }}>{ev.start_time}</span>
                            <span className="font-medium truncate flex-1 min-w-0" style={{ color: colors.text }}>{ev.client_name}</span>
                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              {ev.notes && <MessageSquare size={10} style={{ color: colors.text, opacity: 0.7 }} />}
                              {ev.client_requested && <span style={{ fontSize: 10, lineHeight: 1 }}>❤️</span>}
                              {ev.source === 'online' && <span style={{ fontSize: 10, lineHeight: 1 }}>☁️</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {hiddenCount > 0 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setCurrentDate(new Date(d + 'T12:00:00')); setCalendarView('day'); }}
                        className="block mx-auto mt-1 text-[11px] text-[#5C6656] hover:text-[#556B2F] hover:underline font-medium"
                        data-testid={`week-more-${s.id}-${d}`}>
                        {hiddenCount} more
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      </div>
      {/* /current page */}
      </div>
      {/* /3-panel swipe container */}

      {/* Fresha-style Appointment Side Panel */}
      {sidebarAppt && (() => {
        const sc = statusColors[sidebarAppt.status] || statusColors.confirmed;
        return (
          <div className="fixed inset-0 z-50 flex flex-col justify-end lg:relative lg:inset-auto lg:z-auto lg:flex-row" data-testid="appointment-sidebar-wrapper">
            <div className="absolute inset-0 bg-black/20 lg:hidden" onClick={closeSidebar} />
            <div
              className="relative w-full max-h-[70vh] lg:max-h-full lg:w-80 flex-shrink-0 border-t lg:border-t-0 lg:border-l border-[#E5E7E1] bg-white flex flex-col overflow-hidden rounded-t-2xl lg:rounded-none lg:h-full"
              style={{
                // Mobile: account for iOS home indicator. Desktop/iPad inherits body's safe-area padding already.
                marginBottom: 'env(safe-area-inset-bottom, 0px)',
              }}
              data-testid="appointment-sidebar"
            >
            {/* Drag handle (mobile only) */}
            <div className="lg:hidden flex justify-center pt-2 pb-1 flex-shrink-0"><div className="w-10 h-1 rounded-full bg-[#D1D5CB]" /></div>
            {/* Colored Header */}
            <div className="px-4 py-3 text-white flex-shrink-0 relative" style={{ backgroundColor: sc.bg }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>{fmt(currentDate, 'EEE d MMM')}</p>
                  <p className="text-sm opacity-90">{sidebarAppt.start_time} · {sidebarAppt.duration}min</p>
                </div>
                <div className="relative">
                  <button onClick={() => setStatusDropdownOpen(!statusDropdownOpen)} data-testid="status-dropdown-btn"
                    className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white/20 hover:bg-white/30 transition-colors flex items-center gap-1 capitalize">
                    {sidebarAppt.status === 'confirmed' ? t('appointments.statusBooked') : sidebarAppt.status === 'arrived' ? t('appointments.statusArrived') : sidebarAppt.status === 'started' ? t('appointments.statusStarted') : sidebarAppt.status === 'completed' ? t('appointments.statusCompleted') : sidebarAppt.status === 'no-show' ? t('appointments.statusNoShow') : sidebarAppt.status === 'cancelled' ? t('appointments.statusCancelled') : sidebarAppt.status} <ChevronDown size={12} />
                  </button>
                  {statusDropdownOpen && (
                    <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-xl border border-[#E5E7E1] z-50 py-1" data-testid="status-dropdown-menu">
                      {STATUS_OPTIONS.map(opt => {
                        const Icon = opt.icon;
                        const isActive = sidebarAppt.status === opt.value;
                        const isFuture = opt.value === 'no-show' && sidebarAppt.date && sidebarAppt.start_time && new Date(`${sidebarAppt.date}T${sidebarAppt.start_time}:00`) > new Date();
                        return (
                          <button key={opt.value} onClick={() => { if (isFuture) return; changeSidebarStatus(opt.value); }}
                            disabled={isFuture}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-[#F5F7F0] transition-colors ${isActive ? 'font-semibold' : ''} ${isFuture ? 'opacity-40 cursor-not-allowed hover:bg-transparent' : ''}`}
                            style={opt.color ? { color: opt.color } : { color: '#1A2421' }}>
                            <Icon size={14} /> {t(`appointments.${opt.label}`)} {isActive && <Check size={12} className="ml-auto" />}
                            {isFuture && <span className="ml-auto text-[10px] text-[#5C6656] font-normal">Not yet</span>}
                          </button>
                        );
                      })}
                      {sidebarAppt.status === 'no-show' && (
                        <button onClick={async () => {
                          setStatusDropdownOpen(false);
                          try {
                            await axios.put(`${API}/appointments/${sidebarAppt.id}/status`, { status: 'confirmed' }, { withCredentials: true });
                            setSidebarAppt(prev => prev ? { ...prev, status: 'confirmed' } : null);
                            toast.success('No-show undone');
                            fetchAll();
                          } catch { toast.error('Failed'); }
                        }} className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-[#FFF3F0] text-[#E2725B] border-t border-[#E5E7E1]">
                          <XCircle size={14} /> Undo no-show
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <button onClick={closeSidebar} className="absolute top-3 right-3 text-white/80 hover:text-white" data-testid="close-sidebar-btn"><XIcon size={18} /></button>
            </div>
            {/* Client Info (Fresha-style) */}
            <div className="p-4 border-b border-[#E5E7E1] flex-shrink-0">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-[#E9EDDF] flex items-center justify-center text-lg font-bold text-[#556B2F]">
                  {sidebarAppt.client_name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  {sidebarAppt.client_id === '__walk_in__' ? (
                    <button type="button" onClick={() => { setSidebarClientSwap(true); setSidebarClientSearch(''); setSidebarClientResults([]); }} className="font-semibold text-[#556B2F] hover:underline text-left" data-testid="sidebar-walkin-attach">
                      {sidebarAppt.client_name || 'Walk-in'} <span className="text-xs text-[#5C6656] font-normal">{t('appointments.tapToAttachClient')}</span>
                    </button>
                  ) : (
                    <p className="font-semibold text-[#1A2421]" data-testid="sidebar-client-name">{sidebarAppt.client_name}</p>
                  )}
                  {clientDetail?.email && <p className="text-xs text-[#5C6656] truncate">{clientDetail.email}</p>}
                  {clientDetail?.phone && <p className="text-xs text-[#5C6656]">{clientDetail.phone}</p>}
                  {clientDetail?.is_blocked && <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-bold text-[#E2725B] bg-[#FDE8E8] rounded-full" data-testid="blocked-badge">Blocked</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <button onClick={() => setSidebarClientEdit(!sidebarClientEdit)} className="px-3 py-1.5 rounded-lg border border-[#E5E7E1] text-sm text-[#1A2421] hover:bg-[#F5F7F0] flex items-center gap-1" data-testid="client-actions-sidebar-btn">
                    Actions <ChevronDown size={11} />
                  </button>
                  {sidebarClientEdit && (
                    <div className="absolute left-0 top-full mt-1 w-52 bg-white rounded-lg shadow-xl border border-[#E5E7E1] z-50 py-1" data-testid="client-actions-sidebar-menu">
                      {sidebarAppt.client_id !== '__walk_in__' ? (
                        <button type="button" onClick={async () => {
                          setSidebarClientEdit(false);
                          try {
                            const isGroup = sidebarAppt.booking_group_id && sidebarGroup.length > 1;
                            await axios.put(`${API}/appointments/${sidebarAppt.id}/update-client`, { client_id: '__walk_in__', walk_in_name: 'Walk-in', apply_to_group: isGroup, preserve_group: isGroup }, { withCredentials: true });
                            setSidebarAppt(prev => prev ? { ...prev, client_id: '__walk_in__', client_name: 'Walk-in' } : null);
                            if (isGroup) setSidebarGroup(prev => prev.map(g => ({ ...g, client_id: '__walk_in__', client_name: 'Walk-in' })));
                            setClientDetail(null);
                            // Open the attach-client search panel so the user can pick a new client or leave as walk-in
                            setSidebarClientSwap(true);
                            setSidebarClientSearch('');
                            setSidebarClientResults([]);
                            toast.success('Client removed');
                            fetchAll();
                          } catch { toast.error('Failed to remove client'); }
                        }} className="w-full text-left px-3 py-2 text-sm text-[#1A2421] hover:bg-[#F5F7F0] flex items-center gap-2" data-testid="remove-client-btn"><RefreshCw size={13} /> Remove client</button>
                      ) : (
                        <button type="button" onClick={() => { setSidebarClientEdit(false); setSidebarClientSwap(true); setSidebarClientSearch(''); setSidebarClientResults([]); }} className="w-full text-left px-3 py-2 text-sm text-[#1A2421] hover:bg-[#F5F7F0] flex items-center gap-2"><UserPlus size={13} /> Add client</button>
                      )}
                      <p className="px-3 pt-2 pb-1 text-[10px] font-bold text-[#5C6656] uppercase tracking-wider">Quick actions</p>
                      <button type="button" onClick={() => {
                        setSidebarClientEdit(false);
                        const fullName = clientDetail?.name || sidebarAppt.client_name || '';
                        const parts = fullName.trim().split(/\s+/);
                        const firstName = parts[0] || '';
                        const lastName = clientDetail?.last_name || parts.slice(1).join(' ') || '';
                        setSidebarClientEditForm({ name: firstName, last_name: lastName, email: clientDetail?.email || '', phone: clientDetail?.phone || '', birthday: clientDetail?.birthday || '', gender: clientDetail?.gender || '' });
                      }} className="w-full text-left px-3 py-2 text-sm text-[#1A2421] hover:bg-[#F5F7F0] flex items-center gap-2" data-testid="edit-client-details-btn"><Pencil size={13} /> Edit client details</button>
                      <button type="button" onClick={async () => {
                        setSidebarClientEdit(false);
                        if (clientDetail?.is_blocked) {
                          setBlockClientDialog({ clientId: sidebarAppt.client_id, clientName: sidebarAppt.client_name, action: 'unblock' });
                        } else {
                          setBlockClientDialog({ clientId: sidebarAppt.client_id, clientName: sidebarAppt.client_name, action: 'block' });
                          setBlockReason('');
                        }
                      }} className="w-full text-left px-3 py-2 text-sm text-[#1A2421] hover:bg-[#F5F7F0] flex items-center gap-2" data-testid="block-client-btn"><Ban size={13} /> {clientDetail?.is_blocked ? 'Unblock client' : 'Block client'}</button>
                      <button type="button" onClick={() => { setSidebarClientEdit(false); toast.error('Delete not available — use block or remove instead'); }} className="w-full text-left px-3 py-2 text-sm text-[#E2725B] hover:bg-[#FDE8E8] flex items-center gap-2"><Trash2 size={13} /> Delete client</button>
                    </div>
                  )}
                </div>
                <button onClick={async () => {
                  if (!sidebarAppt.client_id || sidebarAppt.client_id === '__walk_in__') {
                    // Walk-in: open attach-client search
                    setSidebarClientSwap(true); setSidebarClientSearch(''); setSidebarClientResults([]);
                    return;
                  }
                  try {
                    const { data } = await axios.get(`${API}/clients/${sidebarAppt.client_id}/profile`, { withCredentials: true });
                    setSidebarClientProfile(data);
                    setProfileTab('overview');
                    // Fetch activity timeline in background
                    axios.get(`${API}/clients/${sidebarAppt.client_id}/activity`, { withCredentials: true })
                      .then(res => setSidebarClientActivity(res.data?.activity || []))
                      .catch(() => setSidebarClientActivity([]));
                  } catch { toast.error('Failed to load profile'); }
                }} className="px-3 py-1.5 rounded-lg bg-[#1A2421] text-white text-sm hover:bg-[#0D1210] transition-colors" data-testid="view-profile-btn">
                  {sidebarAppt.client_id === '__walk_in__' ? t('appointments.attachClient') : t('appointments.viewProfile', 'View profile')}
                </button>
              </div>

              {/* Client swap (Add / change client) */}
              {sidebarClientSwap && (
                <div className="mt-3 pt-3 border-t border-[#E5E7E1] space-y-2">
                  <p className="text-xs font-bold text-[#1A2421]">{sidebarAppt.client_id === '__walk_in__' ? 'Add client' : 'Change client'}</p>
                  <p className="text-[10px] text-[#5C6656] -mt-1">Or leave empty for walk-in</p>
                  {sidebarNewClient === null ? (
                    <>
                      <div className="relative">
                        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C6656]" />
                        <Input value={sidebarClientSearch} onChange={e => searchSidebarClient(e.target.value)} placeholder="Search client..." className="pl-8 h-8 text-xs border-[#E5E7E1]" autoComplete="off" autoFocus />
                      </div>
                      {sidebarClientResults.length > 0 && (
                        <div className="max-h-36 overflow-y-auto border border-[#E5E7E1] rounded-lg">
                          {sidebarClientResults.map(c => (
                            <button key={c.id} onClick={() => { assignSidebarClient(c); setSidebarClientSwap(false); }} className="w-full text-left px-3 py-2 hover:bg-[#E9EDDF] text-xs border-b border-[#E5E7E1]/50 last:border-0">
                              <p className="font-medium text-[#1A2421]">{c.name}</p>
                              <p className="text-[#5C6656]">{[c.email, c.phone].filter(Boolean).join(' \u00B7 ')}</p>
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-2">
                        <button type="button" onClick={() => setSidebarClientSwap(false)} className="text-xs text-[#5C6656] hover:underline">Close</button>
                        <button type="button" onClick={() => {
                          // Smart seed: if search text looks like a phone number, put it in phone; else parse as name
                          const seed = (sidebarClientSearch || '').trim();
                          const digits = seed.replace(/[^\d]/g, '');
                          const looksLikePhone = digits.length >= 5 && (/^[\d\s+()\-]+$/.test(seed));
                          if (looksLikePhone) {
                            setSidebarNewClient({ name: '', last_name: '', email: '', phone: seed });
                          } else {
                            const parts = seed.split(/\s+/);
                            setSidebarNewClient({ name: parts[0] || '', last_name: parts.slice(1).join(' ') || '', email: '', phone: '' });
                          }
                        }} className="text-xs text-[#556B2F] font-semibold hover:underline flex items-center gap-1" data-testid="sidebar-create-new-client-btn"><UserPlus size={12} /> Create new client</button>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2 bg-[#F9F8F6] p-3 rounded-lg border border-[#E5E7E1]" data-testid="sidebar-new-client-form">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-[#5C6656]">New client</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div><label className="text-[10px] text-[#5C6656] font-bold">First name *</label><Input value={sidebarNewClient.name} onChange={e => setSidebarNewClient(p => ({ ...p, name: e.target.value }))} className="border-[#E5E7E1] h-8 text-xs" autoFocus data-testid="sidebar-new-client-name" /></div>
                        <div><label className="text-[10px] text-[#5C6656] font-bold">Last name</label><Input value={sidebarNewClient.last_name} onChange={e => setSidebarNewClient(p => ({ ...p, last_name: e.target.value }))} className="border-[#E5E7E1] h-8 text-xs" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><label className="text-[10px] text-[#5C6656] font-bold">Email</label><Input type="email" value={sidebarNewClient.email} onChange={e => setSidebarNewClient(p => ({ ...p, email: e.target.value }))} className="border-[#E5E7E1] h-8 text-xs" data-testid="sidebar-new-client-email" /></div>
                        <div><label className="text-[10px] text-[#5C6656] font-bold">Phone *</label><Input value={sidebarNewClient.phone} onChange={e => setSidebarNewClient(p => ({ ...p, phone: e.target.value }))} className="border-[#E5E7E1] h-8 text-xs" data-testid="sidebar-new-client-phone" /></div>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <Button type="button" size="sm" variant="outline" onClick={() => setSidebarNewClient(null)} className="text-xs border-[#E5E7E1] flex-1">Back</Button>
                        <Button type="button" size="sm" className="text-xs bg-[#556B2F] hover:bg-[#4A5D23] text-white flex-1" data-testid="sidebar-new-client-save"
                          onClick={async () => {
                            if (!sidebarNewClient.name.trim()) { toast.error('First name is required'); return; }
                            if (!sidebarNewClient.phone.trim()) { toast.error('Phone number is required'); return; }
                            try {
                              const fullName = [sidebarNewClient.name, sidebarNewClient.last_name].filter(Boolean).join(' ');
                              const { data } = await axios.post(`${API}/clients`, {
                                name: fullName, last_name: sidebarNewClient.last_name || '',
                                email: sidebarNewClient.email, phone: sidebarNewClient.phone,
                              }, { withCredentials: true });
                              await assignSidebarClient({ id: data.id, name: fullName, email: sidebarNewClient.email, phone: sidebarNewClient.phone });
                              setSidebarNewClient(null);
                              setSidebarClientSwap(false);
                              toast.success(`${fullName} created and attached`);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to create client');
                            }
                          }}>Create & attach</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Edit client details */}
              {sidebarClientEditForm && (
                <div className="mt-3 pt-3 border-t border-[#E5E7E1] space-y-2" data-testid="sidebar-edit-client-form">
                  <p className="text-xs font-bold text-[#1A2421]">Edit client details</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-[10px] text-[#5C6656] font-bold">First name *</label><Input value={sidebarClientEditForm.name} onChange={e => setSidebarClientEditForm(p => ({ ...p, name: e.target.value }))} className="border-[#E5E7E1] h-8 text-xs" /></div>
                    <div><label className="text-[10px] text-[#5C6656] font-bold">Last name</label><Input value={sidebarClientEditForm.last_name} onChange={e => setSidebarClientEditForm(p => ({ ...p, last_name: e.target.value }))} className="border-[#E5E7E1] h-8 text-xs" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-[10px] text-[#5C6656] font-bold">Email</label><Input type="email" value={sidebarClientEditForm.email} onChange={e => setSidebarClientEditForm(p => ({ ...p, email: e.target.value }))} className="border-[#E5E7E1] h-8 text-xs" /></div>
                    <div><label className="text-[10px] text-[#5C6656] font-bold">Phone</label><Input value={sidebarClientEditForm.phone} onChange={e => setSidebarClientEditForm(p => ({ ...p, phone: e.target.value }))} className="border-[#E5E7E1] h-8 text-xs" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-[10px] text-[#5C6656] font-bold">Birthday</label><Input type="date" value={sidebarClientEditForm.birthday} onChange={e => setSidebarClientEditForm(p => ({ ...p, birthday: e.target.value }))} className="border-[#E5E7E1] h-8 text-xs" /></div>
                    <div><label className="text-[10px] text-[#5C6656] font-bold">Gender</label>
                      <select value={sidebarClientEditForm.gender} onChange={e => setSidebarClientEditForm(p => ({ ...p, gender: e.target.value }))} className="w-full h-8 px-2 text-xs border border-[#E5E7E1] rounded-lg">
                        <option value="">Select</option><option value="female">Female</option><option value="male">Male</option><option value="non-binary">Non-binary</option><option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button type="button" size="sm" variant="outline" onClick={() => setSidebarClientEditForm(null)} className="text-xs border-[#E5E7E1]">Cancel</Button>
                    <Button type="button" size="sm" className="text-xs bg-[#1A2421] text-white" data-testid="save-sidebar-client-edit"
                      onClick={async () => {
                        const fullName = [sidebarClientEditForm.name, sidebarClientEditForm.last_name].filter(Boolean).join(' ');
                        if (!sidebarClientEditForm.name.trim()) { toast.error('First name required'); return; }
                        try {
                          await axios.put(`${API}/clients/${sidebarAppt.client_id}`, {
                            name: fullName, last_name: sidebarClientEditForm.last_name || '',
                            email: sidebarClientEditForm.email, phone: sidebarClientEditForm.phone,
                            birthday: sidebarClientEditForm.birthday, gender: sidebarClientEditForm.gender
                          }, { withCredentials: true });
                          setSidebarAppt(prev => prev ? { ...prev, client_name: fullName } : null);
                          setClientDetail(prev => prev ? { ...prev, name: fullName, last_name: sidebarClientEditForm.last_name, email: sidebarClientEditForm.email, phone: sidebarClientEditForm.phone, birthday: sidebarClientEditForm.birthday, gender: sidebarClientEditForm.gender } : null);
                          setSidebarClientEditForm(null);
                          toast.success('Client updated');
                          fetchAll();
                        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
                      }}>Save</Button>
                  </div>
                </div>
              )}

              {clientDetail?.no_show_count > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 mt-2 text-[10px] font-bold text-[#E2725B] bg-[#FDE8E8] rounded-full" data-testid="no-show-badge">
                  {clientDetail.no_show_count} no-show{clientDetail.no_show_count > 1 ? 's' : ''}
                </span>
              )}
              {sidebarAppt.notes && <p className="text-xs text-[#5C6656] italic mt-2">{sidebarAppt.notes}</p>}
            </div>
            {/* Services - Interactive (Fresha-style multi-service booking group)
                The Checkout footer at the end of this scroll area is `sticky bottom-0` with
                safe-area-aware padding so it always floats above Safari's bottom toolbar / home indicator. */}
            <div className="flex-1 overflow-y-auto p-4 pb-4">
              {/* === Edit Service Sub-Panel === */}
              {sidebarEditingApptId && (() => {
                const editAppt = sidebarGroup.find(g => g.id === sidebarEditingApptId);
                if (!editAppt) return null;
                const isCompleted = sidebarAppt.status === 'completed';
                return (
                  <div className="space-y-4" data-testid="edit-service-panel">
                    <button onClick={() => setSidebarEditingApptId(null)} className="flex items-center gap-1 text-sm text-[#5C6656] hover:text-[#1A2421]" data-testid="edit-service-back">
                      <ChevronLeft size={16} /> Back
                    </button>
                    <h4 className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Edit service</h4>
                    {/* Service name (click to change treatment) */}
                    {sidebarEditService ? (
                      <div className="border border-[#E5E7E1] rounded-lg overflow-hidden">
                        <div className="px-3 py-2 bg-[#F9F8F6] border-b border-[#E5E7E1] flex items-center justify-between">
                          <span className="text-xs font-bold uppercase tracking-wider text-[#5C6656]">{t('calendar.changeService')}</span>
                          <button onClick={() => setSidebarEditService(false)} className="text-[#5C6656] hover:text-[#1A2421]"><XIcon size={14} /></button>
                        </div>
                        <div className="px-2 py-1.5 border-b border-[#E5E7E1]">
                          <div className="relative">
                            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C6656]" />
                            <input className="w-full pl-8 pr-3 py-1.5 text-sm border border-[#E5E7E1] rounded-lg focus:outline-none focus:border-[#556B2F]"
                              placeholder="Search treatments..." value={sidebarServiceSearch} onChange={e => setSidebarServiceSearch(e.target.value)}
                              data-testid="edit-service-search" autoFocus />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {services.filter(s => s.is_active !== false && (!sidebarServiceSearch || s.name.toLowerCase().includes(sidebarServiceSearch.toLowerCase()))).map(svc => {
                            const currentStaff = staff.find(s => s.id === editAppt.staff_id);
                            const staffAssigned = currentStaff?.assigned_services || [];
                            const canDo = staffAssigned.length === 0 || staffAssigned.includes(svc.id);
                            return (
                            <button key={svc.id} onClick={async () => {
                              try {
                                // Send price + duration explicitly so backend stores the new service's catalog values
                                // (otherwise reschedule keeps the old price -> shows as a strikethrough discount)
                                await axios.put(`${API}/appointments/${editAppt.id}/reschedule`, {
                                  service_id: svc.id,
                                  price: svc.price || 0,
                                  duration: svc.duration || 30,
                                }, { withCredentials: true });
                                const updated = { ...editAppt, service_id: svc.id, service_name: svc.name, price: svc.price || 0, original_price: svc.price || 0, duration: svc.duration || 30 };
                                setSidebarGroup(prev => prev.map(g => g.id === editAppt.id ? { ...g, ...updated } : g));
                                if (editAppt.id === sidebarAppt.id) setSidebarAppt(prev => prev ? { ...prev, ...updated } : null);
                                setSidebarEditService(false);
                                toast.success('Treatment updated');
                                fetchAll();
                              } catch { toast.error('Failed'); }
                            }} className={`w-full text-left px-3 py-2.5 text-xs hover:bg-[#E9EDDF] border-b border-[#E5E7E1]/50 last:border-0 ${svc.id === editAppt.service_id ? 'bg-[#E9EDDF] font-semibold' : ''}`}
                              data-testid={`edit-svc-option-${svc.id}`}>
                              <div className="flex justify-between"><span className="text-[#1A2421]">{svc.name}</span><span className="text-[#556B2F]">{'\u00A3'}{(svc.price||0).toFixed(0)}</span></div>
                              <div className="flex items-center justify-between"><span className="text-[#5C6656]">{svc.duration}min</span>
                              {!canDo && <span className="text-[9px] text-[#856404] bg-[#FFF3CD] px-1 py-0.5 rounded">Team member doesn't provide this</span>}</div>
                            </button>);
                          })}
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => { if (!isCompleted) setSidebarEditService(true); }}
                        className={`w-full text-left px-4 py-3 border border-[#E5E7E1] rounded-lg flex items-center justify-between ${!isCompleted ? 'hover:bg-[#F5F7F0] cursor-pointer' : ''}`}
                        data-testid="edit-service-name-btn">
                        <span className="text-sm font-medium text-[#1A2421]">{editAppt.service_name} , {editAppt.duration}min</span>
                        {!isCompleted && <ChevronRight size={16} className="text-[#5C6656]" />}
                      </button>
                    )}
                    {/* Team member */}
                    <div>
                      <label className="text-xs font-medium text-[#5C6656] block mb-1">Team member</label>
                      <div className="flex items-center gap-2">
                        <select value={editAppt.staff_id} disabled={isCompleted} onChange={async (e) => {
                          const newStaff = staff.find(s => s.id === e.target.value);
                          if (!newStaff) return;
                          const wh = newStaff.working_hours?.[dayName];
                          const isScheduled = wh?.enabled;
                          if (!isScheduled && !window.confirm(`${newStaff.name} isn't scheduled to work on this day. Assign anyway?`)) return;
                          try {
                            await axios.put(`${API}/appointments/${editAppt.id}/reschedule`, { staff_id: newStaff.id }, { withCredentials: true });
                            setSidebarGroup(prev => prev.map(g => g.id === editAppt.id ? { ...g, staff_id: newStaff.id, staff_name: newStaff.name } : g));
                            if (editAppt.id === sidebarAppt.id) setSidebarAppt(prev => prev ? { ...prev, staff_id: newStaff.id, staff_name: newStaff.name } : null);
                            toast.success(isScheduled ? 'Staff changed' : `Assigned to ${newStaff.name} (not scheduled today)`);
                            fetchAll();
                          } catch { toast.error('Failed'); }
                        }} className="flex-1 h-9 px-3 text-sm border border-[#E5E7E1] rounded-lg bg-white text-[#1A2421]" data-testid="edit-service-staff">
                          {staff.filter(s => s.is_active).map(s => {
                            const wh = s.working_hours?.[dayName];
                            const sched = wh?.enabled;
                            return <option key={s.id} value={s.id}>{s.name}{!sched ? ' (not scheduled)' : ''}</option>;
                          })}
                        </select>
                        <button onClick={async () => {
                          const newVal = !editAppt.client_requested;
                          try {
                            await axios.put(`${API}/appointments/${editAppt.id}/reschedule`, { client_requested: newVal }, { withCredentials: true });
                            // Only update THIS service in the booking group — each service has its own heart
                            setSidebarGroup(prev => prev.map(g => g.id === editAppt.id ? { ...g, client_requested: newVal } : g));
                            if (sidebarAppt && sidebarAppt.id === editAppt.id) {
                              setSidebarAppt(prev => prev ? { ...prev, client_requested: newVal } : null);
                            }
                            toast.success(newVal ? 'Client requested this staff' : 'Removed client request');
                            fetchAll();
                          } catch { toast.error('Failed'); }
                        }} className="flex-shrink-0" data-testid="edit-service-heart" title={editAppt.client_requested ? 'Client requested (click to remove)' : 'Mark as client requested'}>
                          <span style={{ fontSize: '20px' }}>{editAppt.client_requested ? '❤️' : '🤍'}</span>
                        </button>
                      </div>
                    </div>
                    {/* Price */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-[#5C6656] block mb-1">Service price</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#5C6656]">GBP</span>
                          <input key={`price-${editAppt.id}-${editAppt.price}`} type="number" min="0" step="0.01" defaultValue={editAppt.price || 0} disabled={isCompleted}
                            onBlur={async (e) => {
                              const newPrice = parseFloat(e.target.value);
                              if (isNaN(newPrice) || newPrice === editAppt.price) return;
                              try {
                                await axios.put(`${API}/appointments/${editAppt.id}/reschedule`, { price: newPrice }, { withCredentials: true });
                                setSidebarGroup(prev => prev.map(g => g.id === editAppt.id ? { ...g, price: newPrice } : g));
                                if (editAppt.id === sidebarAppt.id) setSidebarAppt(prev => prev ? { ...prev, price: newPrice } : null);
                                toast.success('Price updated');
                                fetchAll();
                              } catch { toast.error('Failed'); }
                            }}
                            className="w-full h-9 pl-12 pr-3 text-sm border border-[#E5E7E1] rounded-lg bg-white text-[#1A2421]" data-testid="edit-service-price" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-[#5C6656] block mb-1">Discount</label>
                        <select className="w-full h-9 px-3 text-sm border border-[#E5E7E1] rounded-lg bg-white text-[#5C6656]" disabled={isCompleted} data-testid="edit-service-discount">
                          <option>No discount</option>
                        </select>
                      </div>
                    </div>
                    {/* Start time & Duration */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-[#5C6656] block mb-1">Start time</label>
                        <select defaultValue={editAppt.start_time} disabled={isCompleted} onChange={async (e) => {
                          const newTime = e.target.value;
                          if (!newTime || newTime === editAppt.start_time) return;
                          try {
                            const { data } = await axios.put(`${API}/appointments/${editAppt.id}/reschedule`, { start_time: newTime, cascade_group: true }, { withCredentials: true });
                            const newEnd = (() => { const [h,m] = newTime.split(':').map(Number); const endM = h*60 + m + (editAppt.duration||30); return `${String(Math.floor(endM/60)).padStart(2,'0')}:${String(endM%60).padStart(2,'0')}`; })();
                            setSidebarGroup(prev => prev.map(g => g.id === editAppt.id ? { ...g, start_time: newTime, end_time: newEnd } : g));
                            if (editAppt.id === sidebarAppt.id) setSidebarAppt(prev => prev ? { ...prev, start_time: newTime, end_time: newEnd } : null);
                            toast.success('Start time updated');
                            if (data?.cascade_warnings?.length) showCascadeWarnings(data.cascade_warnings);
                            fetchAll();
                          } catch { toast.error('Failed'); }
                        }} className="w-full h-9 px-3 text-sm border border-[#E5E7E1] rounded-lg bg-white text-[#1A2421]" data-testid="edit-service-time">
                          {Array.from({ length: (END_HOUR - START_HOUR) * 12 }, (_, i) => {
                            const mins = START_HOUR * 60 + i * 5;
                            const t = `${String(Math.floor(mins/60)).padStart(2,'0')}:${String(mins%60).padStart(2,'0')}`;
                            return <option key={t} value={t}>{t}</option>;
                          })}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-[#5C6656] block mb-1">Duration</label>
                        <select defaultValue={editAppt.duration} disabled={isCompleted} onChange={async (e) => {
                          const newDur = parseInt(e.target.value);
                          if (isNaN(newDur) || newDur === editAppt.duration) return;
                          try {
                            await axios.put(`${API}/appointments/${editAppt.id}/reschedule`, { duration: newDur }, { withCredentials: true });
                            const [h,m] = (editAppt.start_time||'09:00').split(':').map(Number);
                            const endM = h*60 + m + newDur;
                            const newEnd = `${String(Math.floor(endM/60)).padStart(2,'0')}:${String(endM%60).padStart(2,'0')}`;
                            setSidebarGroup(prev => prev.map(g => g.id === editAppt.id ? { ...g, duration: newDur, end_time: newEnd } : g));
                            if (editAppt.id === sidebarAppt.id) setSidebarAppt(prev => prev ? { ...prev, duration: newDur, end_time: newEnd } : null);
                            toast.success('Duration updated');
                            fetchAll();
                          } catch { toast.error('Failed'); }
                        }} className="w-full h-9 px-3 text-sm border border-[#E5E7E1] rounded-lg bg-white text-[#1A2421]" data-testid="edit-service-duration">
                          {Array.from({ length: 36 }, (_, i) => (i + 1) * 5).map(d => <option key={d} value={d}>{d}min</option>)}
                        </select>
                      </div>
                    </div>
                    {/* Bottom: Delete + Back */}
                    {!isCompleted && sidebarGroup.length > 1 && (
                      <button onClick={async () => {
                        if (!window.confirm(`Remove ${editAppt.service_name}?`)) return;
                        try {
                          await axios.delete(`${API}/appointments/${editAppt.id}?single=true`, { withCredentials: true });
                          setSidebarGroup(prev => prev.filter(g => g.id !== editAppt.id));
                          setSidebarEditingApptId(null);
                          toast.success(`${editAppt.service_name} removed`);
                          fetchAll();
                        } catch { toast.error('Failed to remove'); }
                      }} className="flex items-center gap-1.5 text-sm text-[#E2725B] hover:text-[#C0392B]" data-testid="edit-service-delete">
                        <Trash2 size={14} /> Remove service
                      </button>
                    )}
                  </div>
                );
              })()}
              {/* === Add Service Sub-Panel === */}
              {!sidebarEditingApptId && sidebarAddService && (
                <div data-testid="sidebar-add-service-panel">
                  <button onClick={() => setSidebarAddService(false)} className="flex items-center gap-1 text-sm text-[#5C6656] hover:text-[#1A2421] mb-2" data-testid="add-service-back">
                    <ChevronLeft size={16} /> Back
                  </button>
                  <h4 className="text-lg font-semibold text-[#1A2421] mb-3" style={{ fontFamily: 'Outfit' }}>Add a service</h4>
                  <div className="border border-[#E5E7E1] rounded-lg overflow-hidden">
                    <div className="px-3 py-2 border-b border-[#E5E7E1]">
                      <div className="relative">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C6656]" />
                        <input className="w-full pl-8 pr-3 py-1.5 text-sm border border-[#E5E7E1] rounded-lg focus:outline-none focus:border-[#556B2F]" placeholder="Search by service name" value={sidebarServiceSearch} onChange={e => setSidebarServiceSearch(e.target.value)} data-testid="sidebar-service-search" autoFocus />
                      </div>
                    </div>
                    {popularServices.length > 0 && !sidebarServiceSearch && (
                      <div className="px-3 py-2 border-b border-[#E5E7E1] bg-[#FAFBF7]">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#556B2F] mb-1">Recently booked</p>
                        <div className="flex flex-wrap gap-1">
                          {popularServices.map(ps => (
                            <button key={ps.id} type="button" disabled={!!addingServiceId} onClick={() => addServiceToBooking(ps.id)}
                              className={`px-2 py-0.5 text-xs rounded-full border border-[#C5CDB8] bg-[#F5F7F0] text-[#3A4A2E] hover:bg-[#E9EDDF] hover:border-[#556B2F] transition-colors ${addingServiceId ? 'opacity-50 cursor-not-allowed' : ''}`}
                              data-testid={`sidebar-popular-svc-${ps.id}`}>
                              {addingServiceId === ps.id ? 'Adding...' : ps.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="max-h-64 overflow-y-auto">
                      {services.filter(svc => svc.is_active !== false && (!sidebarServiceSearch || svc.name.toLowerCase().includes(sidebarServiceSearch.toLowerCase()))).map(svc => {
                        const currentStaff = staff.find(s => s.id === sidebarAppt.staff_id);
                        const staffAssigned = currentStaff?.assigned_services || [];
                        const canPerform = staffAssigned.length > 0 && staffAssigned.includes(svc.id);
                        return (
                        <button key={svc.id} disabled={!!addingServiceId} onClick={() => addServiceToBooking(svc.id)}
                          className={`w-full text-left px-3 py-2.5 hover:bg-[#E9EDDF] border-b border-[#E5E7E1]/50 last:border-0 transition-colors ${addingServiceId ? 'opacity-50 cursor-not-allowed' : ''}`}
                          data-testid={`sidebar-add-svc-${svc.id}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-[#1A2421]">{svc.name}</p>
                              <p className="text-xs text-[#5C6656]">{svc.duration}min</p>
                              {!canPerform && <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-medium bg-[#FFF3CD] text-[#856404] rounded">Team member doesn't provide this service</span>}
                            </div>
                            <span className="text-sm font-medium text-[#556B2F]">{'\u00A3'}{(svc.price || 0).toFixed(0)}</span>
                          </div>
                        </button>);
                      })}
                    </div>
                  </div>
                </div>
              )}
              {/* === Services List (default view) === */}
              {!sidebarEditingApptId && !sidebarAddService && (
                <>
                  <h4 className="text-sm font-semibold text-[#1A2421] mb-3">{t('appointments.services')}</h4>
                  {(() => {
                    // A booking group can span multiple dates (e.g. mani today + brows next Fri).
                    // Show only THIS appointment's date in the primary list, and surface any
                    // other-date siblings in a separate "On other dates" list so staff don't
                    // accidentally check them out today.
                    const todayList = sidebarGroup.filter(g => g.date === sidebarAppt.date);
                    const otherList = sidebarGroup.filter(g => g.date !== sidebarAppt.date);
                    return (
                      <>
                        {todayList.map((grpAppt, grpIdx) => {
                    const isCompleted = sidebarAppt.status === 'completed';
                    return (
                      <div key={grpAppt.id}
                        className={`border-l-2 pl-3 mb-3 rounded-r-lg group/svc ${!isCompleted ? 'cursor-pointer hover:bg-[#F5F7F0] active:bg-[#E9EDDF]' : ''} transition-colors -ml-0.5 py-2 pr-2`}
                        style={{ borderColor: sc.bg }}
                        onClick={() => { if (!isCompleted) { setSidebarEditingApptId(grpAppt.id); setSidebarEditService(false); setSidebarServiceSearch(''); } }}
                        data-testid={`sidebar-group-svc-${grpIdx}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#1A2421] line-clamp-2" data-testid={`sidebar-svc-name-${grpIdx}`}>{grpAppt.service_name}</p>
                            {grpAppt.client_id && sidebarAppt.client_id && grpAppt.client_id !== sidebarAppt.client_id && (
                              <p className="text-[10px] text-[#8B7A2F] bg-[#FBF7EE] rounded-full px-2 py-0.5 inline-block mt-0.5 font-medium" data-testid={`sidebar-guest-badge-${grpIdx}`}>
                                Guest: {grpAppt.client_name}
                              </p>
                            )}
                            <p className="text-xs text-[#5C6656]">
                              {grpAppt.start_time} · {grpAppt.duration}min · {grpAppt.staff_name}
                              {grpAppt.client_requested && <span className="ml-1" style={{ fontSize: '12px' }}>❤️</span>}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="text-sm font-semibold text-[#1A2421]">{'\u00A3'}{(grpAppt.price || 0).toFixed(0)}</span>
                            {grpAppt.original_price != null && grpAppt.original_price !== grpAppt.price && (
                              <span className="text-xs text-[#5C6656] line-through">{'\u00A3'}{grpAppt.original_price.toFixed(0)}</span>
                            )}
                            {!isCompleted && (
                              <Pencil size={13} className="text-[#B0B5A8]" />
                            )}
                            {!isCompleted && sidebarGroup.length > 1 && (
                                  <button onClick={async (e) => {
                                    e.stopPropagation();
                                    if (!window.confirm(`Remove ${grpAppt.service_name}?`)) return;
                                    try {
                                      await axios.delete(`${API}/appointments/${grpAppt.id}?single=true`, { withCredentials: true });
                                      setSidebarGroup(prev => prev.filter(g => g.id !== grpAppt.id));
                                      toast.success(`${grpAppt.service_name} removed`);
                                      fetchAll();
                                    } catch { toast.error('Failed to remove'); }
                                  }} className="text-[#5C6656] hover:text-[#E2725B]"
                                    data-testid={`remove-svc-btn-${grpIdx}`} title="Remove service">
                                    <Trash2 size={14} />
                                  </button>
                                )}
                          </div>
                        </div>
                      </div>
                    );
                        })}
                        {otherList.length > 0 && (
                          <div className="mt-1 mb-3 rounded-lg border border-dashed border-[#C9CDBF] bg-[#F5F7F0]/60 p-2.5" data-testid="sidebar-other-dates">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-[#5C6656] mb-1.5">Also booked · not being checked out today</p>
                            {otherList.map((oa, oi) => {
                              const d = new Date(oa.date + 'T12:00:00');
                              const label = d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
                              return (
                                <div key={oa.id} className="flex items-center justify-between text-xs text-[#5C6656] py-1" data-testid={`sidebar-other-svc-${oi}`}>
                                  <div className="min-w-0 pr-2">
                                    <span className="font-medium text-[#1A2421]">{oa.service_name}</span>
                                    <span className="ml-1.5 text-[#556B2F]">· {label} {oa.start_time}</span>
                                    <span className="ml-1"> · {oa.staff_name}</span>
                                  </div>
                                  <span className="font-semibold text-[#1A2421] flex-shrink-0">{'\u00A3'}{(oa.price || 0).toFixed(0)}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {sidebarAppt.status !== 'completed' && sidebarAppt.status !== 'cancelled' && (
                    <button className="flex items-center gap-1.5 text-sm text-[#556B2F] hover:text-[#4A5D23] font-medium mt-1" data-testid="add-service-sidebar"
                      onClick={() => { setSidebarAddService(true); setSidebarServiceSearch(''); }}>
                      <PlusCircle size={14} /> {t('appointments.addService')}
                    </button>
                  )}
                  {sidebarGroup.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#E5E7E1] flex items-center justify-between">
                      <span className="text-xs text-[#5C6656]">{sidebarGroup.reduce((t, g) => t + (g.duration || 0), 0)}min</span>
                    </div>
                  )}
                </>
              )}
            </div>
            {/* Notes */}
            {sidebarAppt.notes && (
              <div className="px-4 pb-2">
                <div className="p-2.5 bg-[#FFF8E1] border border-[#F0E0A0] rounded-lg">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#856404] mb-1">Notes</p>
                  <p className="text-xs text-[#5C4B00]">{sidebarAppt.notes}</p>
                </div>
              </div>
            )}
            {/* Bottom: Total + Actions */}
            <div className="p-3 border-t border-[#E5E7E1] flex-shrink-0 space-y-2 mb-16 lg:mb-0">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#5C6656]">{t('appointments.total')}</span>
                <div className="text-right">
                  {(() => {
                    // Total reflects only THIS date's services (see openCheckout comment).
                    const todayGrp = sidebarGroup.filter(g => g.date === sidebarAppt.date);
                    const total = todayGrp.length > 1
                      ? todayGrp.reduce((t, g) => t + (g.price || 0), 0)
                      : (sidebarAppt.final_total || sidebarAppt.price || 0);
                    const dur = todayGrp.reduce((t, g) => t + (g.duration || 0), 0);
                    return (
                      <>
                        <span className="text-lg font-bold text-[#1A2421]">{'\u00A3'}{total.toFixed(2)}</span>
                        {todayGrp.length > 1 && (
                          <p className="text-xs text-[#5C6656]">{todayGrp.length} services · {dur}min total</p>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Edit/Add service mode: Apply button */}
              {(sidebarEditingApptId || sidebarAddService) && sidebarAppt.status !== 'completed' && sidebarAppt.status !== 'cancelled' && (
                <Button onClick={() => { setSidebarEditingApptId(null); setSidebarAddService(false); setSidebarEditService(false); }} className="w-full bg-[#1A2421] hover:bg-[#0D1210] text-white py-5" data-testid="sidebar-apply-btn">
                  Apply
                </Button>
              )}

              {/* Default view: Checkout + actions */}
              {!sidebarEditingApptId && !sidebarAddService && (
                <>
                  {/* Completed: View sale + Email receipt + Rebook
                      Sticky to bottom with safe-area-aware padding so it doesn't sit under iPad's Safari toolbar. */}
                  {sidebarAppt.status === 'completed' && (
                <div
                  className="space-y-2 sticky bottom-0 -mx-4 px-4 pt-3 mt-3 bg-white border-t border-[#E5E7E1] z-10"
                  style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1.5rem)' }}
                >
                  {sidebarInvoice && (
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setSidebarPdfUrl(`${API}/invoices/${sidebarInvoice.id}/pdf`)} className="flex-1 border-[#556B2F] text-[#556B2F] text-xs h-9" data-testid="sidebar-view-sale">
                        <Receipt size={13} className="mr-1" /> View sale
                      </Button>
                      <Button variant="outline" onClick={() => setSidebarShowReceipt(!sidebarShowReceipt)} className="flex-1 border-[#5C6656] text-[#5C6656] text-xs h-9" data-testid="sidebar-email-receipt">
                        <Send size={13} className="mr-1" /> Email receipt
                      </Button>
                    </div>
                  )}
                  {sidebarShowReceipt && (
                    <div className="flex gap-2">
                      <Input value={sidebarReceiptEmail} onChange={e => setSidebarReceiptEmail(e.target.value)} placeholder="Client email..." className="h-8 text-xs border-[#E5E7E1] flex-1" data-testid="sidebar-receipt-email" />
                      <Button size="sm" onClick={async () => {
                        try {
                          await axios.post(`${API}/appointments/${sidebarAppt.id}/send-receipt`, { email: sidebarReceiptEmail }, { withCredentials: true });
                          toast.success(`Receipt sent to ${sidebarReceiptEmail}`);
                          setSidebarShowReceipt(false);
                        } catch { toast.error('Failed to send'); }
                      }} className="h-8 bg-[#556B2F] hover:bg-[#4A5D23] text-white text-xs" data-testid="sidebar-send-receipt">{t('appointments.send')}</Button>
                    </div>
                  )}
                  <Button onClick={() => {
                    const rb = { client_id: sidebarAppt.client_id, client_name: sidebarAppt.client_name, service_id: sidebarAppt.service_id, service_name: sidebarAppt.service_name, staff_id: sidebarAppt.staff_id, staff_name: sidebarAppt.staff_name, price: sidebarAppt.price, duration: sidebarAppt.duration, location_id: sidebarAppt.location_id || '' };
                    closeSidebar();
                    setRebookMode(rb);
                  }} className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white py-4" data-testid="rebook-btn">
                    <RefreshCw size={14} className="mr-2" /> {t('appointments.bookNextVisit')}
                  </Button>
                  <Button variant="ghost" onClick={async () => {
                    if (!window.confirm('Void this checkout? This will return the appointment to booked status and void the invoice.')) return;
                    try {
                      await axios.put(`${API}/appointments/${sidebarAppt.id}/void`, {}, { withCredentials: true });
                      toast.success('Checkout voided — appointment returned to booked');
                      closeSidebar();
                      fetchAll();
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to void'); }
                  }} className="w-full text-[#E2725B] hover:bg-red-50 text-xs" data-testid="void-checkout-btn">
                    <Ban size={14} className="mr-1" /> Void checkout
                  </Button>
                </div>
              )}

              {/* Reschedule inline form */}
              {rescheduleOpen && sidebarAppt.status !== 'completed' && (() => {
                const groupSize = sidebarAppt.booking_group_id
                  ? appointments.filter(a => a.booking_group_id === sidebarAppt.booking_group_id && a.client_id === sidebarAppt.client_id && a.status !== 'cancelled').length
                  : 1;
                const isGroup = groupSize > 1;
                const dateChanged = rescheduleDate && rescheduleDate !== sidebarAppt.date;
                const timeChanged = rescheduleTime && rescheduleTime !== sidebarAppt.start_time;
                const willCascade = isGroup && (dateChanged || timeChanged);
                return (
                <div className="mb-3 p-3 bg-[#F9F8F6] border border-[#E5E7E1] rounded-xl space-y-2" data-testid="reschedule-form">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-[#1A2421]">Reschedule appointment</p>
                    <button onClick={() => setRescheduleOpen(false)} className="text-xs text-[#5C6656] hover:underline">Cancel</button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-[10px] font-bold text-[#5C6656]">New date</label><Input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)} className="border-[#E5E7E1] h-9 text-xs" /></div>
                    <div><label className="text-[10px] font-bold text-[#5C6656]">New time</label><Input type="time" value={rescheduleTime} onChange={e => setRescheduleTime(e.target.value)} className="border-[#E5E7E1] h-9 text-xs" /></div>
                  </div>
                  {willCascade && (
                    <p className="text-[10px] text-[#556B2F] bg-[#E9EDDF] rounded px-2 py-1" data-testid="group-move-hint">
                      All {groupSize} services in this booking will move together, keeping the same spacing.
                    </p>
                  )}
                  <Button size="sm" className="w-full bg-[#1A2421] text-white text-xs" data-testid="confirm-reschedule"
                    onClick={async () => {
                      if (!rescheduleDate || !rescheduleTime) { toast.error('Select date and time'); return; }
                      try {
                        const { data: rd } = await axios.put(`${API}/appointments/${sidebarAppt.id}/reschedule`, { date: rescheduleDate, start_time: rescheduleTime, staff_id: sidebarAppt.staff_id, cascade_group: true }, { withCredentials: true });
                        toast.success(willCascade ? `Booking (${groupSize} services) moved to ${rescheduleDate} at ${rescheduleTime}` : `Rescheduled to ${rescheduleDate} at ${rescheduleTime}`);
                        if (rd?.cascade_warnings?.length) showCascadeWarnings(rd.cascade_warnings);
                        setRescheduleOpen(false);
                        closeSidebar();
                        fetchAll();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reschedule'); }
                    }}>Confirm reschedule</Button>
                </div>
                );
              })()}

              {/* Not completed: Quick Actions + Checkout
                  Sticky to the bottom of the scrolling container with safe-area-aware spacing
                  so the Checkout button never sits under Safari's bottom toolbar / home indicator
                  on iPad. Always visible and tappable, even mid-scroll. */}
              {sidebarAppt.status !== 'completed' && sidebarAppt.status !== 'cancelled' && (
                <div
                  className="flex items-center gap-2 sticky bottom-0 -mx-4 px-4 pt-3 mt-3 bg-white border-t border-[#E5E7E1] z-10"
                  style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1.5rem)' }}
                >
                  <div className="relative">
                    <button onClick={() => setQuickActionsOpen(!quickActionsOpen)} className="w-10 h-10 flex items-center justify-center border border-[#E5E7E1] rounded-lg hover:bg-[#F5F7F0] transition-colors" data-testid="quick-actions-btn">
                      <MoreVertical size={18} className="text-[#5C6656]" />
                    </button>
                    {quickActionsOpen && (
                      <div className="absolute bottom-12 left-0 w-56 bg-white border border-[#E5E7E1] rounded-xl shadow-xl z-50" data-testid="quick-actions-menu">
                        <div className="px-4 py-2.5 border-b border-[#E5E7E1]">
                          <p className="text-xs font-bold text-[#1A2421]">Quick actions</p>
                        </div>
                        <div className="py-1">
                          <button onClick={() => {
                            setNoteText(sidebarAppt.notes || '');
                            setNoteDialogOpen(true);
                            setQuickActionsOpen(false);
                          }} className="w-full text-left px-4 py-2.5 text-sm text-[#1A2421] hover:bg-[#F5F7F0] flex items-center gap-2.5">
                            <MessageSquare size={14} className="text-[#5C6656]" /> Add a note
                          </button>
                          <button onClick={async () => {
                            setQuickActionsOpen(false);
                            setActivityLog([]);
                            try {
                              const { data } = await axios.get(`${API}/appointments/${sidebarAppt.id}/activity`, { withCredentials: true });
                              setActivityLog(data.activity || []);
                            } catch { setActivityLog([]); }
                          }} className="w-full text-left px-4 py-2.5 text-sm text-[#1A2421] hover:bg-[#F5F7F0] flex items-center gap-2.5" data-testid="view-activity-btn">
                            <Clock size={14} className="text-[#5C6656]" /> View appointment activity
                          </button>
                          <button onClick={() => {
                            setQuickActionsOpen(false);
                            const rebookData = { appt: sidebarAppt, services: sidebarGroup };
                            closeSidebar();
                            setRebookMode(rebookData);
                          }} className="w-full text-left px-4 py-2.5 text-sm text-[#1A2421] hover:bg-[#F5F7F0] flex items-center gap-2.5">
                            <RefreshCw size={14} className="text-[#5C6656]" /> Rebook
                          </button>
                          <button onClick={() => {
                            setQuickActionsOpen(false);
                            closeSidebar();
                            setRebookMode({
                              _isReschedule: true,
                              _appointmentId: sidebarAppt.id,
                              client_id: sidebarAppt.client_id,
                              client_name: sidebarAppt.client_name,
                              service_id: sidebarAppt.service_id,
                              service_name: sidebarAppt.service_name,
                              staff_id: sidebarAppt.staff_id,
                              staff_name: sidebarAppt.staff_name,
                              price: sidebarAppt.price,
                              duration: sidebarAppt.duration,
                              location_id: sidebarAppt.location_id || '',
                            });
                          }} className="w-full text-left px-4 py-2.5 text-sm text-[#1A2421] hover:bg-[#F5F7F0] flex items-center gap-2.5">
                            <CalendarDays size={14} className="text-[#5C6656]" /> Reschedule
                          </button>
                          <div className="border-t border-[#E5E7E1] my-1" />
                          <button onClick={() => {
                            setQuickActionsOpen(false);
                            setNoShowDialog(sidebarAppt);
                            setNoShowNotify(sidebarAppt.source === 'online');
                          }} className="w-full text-left px-4 py-2.5 text-sm text-[#D4A373] hover:bg-[#FFF8F0] flex items-center gap-2.5">
                            <Ban size={14} /> No-show
                          </button>
                          <button onClick={() => {
                            setQuickActionsOpen(false);
                            setCancelDialog(sidebarAppt);
                            setCancelReason('No reason provided');
                            setCancelNotify(sidebarAppt.source === 'online');
                          }} className="w-full text-left px-4 py-2.5 text-sm text-[#E2725B] hover:bg-[#FDE8E8] flex items-center gap-2.5">
                            <XIcon size={14} /> Cancel
                          </button>
                          {sidebarAppt.status === 'no-show' && (
                            <button onClick={async () => {
                              setQuickActionsOpen(false);
                              try {
                                await axios.put(`${API}/appointments/${sidebarAppt.id}/status`, { status: 'confirmed' }, { withCredentials: true });
                                setSidebarAppt(prev => prev ? { ...prev, status: 'confirmed' } : null);
                                toast.success('No-show undone');
                                fetchAll();
                              } catch { toast.error('Failed'); }
                            }} className="w-full text-left px-4 py-2.5 text-sm text-[#E2725B] hover:bg-[#FDE8E8] flex items-center gap-2.5">
                              <RefreshCw size={14} /> Undo no-show
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <Button onClick={() => { openCheckout(sidebarAppt); closeSidebar(); }} className="flex-1 bg-[#1A2421] hover:bg-[#0D1210] text-white py-5" data-testid="sidebar-checkout-btn">
                    <Receipt size={14} className="mr-2" /> {t('appointments.checkout')}
                  </Button>
                </div>
              )}
                </>
              )}
            </div>
          </div>
          </div>
        );
      })()}

      {/* Appointment Activity Panel — full screen on mobile, overlay on desktop */}
      {activityLog !== null && sidebarAppt && (
        <>
          <div className="fixed inset-0 bg-black/20 z-[69]" onClick={() => setActivityLog(null)} />
          <div className="fixed inset-0 lg:inset-auto lg:top-0 lg:right-0 lg:h-full lg:w-[380px] bg-white z-[70] shadow-2xl flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }} data-testid="activity-panel">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E7E1]">
              <button onClick={() => setActivityLog(null)} className="w-9 h-9 rounded-full bg-[#F0F2EC] flex items-center justify-center active:bg-[#E5E7E1]" data-testid="activity-back-btn"><ChevronLeft size={18} className="text-[#5C6656]" /></button>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Appointment activity</h3>
                <p className="text-[11px] text-[#5C6656] truncate">{sidebarAppt.client_name} · {sidebarAppt.date}</p>
              </div>
              <button onClick={() => setActivityLog(null)} className="w-9 h-9 rounded-full bg-[#F0F2EC] flex items-center justify-center active:bg-[#E5E7E1] lg:hidden" data-testid="activity-close-btn"><XIcon size={16} className="text-[#5C6656]" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 pb-24 lg:pb-4">
            {activityLog.length === 0 ? (
              <div className="text-center py-12 text-sm text-[#5C6656]">
                <Clock size={28} className="mx-auto mb-3 text-[#C5CDB8]" />
                <p>No activity recorded yet</p>
                <p className="text-xs mt-1">Activity will appear here as changes are made</p>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-[7px] top-2 bottom-2 w-[2px] bg-[#E5E7E1]" />
                <div className="space-y-5">
                  {activityLog.map((log, i) => {
                    const typeIcons = { created: '🟢', status_changed: '🔄', rescheduled: '📅', checkout: '💳', notification_sent: '📧' };
                    const typeColors = { created: '#556B2F', status_changed: '#2E86C1', rescheduled: '#D4A373', checkout: '#3E6641', notification_sent: '#8B9A6B' };
                    const color = typeColors[log.event_type] || '#5C6656';
                    const ts = log.timestamp ? new Date(log.timestamp) : null;
                    const timeStr = ts ? ts.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                    return (
                      <div key={log.id || i} className="relative pl-7" data-testid={`activity-item-${i}`}>
                        <div className="absolute left-0 top-0.5 w-4 h-4 rounded-full border-2 bg-white flex items-center justify-center" style={{ borderColor: color }}>
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                        </div>
                        <p className="text-sm font-semibold text-[#1A2421] leading-tight">
                          {log.event_type === 'created' && 'Appointment created'}
                          {log.event_type === 'status_changed' && 'Status changed'}
                          {log.event_type === 'rescheduled' && 'Appointment updated'}
                          {log.event_type === 'checkout' && 'Checked out'}
                          {log.event_type === 'notification_sent' && 'Notification sent'}
                          {!['created','status_changed','rescheduled','checkout','notification_sent'].includes(log.event_type) && (log.event_type || 'Activity')}
                        </p>
                        <p className="text-[11px] text-[#8B9A6B] mt-0.5">{timeStr}</p>
                        <p className="text-xs text-[#5C6656] mt-1 leading-relaxed">{log.description}</p>
                        {log.actor && log.actor !== 'System' && (
                          <p className="text-[10px] text-[#B0B5A8] mt-0.5">by {log.actor}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="px-5 py-3 border-t border-[#E5E7E1] text-center">
            <p className="text-[10px] text-[#C5CDB8]">Activity for this appointment in the last 12 months</p>
          </div>
          </div>
        </>
      )}

      </div>

      {/* Staff Header Menu (Fresha-style) */}
      {staffHeaderMenu && (() => {
        const sm = staff.find(s => s.id === staffHeaderMenu.staffId);
        return (
          <div className="fixed bg-white border border-[#E5E7E1] rounded-xl shadow-xl z-50 py-2 w-48" style={{ left: staffHeaderMenu.x, top: staffHeaderMenu.y }} data-testid="staff-header-menu">
            <button onClick={() => { setStaffFilterMode('custom'); setSelectedStaffIds(new Set([staffHeaderMenu.staffId])); setCalendarView('day'); setStaffHeaderMenu(null); }} className={`w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] flex items-center gap-2 ${calendarView === 'day' ? 'text-[#556B2F] font-semibold' : 'text-[#1A2421]'}`}><CalendarDays size={14} /> Day view</button>
            <button onClick={() => { setStaffFilterMode('custom'); setSelectedStaffIds(new Set([staffHeaderMenu.staffId])); setCalendarView('3day'); setStaffHeaderMenu(null); }} className={`w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] flex items-center gap-2 ${calendarView === '3day' ? 'text-[#556B2F] font-semibold' : 'text-[#1A2421]'}`}><LayoutGrid size={14} /> 3-day view</button>
            <button onClick={() => { setStaffFilterMode('custom'); setSelectedStaffIds(new Set([staffHeaderMenu.staffId])); setCalendarView('week'); setStaffHeaderMenu(null); }} className={`w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] flex items-center gap-2 ${calendarView === 'week' ? 'text-[#556B2F] font-semibold' : 'text-[#1A2421]'}`}><LayoutGrid size={14} /> Week view</button>
            <div className="border-t border-[#E5E7E1] my-1" />
            <p className="px-4 py-1 text-[10px] font-bold text-[#5C6656] uppercase tracking-wider">{t('calendar.actions')}</p>
            <button onClick={() => { openBookDialog(staffHeaderMenu.staffId, '09:00'); setStaffHeaderMenu(null); }} className="w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] text-[#1A2421]">{t('calendar.addAppointment')}</button>
            <button onClick={() => { setBlockPlacementMode(true); setStaffHeaderMenu(null); }} className="w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] text-[#1A2421]">{t('calendar.addBlockedTime')}</button>
            {(() => {
              const scheduled = isStaffScheduledForDate(staffHeaderMenu.staffId, dateStr);
              return (
                <button onClick={async () => {
                  try {
                    await axios.put(`${API}/staff/${staffHeaderMenu.staffId}/schedule-override`, { date: dateStr, enabled: !scheduled, start: '09:00', end: '17:00' }, { withCredentials: true });
                    toast.success(scheduled ? `Day off set for ${dateStr}` : `Working day set for ${dateStr}`);
                    setStaffHeaderMenu(null);
                    fetchAll();
                  } catch { toast.error('Failed'); }
                }} className={`w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] ${scheduled ? 'text-[#E2725B]' : 'text-[#3E6641]'}`}>
                  {scheduled ? `Day off (${fmt(currentDate, 'd MMM')} only)` : `Work this day (${fmt(currentDate, 'd MMM')} only)`}
                </button>
              );
            })()}
            <button onClick={() => { window.location.href = '/staff'; setStaffHeaderMenu(null); }} className="w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] text-[#1A2421]">{t('calendar.editShift')}</button>
            <button onClick={() => { window.location.href = '/staff'; setStaffHeaderMenu(null); }} className="w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] text-[#1A2421]">{t('calendar.viewTeamMember')}</button>
          </div>
        );
      })()}

      {/* Slot Popup (Fresha-style) */}
      {slotPopup && (
        <div className="fixed bg-white border border-[#E5E7E1] rounded-xl shadow-xl z-50 py-3 w-56" style={{ left: Math.min(slotPopup.x, window.innerWidth - 240), top: Math.min(slotPopup.y, window.innerHeight - 240) }} data-testid="slot-popup">
          <div className="flex items-center justify-between px-4 pb-2 border-b border-[#E5E7E1]">
            <span className="font-semibold text-[#1A2421] text-sm">
              {slotPopup.time ? slotPopup.time : format(new Date((slotPopup.date || dateStr) + 'T12:00:00'), 'EEEE, d MMM')}
            </span>
            <button onClick={closeSlotPopup} className="text-[#5C6656] hover:text-[#1A2421]"><XIcon size={16} /></button>
          </div>
          <div className="pt-1">
            <button onClick={() => { openBookDialog(slotPopup.staffId, slotPopup.time, slotPopup.date); closeSlotPopup(); }} className="w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] text-[#1A2421] flex items-center gap-2" data-testid="slot-add-appt"><CalendarDays size={14} /> Add appointment</button>
            <button onClick={openBlockFromSlot} className="w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] text-[#1A2421] flex items-center gap-2" data-testid="slot-add-block"><Ban size={14} /> Add blocked time</button>
            {!slotPopup.time && slotPopup.date && (
              <button onClick={() => { setCurrentDate(new Date(slotPopup.date + 'T12:00:00')); setCalendarView('day'); closeSlotPopup(); }} className="w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] text-[#1A2421] flex items-center gap-2" data-testid="slot-day-view"><LayoutGrid size={14} /> Day view</button>
            )}
          </div>
        </div>
      )}

      {/* Block Time Dialog - Fresha Style */}
      <Dialog open={blockDialog} onOpenChange={setBlockDialog}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-[380px] border-[#E5E7E1] max-h-[80vh] overflow-y-auto p-0" data-testid="add-block-dialog">
          <div className="px-4 pt-4 pb-2">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }} className="text-lg text-center">Add blocked time</DialogTitle></DialogHeader>
          </div>
          <div className="px-4 space-y-3 pb-4">
            {/* Block time type — 3 equal cards */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold">Block time type</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: 'custom', icon: '✏️', label: 'Custom', sub: 'New blocked time', dur: null },
                  { key: 'lunch', icon: '🍽️', label: 'Lunch', sub: '30 min', dur: 30 },
                  { key: 'break', icon: '☕', label: 'Break', sub: '15 min', dur: 15 },
                ].map(bt => (
                  <button key={bt.key} onClick={() => {
                    const updates = { type: bt.key, reason: bt.key === 'custom' ? (blockForm.reason || '') : bt.label };
                    if (bt.dur) {
                      const [sh, sm] = (blockForm.start_time || '09:00').split(':').map(Number);
                      const total = sh * 60 + sm + bt.dur;
                      updates.end_time = `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
                    }
                    setBlockForm(p => ({ ...p, ...updates }));
                  }}
                    className={`flex items-center gap-2 px-2 py-2.5 rounded-xl border-2 transition-colors ${(blockForm.type || 'custom') === bt.key ? 'border-[#1A2421] bg-[#F5F7F0]' : 'border-[#E5E7E1] hover:border-[#D1D5CB]'}`}>
                    <span style={{ fontSize: '18px' }}>{bt.icon}</span>
                    <div className="text-left min-w-0">
                      <p className="text-xs font-bold text-[#1A2421] leading-tight truncate">{bt.label}</p>
                      <p className="text-[10px] text-[#5C6656] leading-tight truncate">{bt.sub}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            {/* Title */}
            <div className="space-y-1">
              <Label className="text-xs font-bold">Title <span className="font-normal text-[#5C6656]">(Optional)</span></Label>
              <Input value={blockForm.reason} onChange={e => setBlockForm(p => ({ ...p, reason: e.target.value }))} className="border-[#E5E7E1] h-10" placeholder="e.g. Lunch meeting" data-testid="block-reason" />
            </div>
            {/* Date */}
            <div className="space-y-1">
              <Label className="text-xs font-bold">Date</Label>
              <Input type="date" value={blockForm.date} onChange={e => setBlockForm(p => ({ ...p, date: e.target.value }))} className="border-[#E5E7E1] h-10" data-testid="block-date" />
            </div>
            {/* Start / End time */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs font-bold">Start time</Label>
                <Input type="time" value={blockForm.start_time} onChange={e => setBlockForm(p => ({ ...p, start_time: e.target.value }))} className="border-[#E5E7E1] h-10" data-testid="block-start" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold">End time</Label>
                <Input type="time" value={blockForm.end_time || (() => { const [h,m] = (blockForm.start_time||'09:00').split(':').map(Number); return `${String(h+1).padStart(2,'0')}:${String(m).padStart(2,'0')}`; })()} onChange={e => setBlockForm(p => ({ ...p, end_time: e.target.value }))} className="border-[#E5E7E1] h-10" data-testid="block-end" />
              </div>
            </div>
            {(() => { const [sh,sm] = (blockForm.start_time||'09:00').split(':').map(Number); const [eh,em] = ((blockForm.end_time || (() => { const [h2,m2] = (blockForm.start_time||'09:00').split(':').map(Number); return `${String(h2+1).padStart(2,'0')}:${String(m2).padStart(2,'0')}`; })())).split(':').map(Number); const dur = (eh*60+em)-(sh*60+sm); return dur > 0 ? <p className="text-xs text-[#5C6656]">{Math.floor(dur/60) > 0 ? `${Math.floor(dur/60)}h${dur%60 > 0 ? ` ${dur%60}min` : ''}` : `${dur}min`} duration</p> : null; })()}
            {/* Team member */}
            <div className="space-y-1">
              <Label className="text-xs font-bold">Team member</Label>
              <select value={blockForm.staff_id} onChange={e => setBlockForm(p => ({ ...p, staff_id: e.target.value }))} className="w-full h-10 px-3 border border-[#E5E7E1] rounded-lg text-sm bg-white" data-testid="block-staff">
                {staff.filter(s => s.is_active).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            {/* Frequency */}
            <div className="space-y-1">
              <Label className="text-xs font-bold">Frequency</Label>
              <select value={blockForm.frequency || 'none'} onChange={e => setBlockForm(p => ({ ...p, frequency: e.target.value }))} className="w-full h-10 px-3 border border-[#E5E7E1] rounded-lg text-sm bg-white">
                <option value="none">Doesn't repeat</option>
                <option value="daily">Every day</option>
                <option value="weekly">Every week</option>
              </select>
            </div>
            {/* Note */}
            <div className="space-y-1">
              <Label className="text-xs font-bold">Note <span className="font-normal text-[#5C6656]">(Optional)</span></Label>
              <textarea value={blockForm.description || ''} onChange={e => setBlockForm(p => ({ ...p, description: e.target.value }))} className="w-full h-14 px-3 py-2 border border-[#E5E7E1] rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#556B2F]/20 bg-white" placeholder="Add a note..." maxLength={255} />
            </div>
            <Button onClick={saveBlock} className="w-full bg-[#1A2421] hover:bg-[#0D1210] text-white py-5 text-sm font-bold" data-testid="save-block-btn">Save</Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Fresha-style Client Profile Panel */}
      {sidebarClientProfile && (() => { try {
        const p = sidebarClientProfile;
        const cl = p.client || {};
        const st = p.stats || {};
        const apptsList = p.appointments || [];
        const invList = p.invoices || [];
        const topSvcs = p.top_services || [];
        return (
        <div className="fixed inset-0 z-[60] flex" data-testid="client-profile-panel">
          <div className="flex-1 bg-black/20" onClick={() => setSidebarClientProfile(null)} />
          <div className="w-[700px] max-w-full bg-white border-l border-[#E5E7E1] flex h-full shadow-2xl">
            {/* Left sidebar */}
            <div className="w-[220px] border-r border-[#E5E7E1] flex flex-col bg-[#FAFAF8] flex-shrink-0">
              <div className="p-4 text-center border-b border-[#E5E7E1]">
                <div className="w-14 h-14 rounded-full bg-[#E9EDDF] flex items-center justify-center text-xl font-bold text-[#556B2F] mx-auto mb-2">{cl.name ? cl.name.charAt(0).toUpperCase() : '?'}</div>
                <p className="font-bold text-sm text-[#1A2421]">{cl.name || 'Unknown'}</p>
                {cl.is_blocked && <span className="inline-block px-2 py-0.5 text-[10px] font-bold text-[#E2725B] bg-[#FDE8E8] rounded-full mt-1">Blocked</span>}
                {cl.email && <p className="text-[11px] text-[#5C6656] mt-1 truncate">{cl.email}</p>}
                {cl.phone && <p className="text-[11px] text-[#5C6656]">{cl.phone}</p>}
                <button className="mt-3 px-3 py-1 rounded-lg border border-[#E5E7E1] text-xs text-[#5C6656] hover:bg-white" onClick={() => setSidebarClientProfile(null)}>Close</button>
              </div>
              <div className="flex-1 overflow-y-auto py-1">
                {['overview','appointments','sales','activity','details'].map(t => (
                  <button key={t} onClick={() => setProfileTab(t)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${profileTab === t ? 'bg-white text-[#1A2421] font-semibold border-l-2 border-[#556B2F]' : 'text-[#5C6656] hover:bg-white/60'}`}>
                    {t === 'overview' ? 'Overview' : t === 'appointments' ? `Appointments (${apptsList.length})` : t === 'sales' ? `Sales (${invList.length})` : t === 'activity' ? `Activity (${sidebarClientActivity.length})` : 'Client details'}
                  </button>
                ))}
              </div>
              <div className="p-3 border-t border-[#E5E7E1] text-[11px] text-[#5C6656] space-y-1">
                {cl.birthday && <p>{cl.birthday}</p>}
                {cl.gender && <p className="capitalize">{cl.gender}</p>}
                {cl.created_at && <p>Joined {String(cl.created_at).slice(0,10)}</p>}
              </div>
            </div>

            {/* Right content */}
            <div className="flex-1 overflow-y-auto">
              {profileTab === 'overview' && (
                <div className="p-5 space-y-4">
                  <h3 className="text-lg font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Overview</h3>
                  <div className="border border-[#E5E7E1] rounded-xl p-4">
                    <p className="text-[10px] font-bold text-[#5C6656] uppercase mb-1">Total sales</p>
                    <p className="text-2xl font-bold text-[#1A2421]">{'\u00A3'}{(st.total_spend || 0).toFixed(0)}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[{l:'Completed',v:st.total_visits||0,c:'#1A2421'},{l:'All appointments',v:st.total_appointments||0,c:'#2E86C1'},{l:'Cancelled',v:st.cancelled||0,c:'#E2725B'},{l:'No show',v:st.no_shows||0,c:'#C0392B'}].map(s=>(
                      <div key={s.l} className="border border-[#E5E7E1] rounded-xl p-3">
                        <p className="text-[10px] font-bold text-[#5C6656] uppercase">{s.l}</p>
                        <p className="text-xl font-bold" style={{color:s.c}}>{s.v}</p>
                      </div>
                    ))}
                  </div>
                  {topSvcs.length > 0 && (
                    <div className="border border-[#E5E7E1] rounded-xl p-4">
                      <p className="text-[10px] font-bold text-[#5C6656] uppercase mb-2">Top services</p>
                      {topSvcs.map((s,i)=>(<div key={i} className="flex justify-between text-sm py-1 border-b border-[#E5E7E1]/50 last:border-0"><span className="text-[#1A2421]">{s.name}</span><span className="text-[#5C6656]">{s.count}x</span></div>))}
                    </div>
                  )}
                  {st.last_visit && <p className="text-xs text-[#5C6656]">Last visit: {st.last_visit}</p>}
                </div>
              )}

              {profileTab === 'appointments' && (
                <div className="p-5 space-y-3">
                  <h3 className="text-lg font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Appointments</h3>
                  <p className="text-xs text-[#5C6656] -mt-2">Sorted by most recently booked (latest first)</p>
                  {apptsList.length === 0 ? <p className="text-sm text-[#5C6656] py-4">No appointments yet</p> :
                    [...apptsList].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || ''))).map((a,i) => (
                      <div key={i} className="border border-[#E5E7E1] rounded-xl p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-xs text-[#5C6656]">{a.date || ''} · {a.start_time || ''}</p>
                            <p className="text-sm font-bold text-[#1A2421] mt-0.5">{a.service_name || 'Service'}</p>
                            <p className="text-xs text-[#5C6656] mt-0.5">{a.staff_name || ''} · {a.duration || 30}min</p>
                            {a.created_at && <p className="text-[10px] text-[#8C9085] mt-1">Booked {String(a.created_at).slice(0, 16).replace('T', ' ')}</p>}
                            {a.source === 'online' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#2E86C1]/10 text-[#2E86C1] font-bold mt-1 inline-block mr-1">Online</span>}
                            {a.source === 'fresha_import' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#8E44AD]/10 text-[#8E44AD] font-bold mt-1 inline-block mr-1">Imported</span>}
                            {a.source === 'ai_assistant' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#556B2F]/10 text-[#556B2F] font-bold mt-1 inline-block mr-1">AI</span>}
                          </div>
                          <div className="text-right">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${a.status === 'completed' ? 'bg-[#E9EDDF] text-[#3E6641]' : a.status === 'cancelled' ? 'bg-[#FDE8E8] text-[#E2725B]' : a.status === 'no-show' ? 'bg-[#FDE8E8] text-[#C0392B]' : 'bg-[#F5F7F0] text-[#556B2F]'}`}>{a.status || 'booked'}</span>
                            <p className="text-sm font-bold text-[#1A2421] mt-1">{'\u00A3'}{(a.price || 0).toFixed(0)}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  }
                </div>
              )}

              {profileTab === 'activity' && (
                <div className="p-5 space-y-3">
                  <h3 className="text-lg font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Appointment activity</h3>
                  <p className="text-xs text-[#5C6656] -mt-2">Timeline of bookings, updates and reminders</p>
                  {sidebarClientActivity.length === 0 ? (
                    <p className="text-sm text-[#5C6656] py-4">No activity yet</p>
                  ) : (
                    <div className="space-y-3">
                      {sidebarClientActivity.map((ev, i) => (
                        <div key={i} className="relative pl-5 pb-2 border-l-2 border-[#E5E7E1]">
                          <div className={`absolute -left-[7px] top-1 w-3 h-3 rounded-full ${
                            ev.type === 'created' ? 'bg-[#3E6641]' :
                            ev.type === 'notification' ? 'bg-[#2E86C1]' :
                            ev.type === 'cancelled' ? 'bg-[#E2725B]' :
                            ev.type === 'rescheduled' ? 'bg-[#F39C12]' :
                            'bg-[#B0B5A8]'
                          }`} />
                          <p className="text-sm font-bold text-[#1A2421]">{ev.title}</p>
                          <p className="text-[11px] text-[#8C9085] mt-0.5">{String(ev.created_at || '').slice(0, 16).replace('T', ' ')}{ev.by ? ` \u00b7 by ${ev.by}` : ''}</p>
                          {ev.details && <p className="text-xs text-[#5C6656] mt-1">{ev.details}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {profileTab === 'sales' && (
                <div className="p-5 space-y-3">
                  <h3 className="text-lg font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Sales</h3>
                  {invList.length === 0 ? <p className="text-sm text-[#5C6656] py-4">No sales yet</p> :
                    invList.map((inv,i)=>(
                      <div key={i} className="border border-[#E5E7E1] rounded-xl p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-xs text-[#5C6656]">{inv.date || ''}</p>
                            <p className="text-sm font-bold text-[#1A2421] mt-0.5">{inv.service_name || 'Sale'}</p>
                            <p className="text-xs text-[#5C6656]">{inv.staff_name || ''} · #{inv.invoice_number || ''}</p>
                          </div>
                          <div className="text-right">
                            <span className={`text-[10px] font-bold ${inv.status === 'paid' ? 'text-[#3E6641]' : 'text-[#E2725B]'}`}>{inv.status === 'paid' ? 'Paid' : String(inv.status || '')}</span>
                            <p className="text-sm font-bold text-[#1A2421] mt-0.5">{'\u00A3'}{(inv.final_total || 0).toFixed(0)}</p>
                            {inv.payment_method && <p className="text-[10px] text-[#5C6656] capitalize">{inv.payment_method}</p>}
                          </div>
                        </div>
                      </div>
                    ))
                  }
                </div>
              )}

              {profileTab === 'details' && (
                <div className="p-5 space-y-5">
                  <h3 className="text-lg font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Client details</h3>
                  <div className="space-y-4">
                    {[{l:'Full name',v:cl.name},{l:'Email',v:cl.email},{l:'Phone',v:cl.phone},{l:'Date of birth',v:cl.birthday},{l:'Gender',v:cl.gender},{l:'Joined',v:cl.created_at?String(cl.created_at).slice(0,10):''},{l:'Source',v:cl.source||cl.referral_source||'Manual'},{l:'Notes',v:cl.notes}].map(f=>(
                      <div key={f.l} className="flex justify-between items-start border-b border-[#E5E7E1] pb-2">
                        <span className="text-xs text-[#5C6656]">{f.l}</span>
                        <span className="text-sm text-[#1A2421] text-right max-w-[60%]">{f.v || '-'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        );
      } catch(e) { console.error('Profile panel error:', e); return null; } })()}


      {/* Edit Blocked Time — Fresha-style Dialog */}
      <Dialog open={!!editingBlock} onOpenChange={(o) => { if (!o) setEditingBlock(null); }}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-sm border-[#E5E7E1] max-h-[90vh] sm:max-h-[80vh] flex flex-col p-0" data-testid="edit-block-dialog">
          {editingBlock && (<>
            <div className="px-5 pt-5 pb-2 flex-shrink-0">
              <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }} className="text-lg">Edit blocked time</DialogTitle></DialogHeader>
            </div>
            <div className="px-5 space-y-3 pb-3 flex-1 overflow-y-auto">
              <div className="space-y-1">
                <label className="text-xs font-bold text-[#1A2421]">Title <span className="font-normal text-[#5C6656]">(Optional)</span></label>
                <Input value={editingBlock.reason || ''} onChange={e => setEditingBlock(prev => ({ ...prev, reason: e.target.value }))} className="border-[#E5E7E1] h-10" placeholder="e.g. Lunch meeting" data-testid="edit-block-reason" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-[#1A2421]">Date</label>
                <Input type="date" value={editingBlock.date} onChange={e => setEditingBlock(prev => ({ ...prev, date: e.target.value }))} className="border-[#E5E7E1] h-10" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-[#1A2421]">Start time</label>
                  <Input type="time" value={editingBlock.start_time} onChange={e => setEditingBlock(prev => ({ ...prev, start_time: e.target.value }))} className="border-[#E5E7E1] h-10" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-[#1A2421]">End time</label>
                  <Input type="time" value={editingBlock.end_time} onChange={e => setEditingBlock(prev => ({ ...prev, end_time: e.target.value }))} className="border-[#E5E7E1] h-10" />
                </div>
              </div>
              {(() => { const [sh,sm] = (editingBlock.start_time||'09:00').split(':').map(Number); const [eh,em] = (editingBlock.end_time||'09:15').split(':').map(Number); const dur = (eh*60+em)-(sh*60+sm); return dur > 0 ? <p className="text-xs text-[#5C6656]">{Math.floor(dur/60) > 0 ? `${Math.floor(dur/60)}h${dur%60 > 0 ? ` ${dur%60}min` : ''}` : `${dur}min`}</p> : null; })()}
              <div className="space-y-1">
                <label className="text-xs font-bold text-[#1A2421]">Team member</label>
                <select value={editingBlock.staff_id} onChange={e => setEditingBlock(prev => ({ ...prev, staff_id: e.target.value }))} className="w-full h-10 px-3 border border-[#E5E7E1] rounded-lg text-sm bg-white">
                  {staff.filter(s => s.is_active).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-[#1A2421]">Notes <span className="font-normal text-[#5C6656]">(Optional)</span></label>
                <textarea
                  value={editingBlock.notes ?? editingBlock.description ?? ''}
                  onChange={e => { const v = e.target.value; setEditingBlock(prev => prev ? ({ ...prev, notes: v, description: v }) : prev); }}
                  onFocus={(e) => {
                    // On mobile, the iOS keyboard pushes the dialog content; scroll the textarea into view
                    // so it stays visible above the keyboard. Without this the user can type but can't see what they wrote.
                    setTimeout(() => { try { e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch {} }, 300);
                  }}
                  className="w-full h-20 px-3 py-2 border border-[#E5E7E1] rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#556B2F]/20" placeholder="Add a note..."
                  data-testid="edit-block-notes"
                />
              </div>
            </div>
            <div className="px-5 pb-5 pt-3 border-t border-[#E5E7E1] flex items-center gap-3 flex-shrink-0 bg-white" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 20px)' }}>
              <button type="button" onClick={(e) => { e.stopPropagation(); e.preventDefault(); deleteBlock(editingBlock.id); }} className="px-3 py-2.5 rounded-xl border border-[#E2725B]/30 hover:bg-[#E2725B]/10 transition-colors flex items-center gap-1.5 text-[#E2725B] text-sm font-semibold" data-testid="delete-block-btn">
                <Trash2 size={16} /> Delete
              </button>
              <Button variant="outline" onClick={() => setEditingBlock(null)} className="flex-1 border-[#E5E7E1] text-[#1A2421]">Cancel</Button>
              <Button onClick={saveEditBlock} className="flex-1 bg-[#1A2421] hover:bg-[#0D1210] text-white" data-testid="save-edit-block-btn">Save</Button>
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Active Block — Cancel / Save bar (Fresha-style) */}
      {activeBlock && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[#E5E7E1] shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-3 flex gap-3" data-testid="block-move-bar">
          <Button variant="outline" onClick={cancelActiveBlock} className="flex-1 border-[#E5E7E1] text-[#1A2421] rounded-lg py-5 font-semibold">Cancel</Button>
          <Button onClick={saveActiveBlock} className="flex-1 bg-[#1A2421] hover:bg-[#0D1210] text-white rounded-lg py-5 font-semibold">Save</Button>
        </div>
      )}

      {/* Touch Edit Mode: Cancel / Save bar (Fresha-style fixed bottom — above bottom nav) */}
      {touchEditMode && (
        <div className="fixed bottom-0 left-0 right-0 z-[10000] bg-white border-t border-[#E5E7E1] px-4 py-3 flex gap-3" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 12px)' }} data-testid="touch-edit-bar">
          <button onClick={cancelTouchEdit} className="flex-1 py-3.5 rounded-xl border border-[#E5E7E1] text-sm font-semibold text-[#1A2421] active:bg-[#F5F7F0]" data-testid="touch-edit-cancel">Cancel</button>
          <button onClick={saveTouchEdit} className="flex-1 py-3.5 rounded-xl bg-[#556B2F] text-white text-sm font-semibold active:bg-[#4A5D23]" data-testid="touch-edit-save">Save</button>
        </div>
      )}

      {/* Book Appointment Dialog */}

      {/* Placement Mode — Fresha-style banner */}
      {(blockPlacementMode || apptPlacementMode) && (
        <div className="fixed top-0 left-0 right-0 z-[9999] bg-[#1A2421] text-white px-4 py-3 flex items-center justify-between shadow-lg" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 12px)' }} data-testid="placement-banner">
          <span className="text-sm font-semibold">{blockPlacementMode ? 'Tap a time slot to block' : 'Select a time'}</span>
          <button onClick={() => { setBlockPlacementMode(false); setApptPlacementMode(false); blockPlaceRef.current = null; }} className="px-4 py-1.5 rounded-lg bg-white/20 text-white text-sm font-medium active:bg-white/30" data-testid="cancel-placement">Cancel</button>
        </div>
      )}

      <Dialog open={bookDialogOpen} onOpenChange={setBookDialogOpen}>
        <DialogContent className="max-w-[calc(100vw-1.5rem)] sm:max-w-md border-[#E5E7E1] max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('appointments.newAppointment')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3 mt-1 overflow-y-auto flex-1 pr-1">
            {/* Client: Fresha-style Search / Selected / Walk-In */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t('appointments.client')}</Label>
                <button type="button" onClick={toggleWalkIn} className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors ${isWalkIn ? 'bg-[#556B2F] text-white' : 'text-[#556B2F] hover:bg-[#E9EDDF]'}`} data-testid="walk-in-toggle">
                  <UserPlus size={12} /> {t('appointments.walkIn')}
                </button>
              </div>
              {isWalkIn ? (
                <Input data-testid="walk-in-name" placeholder="Walk-in client name (optional)" value={walkInName} onChange={e => setWalkInName(e.target.value)} className="border-[#E5E7E1]" />
              ) : selectedClient && !showNewClientForm ? (
                /* Selected client card (Fresha-style) */
                <div className="border border-[#E5E7E1] rounded-xl p-3" data-testid="selected-client-card">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#E9EDDF] flex items-center justify-center text-sm font-bold text-[#556B2F] flex-shrink-0">{selectedClient.name?.charAt(0)?.toUpperCase() || '?'}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#1A2421]">{selectedClient.name}</p>
                      {selectedClient.email && <p className="text-xs text-[#5C6656] truncate">{selectedClient.email}</p>}
                      {selectedClient.phone && <p className="text-xs text-[#5C6656]">{selectedClient.phone}</p>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button type="button" onClick={() => setShowClientActions(!showClientActions)} className="text-xs px-2.5 py-1 rounded-md border border-[#E5E7E1] text-[#1A2421] hover:bg-[#F5F7F0] flex items-center gap-1" data-testid="client-actions-btn">Actions <ChevronDown size={10} /></button>
                    </div>
                  </div>
                  {/* No-show / blocked client warnings */}
                  {(() => {
                    const ns = Number(selectedClient.no_show_count || 0);
                    if (selectedClient.is_blocked) {
                      return (
                        <div className="mt-2 p-2 bg-[#FDE8E8] border border-[#E2725B]/40 rounded-lg flex items-center gap-2" data-testid="client-blocked-banner">
                          <Ban size={14} className="text-[#C0392B] flex-shrink-0" />
                          <p className="text-[11px] font-semibold text-[#C0392B] flex-1">This client is blocked. New bookings will be rejected.</p>
                          <button type="button" onClick={async () => {
                            try {
                              await axios.delete(`${API}/clients/${selectedClient.id}/block`, { withCredentials: true });
                              setSelectedClient(c => c ? { ...c, is_blocked: false } : c);
                              toast.success('Client unblocked');
                            } catch (err) { toast.error(err.response?.data?.detail || 'Failed to unblock'); }
                          }} className="text-[10px] underline text-[#C0392B] hover:text-[#E2725B]" data-testid="unblock-client-btn">Unblock</button>
                        </div>
                      );
                    }
                    if (ns >= 3) {
                      return (
                        <div className="mt-2 p-2 bg-[#FFF4E5] border border-[#E2725B] rounded-lg" data-testid="client-noshow-warning">
                          <div className="flex items-start gap-2">
                            <AlertTriangle size={14} className="text-[#E2725B] flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-bold text-[#C0392B]">{ns} no-shows on record</p>
                              <p className="text-[10px] text-[#5C6656] mt-0.5">This client has missed {ns} appointments. Consider blocking them.</p>
                            </div>
                          </div>
                          <button type="button" onClick={async () => {
                            if (!window.confirm(`Block ${selectedClient.name}? They won't be able to book again until unblocked.`)) return;
                            try {
                              await axios.post(`${API}/clients/${selectedClient.id}/block`, { reason: `${ns} no-shows` }, { withCredentials: true });
                              setSelectedClient(c => c ? { ...c, is_blocked: true } : c);
                              toast.success(`${selectedClient.name} blocked`);
                            } catch (err) { toast.error(err.response?.data?.detail || 'Failed to block'); }
                          }} className="mt-2 w-full text-[11px] font-semibold bg-[#C0392B] hover:bg-[#A83020] text-white rounded-md py-1.5 transition-colors" data-testid="block-client-btn">
                            Block this client
                          </button>
                        </div>
                      );
                    }
                    if (ns >= 1) {
                      return (
                        <div className="mt-2 p-1.5 bg-[#FFF4E5] border border-[#F2C879] rounded-md flex items-center gap-2" data-testid="client-noshow-banner">
                          <AlertTriangle size={12} className="text-[#D4A373] flex-shrink-0" />
                          <p className="text-[11px] text-[#7C5A2A]"><span className="font-bold">{ns}</span> no-show{ns > 1 ? 's' : ''} on record</p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {showClientActions && (
                    <div className="mt-2 pt-2 border-t border-[#E5E7E1] space-y-1" data-testid="client-actions-menu">
                      <button type="button" onClick={() => {
                        setShowClientActions(false);
                        const fullName = selectedClient.name || '';
                        const parts = fullName.trim().split(/\s+/);
                        setEditClientData({ ...selectedClient, name: parts[0] || '', last_name: selectedClient.last_name || parts.slice(1).join(' ') || '' });
                        setShowEditClient(true);
                      }} className="w-full text-left px-3 py-1.5 text-xs text-[#1A2421] hover:bg-[#F5F7F0] rounded flex items-center gap-2"><Pencil size={11} /> Edit client details</button>
                      <button type="button" onClick={() => { setSelectedClient(null); setClientSearch(''); setForm(p => ({ ...p, client_id: '' })); setShowClientActions(false); }} className="w-full text-left px-3 py-1.5 text-xs text-[#E2725B] hover:bg-[#FDE8E8] rounded flex items-center gap-2"><XIcon size={11} /> Remove client</button>
                    </div>
                  )}
                  {/* Inline edit client form */}
                  {showEditClient && editClientData && (
                    <div className="mt-3 pt-3 border-t border-[#E5E7E1] space-y-2" data-testid="edit-client-inline">
                      <p className="text-xs font-bold text-[#1A2421]">Edit client details</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="First name *" value={editClientData.name || ''} onChange={e => setEditClientData(p => ({ ...p, name: e.target.value }))} className="border-[#E5E7E1] bg-white text-sm" />
                        <Input placeholder="Last name" value={editClientData.last_name || ''} onChange={e => setEditClientData(p => ({ ...p, last_name: e.target.value }))} className="border-[#E5E7E1] bg-white text-sm" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Email" type="email" value={editClientData.email || ''} onChange={e => setEditClientData(p => ({ ...p, email: e.target.value }))} className="border-[#E5E7E1] bg-white text-sm" />
                        <Input placeholder="Phone" value={editClientData.phone || ''} onChange={e => setEditClientData(p => ({ ...p, phone: e.target.value }))} className="border-[#E5E7E1] bg-white text-sm" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><label className="text-[10px] text-[#5C6656]">Birthday</label><Input type="date" value={editClientData.birthday || ''} onChange={e => setEditClientData(p => ({ ...p, birthday: e.target.value }))} className="border-[#E5E7E1] bg-white text-sm" /></div>
                        <div><label className="text-[10px] text-[#5C6656]">Gender</label>
                          <select value={editClientData.gender || ''} onChange={e => setEditClientData(p => ({ ...p, gender: e.target.value }))} className="w-full h-9 px-2 text-sm border border-[#E5E7E1] rounded-lg bg-white">
                            <option value="">Select</option><option value="female">Female</option><option value="male">Male</option><option value="non-binary">Non-binary</option><option value="other">Other</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button type="button" size="sm" variant="outline" onClick={() => setShowEditClient(false)} className="text-xs border-[#E5E7E1]">Cancel</Button>
                        <Button type="button" size="sm" className="text-xs bg-[#1A2421] text-white" data-testid="save-edit-client"
                          onClick={async () => {
                            try {
                              const fullName = [editClientData.name, editClientData.last_name].filter(Boolean).join(' ');
                              await axios.put(`${API}/clients/${selectedClient.id}`, { name: fullName, last_name: editClientData.last_name || '', email: editClientData.email, phone: editClientData.phone, birthday: editClientData.birthday || '', gender: editClientData.gender || '' }, { withCredentials: true });
                              setSelectedClient({ ...selectedClient, name: fullName, email: editClientData.email, phone: editClientData.phone });
                              setClientSearch(fullName);
                              setShowEditClient(false);
                              toast.success('Client updated');
                            } catch { toast.error('Failed to update'); }
                          }}>Save</Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative" ref={clientSearchRef}>
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6656]" />
                    <Input data-testid="client-search-input" placeholder="Search client or enter phone number..." value={clientSearch} onChange={e => searchClients(e.target.value)} onFocus={() => clientResults.length > 0 && setShowClientDropdown(true)} className="pl-9 border-[#E5E7E1]" autoComplete="off" />
                  </div>
                  {showClientDropdown && !showNewClientForm && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7E1] rounded-lg shadow-lg max-h-64 overflow-auto">
                      {/* Add new client */}
                      <button type="button" onClick={() => {
                        setShowNewClientForm(true); setShowClientDropdown(false);
                        const searchVal = clientSearch.trim();
                        const isPhone = /^\+?\d[\d\s-]{5,}$/.test(searchVal);
                        const parts = searchVal.split(/\s+/);
                        const firstName = parts[0] || '';
                        const lastName = parts.slice(1).join(' ');
                        setNewClientForm(isPhone ? { name: '', last_name: '', email: '', phone: searchVal, birthday: '', gender: '' } : { name: firstName, last_name: lastName, email: '', phone: '', birthday: '', gender: '' });
                      }} className="w-full text-left px-3 py-2.5 hover:bg-[#F5F7F0] flex items-center gap-2 border-b border-[#E5E7E1] sticky top-0 bg-white z-10" data-testid="add-client-btn-results">
                        <div className="w-7 h-7 rounded-full bg-[#556B2F] flex items-center justify-center"><Plus size={12} className="text-white" /></div>
                        <span className="text-sm font-medium text-[#556B2F]">Add new client</span>
                      </button>
                      {/* Walk-in option */}
                      <button type="button" onClick={() => { toggleWalkIn(); setShowClientDropdown(false); }} className="w-full text-left px-3 py-2 hover:bg-[#F5F7F0] flex items-center gap-2 border-b border-[#E5E7E1]">
                        <div className="w-7 h-7 rounded-full bg-[#F5F7F0] flex items-center justify-center"><UserPlus size={12} className="text-[#5C6656]" /></div>
                        <span className="text-sm text-[#5C6656]">Walk-In</span>
                      </button>
                      {clientResults.length === 0 && clientSearch.length >= 1 && (
                        <div className="px-3 py-3 text-sm text-[#5C6656] text-center">No clients found for "{clientSearch}"</div>
                      )}
                      {clientResults.map(c => (
                        <button type="button" key={c.id} onClick={() => selectClient(c)} className="w-full text-left px-3 py-2.5 hover:bg-[#E9EDDF] transition-colors border-b border-[#E5E7E1]/50 last:border-0 flex items-center gap-3" data-testid={`client-option-${c.id}`}>
                          <div className="w-8 h-8 rounded-full bg-[#F0F2EC] flex items-center justify-center text-xs font-bold text-[#556B2F] flex-shrink-0">{c.name?.charAt(0)?.toUpperCase() || '?'}</div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#1A2421] truncate">{c.name || 'Unnamed'}</p>
                            <p className="text-xs text-[#5C6656] truncate">{c.phone || c.email || ''}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Inline Add New Client Form (Fresha-style) */}
              {showNewClientForm && (
                <div className="mt-2 p-4 bg-[#F9F8F6] border border-[#E5E7E1] rounded-xl space-y-3" data-testid="new-client-inline-form">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-[#1A2421]">Add a new client</p>
                    <button type="button" onClick={() => setShowNewClientForm(false)} className="text-xs text-[#5C6656] hover:underline">Cancel</button>
                  </div>
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="text-[10px] text-[#5C6656] font-bold">First name *</label><Input value={newClientForm.name} onChange={e => setNewClientForm(p => ({ ...p, name: e.target.value }))} className="border-[#E5E7E1] bg-white" data-testid="new-client-name" autoFocus /></div>
                      <div><label className="text-[10px] text-[#5C6656] font-bold">Last name</label><Input value={newClientForm.last_name || ''} onChange={e => setNewClientForm(p => ({ ...p, last_name: e.target.value }))} className="border-[#E5E7E1] bg-white" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="text-[10px] text-[#5C6656] font-bold">Email</label><Input type="email" value={newClientForm.email} onChange={e => setNewClientForm(p => ({ ...p, email: e.target.value }))} className="border-[#E5E7E1] bg-white" data-testid="new-client-email" /></div>
                      <div><label className="text-[10px] text-[#5C6656] font-bold">Phone *</label><Input value={newClientForm.phone} onChange={e => setNewClientForm(p => ({ ...p, phone: e.target.value }))} className="border-[#E5E7E1] bg-white" data-testid="new-client-phone" /></div>
                    </div>
                  </div>
                  <Button type="button" size="sm" className="w-full bg-[#1A2421] hover:bg-[#0D1210] text-white" data-testid="save-new-client"
                    onClick={async () => {
                      if (!newClientForm.name.trim()) { toast.error('First name is required'); return; }
                      if (!newClientForm.phone.trim()) { toast.error('Phone number is required'); return; }
                      try {
                        const fullName = [newClientForm.name, newClientForm.last_name].filter(Boolean).join(' ');
                        const { data } = await axios.post(`${API}/clients`, { name: fullName, last_name: newClientForm.last_name || '', email: newClientForm.email, phone: newClientForm.phone }, { withCredentials: true });
                        selectClient({ id: data.id, name: fullName, email: newClientForm.email, phone: newClientForm.phone });
                        setShowNewClientForm(false);
                        setNewClientForm({ name: '', last_name: '', email: '', phone: '', birthday: '', gender: '' });
                        toast.success('Client created');
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create client'); }
                    }}>Save & Select Client</Button>
                </div>
              )}
            </div>

            {/* Last visits — quick re-book */}
            {selectedClient && clientHistory.length > 0 && (
              <div className="space-y-1.5" data-testid="client-last-visits">
                <Label className="text-xs text-[#5C6656]">Last {clientHistory.length} visit{clientHistory.length > 1 ? 's' : ''} — tap to re-book</Label>
                <div className="space-y-1.5">
                  {clientHistory.map((v, i) => {
                    // "(upcoming)" should only show when the appointment is BOTH confirmed AND in the future.
                    // Compute "today" in the business's timezone so an admin abroad still gets correct labels.
                    let isUpcoming = false;
                    try {
                      if (v.status === 'confirmed' && v.date) {
                        const todayParts = businessTimezone
                          ? new Intl.DateTimeFormat('en-CA', { timeZone: businessTimezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date())
                          : null;
                        const todayStr = todayParts
                          ? `${todayParts.find(p => p.type === 'year').value}-${todayParts.find(p => p.type === 'month').value}-${todayParts.find(p => p.type === 'day').value}`
                          : format(new Date(), 'yyyy-MM-dd');
                        // Compare lexicographically — yyyy-MM-dd strings sort correctly.
                        isUpcoming = v.date > todayStr || (v.date === todayStr && (v.start_time || '23:59') >= format(new Date(), 'HH:mm'));
                      }
                    } catch { /* fall back to false */ }
                    return (
                    <button
                      type="button"
                      key={i}
                      onClick={() => {
                        const newSvcs = (v.services || [])
                          .filter(s => s.id)
                          .map(s => ({ id: s.id, name: s.name, duration: s.duration || 30, price: s.price || 0 }));
                        if (newSvcs.length === 0) { toast.error('No services to re-book from this visit'); return; }
                        setBookingServices(newSvcs);
                        toast.success(`Loaded ${newSvcs.length} service${newSvcs.length > 1 ? 's' : ''} from ${v.date}`);
                      }}
                      className="w-full text-left px-3 py-2 bg-[#F5F7F0] hover:bg-[#E9EDDF] border border-[#E5E7E1] hover:border-[#556B2F] rounded-lg transition-colors"
                      data-testid={`rebook-visit-${i}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-[#1A2421]">
                            {v.date}{v.start_time ? ` · ${v.start_time}` : ''}
                            {isUpcoming && <span className="ml-1.5 text-[10px] text-[#2E86C1] font-medium">(upcoming)</span>}
                          </p>
                          <p className="text-xs text-[#5C6656] truncate">
                            {(v.services || []).map(s => s.name).filter(Boolean).join(', ')}
                          </p>
                        </div>
                        <span className="text-xs font-semibold text-[#556B2F] flex-shrink-0">{money(v.total_price || 0)}</span>
                      </div>
                    </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Services: Multi-select with search */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t('appointments.services')}</Label>
                <span className="text-xs text-[#5C6656]">{bookingServices.length > 0 ? `${bookingServices.length} ${t('appointments.selected')} · ${bookingServices.reduce((t, s) => t + (s.duration || 0), 0)}${t('appointments.min')} · ${money(bookingServices.reduce((t, s) => t + (s.price || 0), 0))}` : ''}</span>
              </div>
              {/* Popular services quick-pick */}
              {popularServices.length > 0 && bookingServices.length === 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="popular-services-picks">
                  {popularServices.map(ps => (
                    <button type="button" key={ps.id} onClick={() => setBookingServices(prev => [...prev, { id: ps.id, name: ps.name, duration: ps.duration || 30, price: ps.price || 0 }])}
                      className="px-2.5 py-1 text-xs rounded-full border border-[#C5CDB8] bg-[#F5F7F0] text-[#3A4A2E] hover:bg-[#E9EDDF] hover:border-[#556B2F] transition-colors" data-testid={`popular-svc-${ps.id}`}>
                      {ps.name} <span className="text-[#5C6656] ml-0.5">{money(ps.price)}</span>
                    </button>
                  ))}
                </div>
              )}
              {/* Selected services list */}
              {bookingServices.length > 0 && (
                <div className="space-y-2">
                  {bookingServices.map((svc, idx) => (
                    <div key={`${svc.id}-${idx}`} className="px-3 py-2 bg-[#F5F7F0] rounded-lg border border-[#E5E7E1]">
                      <div className="flex items-start gap-2">
                        <span className="text-xs font-medium text-[#556B2F] flex-shrink-0 mt-0.5">{idx + 1}.</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#1A2421] font-medium truncate">{svc.name}</p>
                          <div className="flex items-center gap-1.5 text-[11px] text-[#5C6656]">
                            <span>{svc.duration}min ·</span>
                            <button type="button" onClick={() => {
                              const cur = String(svc.price ?? 0);
                              const raw = window.prompt(`Override price for "${svc.name}"? Leave blank to reset to default.`, cur);
                              if (raw === null) return;
                              if (raw.trim() === '') {
                                const orig = services.find(s => s.id === svc.id);
                                setBookingServices(prev => prev.map((s, i) => i === idx ? { ...s, price: orig ? (orig.price || 0) : 0, price_override: false } : s));
                                return;
                              }
                              const v = parseFloat(raw);
                              if (isNaN(v) || v < 0) { toast.error('Please enter a valid number'); return; }
                              setBookingServices(prev => prev.map((s, i) => i === idx ? { ...s, price: v, price_override: true } : s));
                            }} className={`px-1 rounded hover:bg-[#E9EDDF] hover:text-[#556B2F] transition-colors ${svc.price_override ? 'text-[#556B2F] font-semibold underline decoration-dotted' : 'text-[#5C6656] underline decoration-dotted'}`} data-testid={`edit-svc-price-${idx}`}>
                              {money(Number(svc.price ?? 0))}{svc.price_override ? ' (overridden)' : ''}
                            </button>
                          </div>
                        </div>
                        <button type="button" onClick={() => setBookingServices(prev => prev.filter((_, i) => i !== idx))} className="text-[#C0392B] hover:text-[#E74C3C] flex-shrink-0 mt-0.5" data-testid={`remove-service-${idx}`}><XIcon size={14} /></button>
                      </div>
                      {/* Per-service staff and time */}
                      <div className="flex gap-2 mt-1.5">
                        <select value={svc.staff_id || form.staff_id} onChange={(e) => {
                          setBookingServices(prev => prev.map((s, i) => i === idx ? { ...s, staff_id: e.target.value } : s));
                        }} className="flex-1 h-7 px-2 text-xs border border-[#E5E7E1] rounded bg-white text-[#1A2421]" data-testid={`svc-staff-${idx}`}>
                          {staff.filter(s => s.is_active).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <select value={svc.start_time || form.start_time} onChange={(e) => {
                          setBookingServices(prev => prev.map((s, i) => i === idx ? { ...s, start_time: e.target.value } : s));
                        }} className="w-20 h-7 px-1 text-xs border border-[#E5E7E1] rounded bg-white text-[#1A2421]" data-testid={`svc-time-${idx}`}>
                          {Array.from({ length: (END_HOUR - START_HOUR) * 12 }, (_, i) => {
                            const mins = START_HOUR * 60 + i * 5;
                            const t = `${String(Math.floor(mins/60)).padStart(2,'0')}:${String(mins%60).padStart(2,'0')}`;
                            return <option key={t} value={t}>{t}</option>;
                          })}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Add service - searchable dropdown */}
              <ServiceSearchPicker
                services={services}
                staff={staff}
                formStaffId={form.staff_id}
                bookingServices={bookingServices}
                onSelect={(svc) => {
                  // Auto-calculate start time: after last service ends
                  let autoTime = form.start_time;
                  if (bookingServices.length > 0) {
                    const lastSvc = bookingServices[bookingServices.length - 1];
                    const lastTime = lastSvc.start_time || form.start_time;
                    const [h,m] = lastTime.split(':').map(Number);
                    const nextMins = h * 60 + m + (lastSvc.duration || 30);
                    autoTime = `${String(Math.floor(nextMins / 60)).padStart(2, '0')}:${String(nextMins % 60).padStart(2, '0')}`;
                  }
                  setBookingServices(prev => [...prev, { id: svc.id, name: svc.name, duration: svc.duration || 30, price: svc.price || 0, category: svc.category || '', start_time: autoTime }]);
                  if (bookingServices.length === 0) setForm(p => ({ ...p, service_id: svc.id }));
                }}
                t={t}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('appointments.staff')}</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Select value={form.staff_id} onValueChange={v => setForm(p => ({ ...p, staff_id: v }))}>
                    <SelectTrigger data-testid="select-staff" className="border-[#E5E7E1]"><SelectValue placeholder={t('appointments.selectStaff')} /></SelectTrigger>
                    <SelectContent>{activeStaff.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <button type="button" onClick={() => setForm(p => ({ ...p, client_requested: !p.client_requested }))}
                  className={`flex-shrink-0 w-10 h-10 rounded-lg border-2 flex items-center justify-center transition-all ${form.client_requested ? 'border-[#E2725B] bg-[#FEF2F0]' : 'border-[#E5E7E1] hover:border-[#D1D5CB]'}`}
                  title={form.client_requested ? 'Client requested this staff (click to remove)' : 'Mark as client requested'}
                  data-testid="new-appt-heart">
                  <span style={{ fontSize: '18px' }}>{form.client_requested ? '❤️' : '🤍'}</span>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">{t('appointments.date')}</Label>
                <Input data-testid="appointment-date" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} required className="border-[#E5E7E1] h-9" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('appointments.time')}</Label>
                <Input data-testid="appointment-time" type="time" value={form.start_time} onChange={e => setForm(p => ({ ...p, start_time: e.target.value }))} required className="border-[#E5E7E1] h-9" />
              </div>
            </div>

            {/* Recurrence — compact */}
            <div className="flex items-center gap-2">
              <Repeat size={13} className="text-[#556B2F] flex-shrink-0" />
              <Select value={form.recurrence} onValueChange={v => setForm(p => ({ ...p, recurrence: v, recurrence_count: v === 'none' ? 0 : (p.recurrence_count || 4) }))}>
                <SelectTrigger data-testid="recurrence-select" className="border-[#E5E7E1] h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('appointments.noRepeat')}</SelectItem>
                  <SelectItem value="weekly">{t('appointments.weekly')}</SelectItem>
                  <SelectItem value="biweekly">{t('appointments.biweekly')}</SelectItem>
                  <SelectItem value="monthly">{t('appointments.monthly')}</SelectItem>
                </SelectContent>
              </Select>
              {form.recurrence !== 'none' && (
                <>
                  <Label className="text-xs text-[#5C6656] whitespace-nowrap">{t('appointments.repeatFor')}</Label>
                  <Input data-testid="recurrence-count" type="number" min="1" max="26" value={form.recurrence_count} onChange={e => setForm(p => ({ ...p, recurrence_count: parseInt(e.target.value) || 1 }))} className="w-14 h-8 text-xs border-[#E5E7E1]" />
                </>
              )}
            </div>

            <Textarea data-testid="appointment-notes" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={1} className="border-[#E5E7E1] text-sm" placeholder={t('appointments.optionalNotes')} />
            <Button data-testid="submit-appointment" type="submit" className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white" disabled={submitting || (bookingServices.length === 0 && !form.service_id)}>
              {bookingServices.length > 1 ? `${t('appointments.book')} ${bookingServices.length} ${t('appointments.services')}` : form.recurrence !== 'none' ? `${t('appointments.book')} ${1 + (form.recurrence_count || 0)} ${t('appointments.title')}` : t('appointments.bookAppointment')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pre-Checkout: Payment Method Selection */}
      <Dialog open={preCheckoutOpen} onOpenChange={setPreCheckoutOpen}>
        <DialogContent className="sm:max-w-sm border-[#E5E7E1]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>
              <Receipt size={20} className="text-[#556B2F]" /> {t('appointments.checkout')}
            </DialogTitle>
          </DialogHeader>
          {preCheckoutAppt && (
            <div className="space-y-4 mt-2">
              <div className="p-3 bg-[#F9F8F6] rounded-xl border border-[#E5E7E1]">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-[#1A2421] text-sm">{preCheckoutAppt.client_name}</p>
                  {(preCheckoutAppt.client_id === '__walk_in__' || showCheckoutClientSwap) && (
                    <button type="button" onClick={() => setShowCheckoutClientSwap(!showCheckoutClientSwap)} className="text-xs text-[#556B2F] hover:underline font-medium" data-testid="swap-client-btn">
                      {showCheckoutClientSwap ? 'Cancel' : 'Change client'}
                    </button>
                  )}
                </div>
                {showCheckoutClientSwap && (
                  <div className="mt-2 space-y-2">
                    <div className="relative">
                      <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C6656]" />
                      <Input value={checkoutClientSearch} onChange={e => searchCheckoutClients(e.target.value)} placeholder="Search client by name or phone..." className="pl-8 h-8 text-xs border-[#E5E7E1]" data-testid="checkout-client-search" autoComplete="off" />
                    </div>
                    {checkoutClientResults.length > 0 && (
                      <div className="max-h-32 overflow-y-auto border border-[#E5E7E1] rounded-lg">
                        {checkoutClientResults.map(c => (
                          <button key={c.id} type="button" onClick={() => swapWalkInClient(c)} className="w-full text-left px-3 py-2 hover:bg-[#E9EDDF] text-xs border-b border-[#E5E7E1]/50 last:border-0" data-testid={`checkout-client-${c.id}`}>
                            <p className="font-medium text-[#1A2421]">{c.name}</p>
                            <p className="text-[#5C6656]">{[c.email, c.phone].filter(Boolean).join(' · ')}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {/* Show all services in group */}
                {preCheckoutAppt._groupAppts?.length > 1 ? (
                  <div className="mt-2 space-y-1.5">
                    {preCheckoutAppt._groupAppts.map((ga, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-[#5C6656]">{ga.service_name} · {ga.staff_name}</span>
                        <span className="font-medium text-[#1A2421]">{'\u00A3'}{(ga.price||0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-[#5C6656] mt-1">{preCheckoutAppt.service_name} · {preCheckoutAppt.start_time}-{preCheckoutAppt.end_time}</p>
                )}
                <p className="text-lg font-bold text-[#556B2F] mt-1">{'\u00A3'}{preCheckoutAppt.price?.toFixed(2)}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-[#5C6656]">{t('appointments.paymentMethod')}</Label>
                {/* Collected payments list */}
                {collectedPayments.length > 0 && (
                  <div className="space-y-1.5 p-2.5 bg-[#F5F7F0] border border-[#E5E7E1] rounded-lg">
                    {collectedPayments.map((cp, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-sm text-[#1A2421] capitalize font-medium">{cp.method === 'bank_transfer' ? 'Transfer' : cp.method}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-[#3E6641]">{'\u00A3'}{(cp.tendered || cp.amount).toFixed(2)}</span>
                          <button type="button" onClick={() => setCollectedPayments(prev => prev.filter((_, i) => i !== idx))} className="text-[#E2725B] hover:text-[#C0392B]" data-testid={`remove-payment-${idx}`}><XIcon size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Left to pay */}
                {(() => {
                  const finalTotal = Math.max((preCheckoutAppt?.price || 0) - checkoutDiscount + checkoutTip, 0);
                  const totalCollected = collectedPayments.reduce((s, p) => s + p.amount, 0);
                  const leftToPay = Math.max(finalTotal - totalCollected, 0);
                  const fullyPaid = leftToPay < 0.01;
                  return (
                    <>
                      {collectedPayments.length > 0 && (
                        <div className={`px-3 py-2.5 rounded-lg ${fullyPaid ? 'bg-[#3E6641]/10 text-[#3E6641] text-sm font-bold' : 'bg-[#E2725B]/10 text-[#E2725B]'}`}>
                          {fullyPaid ? (() => {
                            const cashChange = collectedPayments.reduce((s, p) => s + (p.change || 0), 0);
                            return cashChange > 0.01
                              ? <span className="text-base font-extrabold">Change: {'\u00A3'}{cashChange.toFixed(2)}</span>
                              : 'Fully allocated';
                          })() : <span className="text-base font-extrabold">Left to pay: {'\u00A3'}{leftToPay.toFixed(2)}</span>}
                        </div>
                      )}
                      {!fullyPaid && (
                        <div className="grid grid-cols-3 gap-2">
                          {paymentMethods.filter(pm => pm !== 'voucher').map(pm => {
                            const pmLabel = pm === 'card' ? t('appointments.card') : pm === 'cash' ? t('appointments.cash') : pm === 'bank_transfer' ? 'Transfer' : pm;
                            return (
                              <button key={pm} type="button" data-testid={`payment-method-${pm}`}
                                onClick={() => {
                                  if (pm === 'cash') {
                                    setCashInputValue(leftToPay.toFixed(0));
                                    setCashDialogOpen(true);
                                  } else {
                                    setCollectedPayments(prev => [...prev, { method: pm, amount: parseFloat(leftToPay.toFixed(2)) }]);
                                  }
                                }}
                                className="px-3 py-3 rounded-xl text-sm font-medium border border-[#E5E7E1] text-[#1A2421] hover:border-[#556B2F] hover:bg-[#F5F7F0] transition-colors capitalize flex flex-col items-center gap-1.5">
                                {pm === 'cash' && <DollarSign size={18} className="text-[#556B2F]" />}
                                {pm === 'card' && <CreditCard size={18} className="text-[#556B2F]" />}
                                {pm === 'bank_transfer' && <Receipt size={18} className="text-[#556B2F]" />}
                                {pm !== 'cash' && pm !== 'card' && pm !== 'bank_transfer' && <DollarSign size={18} className="text-[#556B2F]" />}
                                {pmLabel}
                              </button>
                            );
                          })}
                          {paymentMethods.includes('voucher') && (
                            <button type="button" data-testid="payment-method-voucher"
                              onClick={() => { setCheckoutPaymentMethod('voucher'); setCollectedPayments([]); }}
                              className="px-3 py-3 rounded-xl text-sm font-medium border border-[#E5E7E1] text-[#1A2421] hover:border-[#556B2F] hover:bg-[#F5F7F0] transition-colors capitalize flex flex-col items-center gap-1.5">
                              <Gift size={18} className="text-[#556B2F]" />
                              {t('appointments.voucher')}
                            </button>
                          )}
                          <button type="button" data-testid="payment-method-paylink"
                            disabled={!licence?.paylink_eligible}
                            title={
                              !licence?.paylink_whitelisted
                                ? 'PayLink is in early access — not available for your account yet.'
                                : !licence?.stripe_connected
                                ? 'Connect your Stripe account in Settings → Payments to enable PayLink.'
                                : ''
                            }
                            onClick={() => {
                              if (!licence?.paylink_eligible) {
                                if (!licence?.paylink_whitelisted) {
                                  toast.error('PayLink is in early access — contact hello@apptio.app for access.');
                                } else if (!licence?.stripe_connected) {
                                  toast.error('Connect your Stripe account first (Settings → Payments).');
                                }
                                return;
                              }
                              setPayLinkEmail(clientDetail?.email || '');
                              setPayLinkPhone(clientDetail?.phone || '');
                              setPayLinkResult(null);
                              setPayLinkPanel('compose');
                            }}
                            className={`px-3 py-3 rounded-xl text-sm font-medium border transition-colors capitalize flex flex-col items-center gap-1.5 col-span-3 sm:col-span-1 ${
                              licence?.paylink_eligible
                                ? 'border-[#556B2F] text-[#556B2F] bg-[#E9EDDF] hover:bg-[#DBE3CD] cursor-pointer'
                                : 'border-[#E5E7E1] text-[#B0B5A8] bg-[#F9F8F6] cursor-not-allowed opacity-60'
                            }`}>
                            <Send size={18} className={licence?.paylink_eligible ? 'text-[#556B2F]' : 'text-[#B0B5A8]'} />
                            <span className="leading-tight text-center">
                              PayLink
                              <span className="block text-[10px] font-normal text-[#5C6656]">
                                {licence?.paylink_eligible
                                  ? 'Apple/Google Pay'
                                  : !licence?.paylink_whitelisted
                                    ? 'Coming soon'
                                    : 'Connect Stripe'}
                              </span>
                            </span>
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Cash Numpad Dialog */}
              <Dialog open={cashDialogOpen} onOpenChange={setCashDialogOpen}>
                <DialogContent className="sm:max-w-[320px] border-[#E5E7E1] p-0 overflow-hidden">
                  <div className="p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Add cash amount</h3>
                    </div>
                    <div className="text-center py-3">
                      <span className="text-3xl font-bold text-[#1A2421]">{'\u00A3'} <span className="border-b-2 border-[#556B2F] pb-0.5">{cashInputValue || '0'}</span></span>
                    </div>
                    {/* Quick amount buttons */}
                    {(() => {
                      const finalTotal = Math.max((preCheckoutAppt?.price || 0) - checkoutDiscount + checkoutTip, 0);
                      const totalCollected = collectedPayments.reduce((s, p) => s + p.amount, 0);
                      const leftToPay = Math.max(finalTotal - totalCollected, 0);
                      const quickAmounts = [leftToPay, ...[5,10,20,50].map(n => Math.ceil(leftToPay / n) * n).filter(v => v > leftToPay)].filter((v, i, a) => a.indexOf(v) === i).slice(0, 5);
                      return (
                        <>
                          <div className="flex gap-2 justify-center flex-wrap">
                            {quickAmounts.map(q => (
                              <button key={q} type="button" onClick={() => setCashInputValue(q.toFixed(0))}
                                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${cashInputValue === q.toFixed(0) ? 'bg-[#556B2F] text-white border-[#556B2F]' : 'border-[#E5E7E1] text-[#1A2421] hover:border-[#556B2F]'}`}>
                                {'\u00A3'}{q.toFixed(0)}
                              </button>
                            ))}
                          </div>
                          {/* Numpad */}
                          <div className="grid grid-cols-3 gap-2">
                            {['1','2','3','4','5','6','7','8','9','.','0','del'].map(k => (
                              <button key={k} type="button" data-testid={`numpad-${k}`}
                                onClick={() => {
                                  if (k === 'del') setCashInputValue(prev => prev.slice(0, -1));
                                  else if (k === '.') { if (!cashInputValue.includes('.')) setCashInputValue(prev => (prev || '0') + '.'); }
                                  else setCashInputValue(prev => prev === '0' ? k : prev + k);
                                }}
                                className="h-12 rounded-xl text-lg font-semibold border border-[#E5E7E1] text-[#1A2421] hover:bg-[#F5F7F0] active:bg-[#E9EDDF] transition-colors flex items-center justify-center">
                                {k === 'del' ? <ChevronLeft size={20} /> : k}
                              </button>
                            ))}
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t border-[#E5E7E1]">
                            {(() => {
                              const cashAmt = parseFloat(cashInputValue) || 0;
                              const remaining = leftToPay - cashAmt;
                              if (remaining > 0) return <span className="text-sm text-[#5C6656]">Left to pay · {'\u00A3'}{remaining.toFixed(2)}</span>;
                              if (remaining < -0.001) return <span className="text-sm font-bold text-[#3E6641]">Change · {'\u00A3'}{Math.abs(remaining).toFixed(2)}</span>;
                              return <span className="text-sm text-[#3E6641]">Exact amount</span>;
                            })()}
                            <Button onClick={() => {
                              const amount = parseFloat(cashInputValue) || 0;
                              if (amount <= 0) { toast.error('Enter an amount'); return; }
                              const addAmount = Math.min(amount, leftToPay);
                              const change = Math.max(amount - leftToPay, 0);
                              setCollectedPayments(prev => [...prev, { method: 'cash', amount: parseFloat(addAmount.toFixed(2)), tendered: parseFloat(amount.toFixed(2)), change: parseFloat(change.toFixed(2)) }]);
                              setCashDialogOpen(false);
                              setCashInputValue('');
                            }} className="bg-[#1A2421] hover:bg-[#0D1210] text-white rounded-full px-6 h-10 text-sm font-bold" data-testid="add-cash-btn">Add</Button>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </DialogContent>
              </Dialog>

              {checkoutPaymentMethod === 'voucher' && (
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-[#5C6656]">{t('appointments.voucherCode')}</Label>
                  <div className="flex gap-2">
                    <Input value={checkoutVoucherCode} onChange={e => setCheckoutVoucherCode(e.target.value.toUpperCase())} placeholder="Enter code" className="border-[#E5E7E1] h-9 text-sm font-mono" data-testid="voucher-code-input" />
                    <Button variant="outline" className="border-[#E5E7E1] h-9 text-sm" data-testid="verify-voucher-btn"
                      onClick={async () => {
                        try {
                          const { data } = await axios.post(`${API}/vouchers/verify`, { code: checkoutVoucherCode }, { withCredentials: true });
                          setCheckoutVoucherInfo(data);
                          if (data.valid) toast.success(`Valid! Balance: \u00A3${data.remaining.toFixed(2)}`);
                          else toast.error(data.reason || 'Invalid');
                        } catch (err) { toast.error(err.response?.data?.detail || 'Invalid code'); setCheckoutVoucherInfo(null); }
                      }}>{t('appointments.verify')}</Button>
                  </div>
                  {checkoutVoucherInfo?.valid && (
                    <p className="text-xs text-[#3E6641] font-medium">Balance: {'\u00A3'}{checkoutVoucherInfo.remaining.toFixed(2)}</p>
                  )}
                  {checkoutVoucherInfo && !checkoutVoucherInfo.valid && (
                    <p className="text-xs text-[#E2725B] font-medium">{checkoutVoucherInfo.reason}</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-[#5C6656]">{t('appointments.discountLabel')} ({'\u00A3'})</Label>
                  <Input type="number" min="0" step="0.5" value={checkoutDiscount} onChange={e => setCheckoutDiscount(parseFloat(e.target.value) || 0)} className="border-[#E5E7E1] h-9" data-testid="checkout-discount" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#5C6656]">{t('appointments.tipLabel')} ({'\u00A3'})</Label>
                  <Input type="number" min="0" step="0.5" value={checkoutTip} onChange={e => setCheckoutTip(parseFloat(e.target.value) || 0)} className="border-[#E5E7E1] h-9" data-testid="checkout-tip" />
                </div>
              </div>

              <div className="p-3 bg-[#E9EDDF] rounded-lg">
                <div className="flex justify-between text-sm"><span className="text-[#5C6656]">{t('appointments.subtotal')}</span><span>{'\u00A3'}{preCheckoutAppt.price?.toFixed(2)}</span></div>
                {checkoutDiscount > 0 && <div className="flex justify-between text-sm text-[#E2725B]"><span>{t('appointments.discountLabel')}</span><span>-{'\u00A3'}{checkoutDiscount.toFixed(2)}</span></div>}
                {checkoutTip > 0 && <div className="flex justify-between text-sm text-[#3E6641]"><span>{t('appointments.tipLabel')}</span><span>+{'\u00A3'}{checkoutTip.toFixed(2)}</span></div>}
                <div className="flex justify-between font-bold text-lg mt-1 pt-1 border-t border-[#556B2F]/20">
                  <span>{t('appointments.total')}</span>
                  <span className="text-[#556B2F]">{'\u00A3'}{Math.max((preCheckoutAppt.price || 0) - checkoutDiscount + checkoutTip, 0).toFixed(2)}</span>
                </div>
              </div>

              <Button onClick={handleCheckout} disabled={(() => {
                const ft = Math.max((preCheckoutAppt?.price || 0) - checkoutDiscount + checkoutTip, 0);
                // £0 total (e.g. fully discounted) — always allow checkout
                if (ft <= 0.005) return false;
                return collectedPayments.length === 0 && checkoutPaymentMethod !== 'voucher';
              })()} className="w-full bg-[#3E6641] hover:bg-[#2D5230] text-white py-5 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="confirm-checkout-btn">
                <Check size={16} className="mr-2" /> {(() => {
                  const ft = Math.max((preCheckoutAppt?.price || 0) - checkoutDiscount + checkoutTip, 0);
                  if (ft <= 0.005) return 'Complete (Free)';
                  const tc = collectedPayments.reduce((s, p) => s + p.amount, 0);
                  return (collectedPayments.length > 0 && (ft - tc) > 0.01) ? 'Save part-paid' : 'Complete Checkout';
                })()}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PayLink Dialog */}
      <Dialog open={!!payLinkPanel} onOpenChange={(o) => { if (!o) { setPayLinkPanel(false); setPayLinkResult(null); } }}>
        <DialogContent className="sm:max-w-md border-[#E5E7E1] p-0 overflow-hidden" data-testid="paylink-dialog">
          <div className="px-5 pt-5 pb-3 border-b border-[#E5E7E1] flex items-center gap-2">
            <Send size={18} className="text-[#556B2F]" />
            <h2 className="text-base font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Apptio PayLink</h2>
            <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-white bg-[#556B2F] px-2 py-0.5 rounded">Apple/Google Pay</span>
          </div>

          {payLinkPanel === 'compose' && (
            <div className="p-5 space-y-4">
              <div className="bg-[#F5F7F0] rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#5C6656]">Amount</p>
                  <p className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{'\u00A3'}{Math.max((preCheckoutAppt?.price || 0) - checkoutDiscount + checkoutTip, 0).toFixed(2)}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#5C6656]">Customer</p>
                  <p className="text-sm font-medium text-[#1A2421]">{preCheckoutAppt?.client_name || 'Walk-in'}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold text-[#1A2421]">Send via</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setPayLinkChannels(c => ({ ...c, email: !c.email }))}
                    className={`px-3 py-2.5 rounded-xl border-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${payLinkChannels.email ? 'border-[#556B2F] bg-[#E9EDDF] text-[#1A2421]' : 'border-[#E5E7E1] text-[#5C6656]'}`}
                    data-testid="paylink-channel-email">
                    <Mail size={14} /> Email
                  </button>
                  <button type="button" onClick={() => setPayLinkChannels(c => ({ ...c, sms: !c.sms }))}
                    className={`px-3 py-2.5 rounded-xl border-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${payLinkChannels.sms ? 'border-[#556B2F] bg-[#E9EDDF] text-[#1A2421]' : 'border-[#E5E7E1] text-[#5C6656]'}`}
                    data-testid="paylink-channel-sms">
                    <Phone size={14} /> SMS
                  </button>
                </div>
              </div>

              {payLinkChannels.email && (
                <div className="space-y-1">
                  <Label className="text-xs font-bold text-[#1A2421]">Email address</Label>
                  <Input type="email" value={payLinkEmail} onChange={e => setPayLinkEmail(e.target.value)} placeholder="customer@email.com" className="border-[#E5E7E1] h-10" data-testid="paylink-email-input" />
                </div>
              )}

              {payLinkChannels.sms && (
                <div className="space-y-1">
                  <Label className="text-xs font-bold text-[#1A2421]">Mobile number</Label>
                  <Input type="tel" value={payLinkPhone} onChange={e => setPayLinkPhone(e.target.value)} placeholder="+447..." className="border-[#E5E7E1] h-10" data-testid="paylink-phone-input" />
                  <p className="text-[10px] text-[#5C6656]">Twilio required — leave empty if not configured</p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1 border-[#E5E7E1] text-[#1A2421]" onClick={() => setPayLinkPanel(false)}>Cancel</Button>
                <Button onClick={() => {
                  const channels = [];
                  if (payLinkChannels.email && payLinkEmail.trim()) channels.push('email');
                  if (payLinkChannels.sms && payLinkPhone.trim()) channels.push('sms');
                  sendPayLink(channels);
                }} disabled={payLinkSending} className="flex-1 bg-[#556B2F] hover:bg-[#4A5D23] text-white" data-testid="paylink-send-btn">
                  {payLinkSending ? 'Sending…' : 'Send PayLink'}
                </Button>
              </div>
            </div>
          )}

          {payLinkPanel === 'sent' && payLinkResult && (
            <div className="p-5 space-y-4">
              {/* Status badge */}
              <div className="flex items-center justify-center">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${payLinkResult.status === 'paid' ? 'bg-[#E9EDDF] text-[#556B2F]' : payLinkResult.status === 'pending' ? 'bg-[#FEF6E4] text-[#D4A373]' : 'bg-[#FDE8E8] text-[#E2725B]'}`} data-testid="paylink-status-badge">
                  {payLinkResult.status === 'pending' && <span className="w-2 h-2 rounded-full bg-[#D4A373] animate-pulse"></span>}
                  {payLinkResult.status === 'paid' ? '✓ Paid' : payLinkResult.status === 'pending' ? 'Waiting for payment…' : payLinkResult.status}
                </div>
              </div>

              {/* QR code */}
              <div className="flex flex-col items-center gap-2">
                <div className="p-3 bg-white rounded-xl border-2 border-[#E5E7E1]">
                  <img alt="PayLink QR" className="w-48 h-48"
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=10&data=${encodeURIComponent(payLinkResult.url)}`}
                    data-testid="paylink-qr" />
                </div>
                <p className="text-xs text-[#5C6656] text-center">Scan with phone camera to pay</p>
                <p className="text-[10px] text-[#556B2F] text-center bg-[#E9EDDF] rounded-full px-3 py-1 mt-1" data-testid="paylink-wallets-hint">
                  Customer can pay with  Pay / G Pay / card
                </p>
              </div>

              {/* Channels result */}
              {payLinkResult.channels_sent?.length > 0 && (
                <div className="bg-[#F5F7F0] rounded-xl p-3 space-y-1 text-xs">
                  {payLinkResult.send_results?.email && (
                    <div className="flex items-center gap-2">
                      <Mail size={12} />
                      <span className="text-[#1A2421] flex-1">{payLinkResult.send_results.email.to}</span>
                      <span className={payLinkResult.send_results.email.ok ? 'text-[#556B2F]' : 'text-[#E2725B]'}>
                        {payLinkResult.send_results.email.ok ? '✓ sent' : '✗ failed'}
                      </span>
                    </div>
                  )}
                  {payLinkResult.send_results?.sms && (
                    <div className="flex items-center gap-2">
                      <Phone size={12} />
                      <span className="text-[#1A2421] flex-1">{payLinkResult.send_results.sms.to}</span>
                      <span className={payLinkResult.send_results.sms.ok ? 'text-[#556B2F]' : 'text-[#E2725B]'}>
                        {payLinkResult.send_results.sms.ok ? '✓ sent' : '✗ failed'}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Copy link */}
              <button type="button" onClick={() => { navigator.clipboard?.writeText(payLinkResult.url); toast.success('Link copied'); }}
                className="w-full text-xs text-[#556B2F] underline" data-testid="paylink-copy-btn">Copy link</button>

              <div className="flex gap-2">
                {payLinkResult.status === 'pending' && (
                  <Button variant="outline" className="flex-1 border-[#E2725B]/30 text-[#E2725B] hover:bg-[#E2725B]/10" onClick={cancelPayLink} data-testid="paylink-cancel-btn">
                    Cancel & take cash
                  </Button>
                )}
                <Button className="flex-1 bg-[#1A2421] hover:bg-[#0D1210] text-white" onClick={() => setPayLinkPanel(false)} data-testid="paylink-close-btn">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Part-paid confirmation dialog */}
      <Dialog open={partPaidConfirm} onOpenChange={setPartPaidConfirm}>
        <DialogContent className="sm:max-w-sm border-[#E5E7E1]">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Part-paid sale</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5C6656] mt-1">Full payment has not been added for this sale. You can save it as part-paid then add additional payment at anytime in the future.</p>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1 border-[#E5E7E1]" onClick={() => setPartPaidConfirm(false)} data-testid="part-paid-cancel">Cancel</Button>
            <Button className="flex-1 bg-[#1A2421] hover:bg-[#2D3B2D] text-white" onClick={handleCheckout} data-testid="part-paid-continue">Continue</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Checkout / Receipt / Rebook Dialog */}
      <Dialog open={checkoutDialog} onOpenChange={setCheckoutDialog}>
        <DialogContent className="sm:max-w-md border-[#E5E7E1]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>
              <Receipt size={20} className="text-[#556B2F]" /> Checkout
            </DialogTitle>
          </DialogHeader>
          {checkoutReceipt && (
            <div className="space-y-4 mt-2">
              {/* Part-paid badge */}
              {checkoutReceipt.payment_status === 'partial' && (
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#E2725B] text-white">Part-paid</span>
                  <span className="text-xs text-[#5C6656]">Balance: {'\u00A3'}{(checkoutReceipt.balance_due || 0).toFixed(2)}</span>
                </div>
              )}
              {/* Receipt */}
              <div className="p-4 bg-white rounded-xl border-2 border-[#E5E7E1]" data-testid="receipt-card">
                <div className="text-center mb-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#5C6656]">{t('appointments.receiptTitle')}</p>
                </div>
                <Separator className="bg-[#E5E7E1] mb-3" />
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[#5C6656]">{t('appointments.client')}</span><span className="font-medium text-[#1A2421]">{checkoutReceipt.client_name}</span></div>
                  <div className="flex justify-between"><span className="text-[#5C6656]">{t('appointments.service')}</span><span className="font-medium text-[#1A2421]">{checkoutReceipt.service_name}</span></div>
                  <div className="flex justify-between"><span className="text-[#5C6656]">{t('appointments.staff')}</span><span className="font-medium text-[#1A2421]">{checkoutReceipt.staff_name}</span></div>
                  <div className="flex justify-between"><span className="text-[#5C6656]">{t('appointments.date')}</span><span className="font-medium text-[#1A2421]">{checkoutReceipt.date}</span></div>
                  <div className="flex justify-between"><span className="text-[#5C6656]">{t('appointments.time')}</span><span className="font-medium text-[#1A2421]">{checkoutReceipt.time}</span></div>
                  <div className="flex justify-between"><span className="text-[#5C6656]">{t('appointments.paymentMethod')}</span><span className="font-medium text-[#1A2421] capitalize">{checkoutReceipt.payment_method}</span></div>
                </div>
                <Separator className="bg-[#E5E7E1] my-3" />
                <div className="space-y-1">
                  {checkoutReceipt.discount > 0 && <div className="flex justify-between text-sm"><span className="text-[#E2725B]">{t('appointments.discountLabel')}</span><span className="text-[#E2725B]">-{'\u00A3'}{checkoutReceipt.discount?.toFixed(2)}</span></div>}
                  {checkoutReceipt.tip > 0 && <div className="flex justify-between text-sm"><span className="text-[#3E6641]">{t('appointments.tipLabel')}</span><span className="text-[#3E6641]">+{'\u00A3'}{checkoutReceipt.tip?.toFixed(2)}</span></div>}
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-[#1A2421]">{t('appointments.total')}</span>
                    <span className="text-2xl font-bold text-[#556B2F]" style={{ fontFamily: 'Outfit' }}>{'\u00A3'}{checkoutReceipt.final_total?.toFixed(2)}</span>
                  </div>
                  {checkoutReceipt.payment_status === 'partial' && (
                    <>
                      <div className="flex justify-between text-sm"><span className="text-[#5C6656]">Paid</span><span className="font-medium text-[#3E6641]">{'\u00A3'}{(checkoutReceipt.amount_paid || 0).toFixed(2)}</span></div>
                      <div className="flex justify-between text-sm"><span className="font-bold text-[#E2725B]">Balance</span><span className="font-bold text-[#E2725B]">{'\u00A3'}{(checkoutReceipt.balance_due || 0).toFixed(2)}</span></div>
                    </>
                  )}
                </div>
              </div>

              {/* Send Receipt */}
              {receiptSent ? (
                <div className="flex items-center justify-center gap-2 py-2 text-sm text-[#3E6641] font-medium"><Check size={14} /> Receipt Sent</div>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input value={receiptEmail} onChange={e => setReceiptEmail(e.target.value)} placeholder="Client email" type="email" inputMode="email" className="border-[#E5E7E1] flex-1 text-sm" style={{ fontSize: '16px' }} data-testid="receipt-email-input" />
                    <Button onClick={() => handleSendReceipt(receiptEmail || undefined)} disabled={!!(receiptEmail && !/\S+@\S+\.\S+/.test(receiptEmail))} variant="outline" className="border-[#556B2F] text-[#556B2F] flex-shrink-0" data-testid="send-receipt-btn">
                      <Send size={14} className="mr-1.5" /> Send
                    </Button>
                  </div>
                  <p className="text-[10px] text-[#5C6656] text-center">Leave blank to use client's email on file</p>
                </div>
              )}

              {/* Google Review */}
              {googleReviewUrl && (
                <Button variant="outline" className="w-full border-[#D4A373] text-[#D4A373] hover:bg-[#FDF3E7]" onClick={() => window.open(googleReviewUrl, '_blank')} data-testid="google-review-btn">
                  <ExternalLink size={14} className="mr-2" /> Ask client to leave a Google Review
                </Button>
              )}

              {/* Rebook Section */}
              <div className="p-4 bg-[#F9F8F6] rounded-xl border border-[#E5E7E1] space-y-3">
                <div className="flex items-center gap-2">
                  <CalendarDays size={16} className="text-[#556B2F]" />
                  <p className="font-medium text-[#1A2421] text-sm" style={{ fontFamily: 'Outfit' }}>{t('appointments.bookNextVisit')}</p>
                </div>
                <p className="text-xs text-[#5C6656]">{t('appointments.bookNextDesc')}</p>
                <Button onClick={() => {
                  const rb = { client_id: checkoutAppt.client_id, client_name: checkoutAppt.client_name, service_id: checkoutAppt.service_id, service_name: checkoutAppt.service_name, staff_id: checkoutAppt.staff_id, staff_name: checkoutAppt.staff_name, price: checkoutAppt.price, duration: checkoutAppt.duration, location_id: checkoutAppt.location_id || '' };
                  setCheckoutDialog(false);
                  setRebookMode(rb);
                }} className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white" data-testid="confirm-rebook-btn">
                  <RefreshCw size={14} className="mr-2" /> Book Next Visit
                </Button>
              </div>

      <Button onClick={() => setCheckoutDialog(false)} variant="ghost" className="w-full text-[#5C6656]">{t('appointments.close')}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== ADD SALE SIDEBAR ===== */}
      {saleSidebar && (
        <div className="fixed inset-0 z-[60] flex justify-end" data-testid="sale-sidebar">
          <div className="absolute inset-0 bg-black/20" onClick={() => setSaleSidebar(false)} />
          <div className="relative w-[420px] max-w-full bg-white shadow-2xl border-l border-[#E5E7E1] flex flex-col h-[100dvh] animate-in slide-in-from-right">
            <div className="p-4 border-b border-[#E5E7E1] flex items-center justify-between">
              <h2 className="text-lg font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('calendar.addSale')}</h2>
              <button onClick={() => setSaleSidebar(false)} className="text-[#5C6656] hover:text-[#1A2421]"><XIcon size={18} /></button>
            </div>
            {!salePayStep ? (
              <div className="flex-1 overflow-y-auto">
                {/* Client selector */}
                <div className="p-4 border-b border-[#E5E7E1]">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#5C6656] mb-2">{t('appointments.client')}</p>
                  {saleClient ? (
                    <div className="flex items-center gap-2"><div className="w-8 h-8 rounded-full bg-[#E9EDDF] flex items-center justify-center text-sm font-bold text-[#556B2F]">{saleClient.name?.charAt(0)}</div><div className="flex-1"><p className="text-sm font-medium text-[#1A2421]">{saleClient.name}</p><p className="text-[11px] text-[#5C6656]">{saleClient.email}</p></div><button onClick={() => setSaleClient(null)} className="text-[#5C6656] hover:text-[#E2725B]"><XIcon size={14} /></button></div>
                  ) : (
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6656]" />
                      <Input placeholder={t('appointments.searchClient')} value={saleClientSearch} onChange={async (e) => {
                        setSaleClientSearch(e.target.value);
                        if (e.target.value.length >= 2) {
                          const { data } = await axios.get(`${API}/clients?search=${encodeURIComponent(e.target.value)}`, { withCredentials: true }).catch(() => ({ data: { clients: [] } }));
                          setSaleClientResults(data.clients || data || []);
                        } else { setSaleClientResults([]); }
                      }} className="pl-9 border-[#E5E7E1] h-9 text-sm" data-testid="sale-client-search" />
                      {saleClientResults.length > 0 && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7E1] rounded-lg shadow-lg max-h-36 overflow-auto">
                          {saleClientResults.map(c => (
                            <button key={c.id} onClick={() => { setSaleClient(c); setSaleClientSearch(''); setSaleClientResults([]); }} className="w-full text-left px-3 py-2 hover:bg-[#E9EDDF] text-sm border-b border-[#E5E7E1]/50 last:border-0">{c.name} <span className="text-[#5C6656]">{c.email}</span></button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Tabs: Services / Gift Cards */}
                <div className="flex border-b border-[#E5E7E1]">
                  {[{ key: 'services', label: 'Services' }, { key: 'giftcard', label: 'Gift Cards' }].map(t => (
                    <button key={t.key} onClick={() => setSaleTab(t.key)}
                      className={`flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors ${saleTab === t.key ? 'border-[#556B2F] text-[#1A2421]' : 'border-transparent text-[#5C6656]'}`}
                      data-testid={`sale-tab-${t.key}`}>{t.label}</button>
                  ))}
                </div>
                {saleTab === 'services' && (
                  <div className="p-3 space-y-1">
                    {services.map(svc => (
                      <button key={svc.id} onClick={() => setSaleCart(prev => [...prev, { type: 'service', id: svc.id, name: svc.name, price: svc.price || 0 }])}
                        className="w-full text-left flex items-center justify-between px-3 py-2.5 rounded-lg border border-[#E5E7E1] hover:border-[#556B2F]/30 transition-colors" data-testid={`sale-svc-${svc.id}`}>
                        <div><p className="text-sm font-medium text-[#1A2421]">{svc.name}</p><p className="text-[11px] text-[#5C6656]">{svc.duration}min</p></div>
                        <span className="text-sm font-medium text-[#556B2F]">{'\u00A3'}{(svc.price||0).toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                )}
                {saleTab === 'giftcard' && (
                  <div className="p-4 space-y-3">
                    <p className="text-xs text-[#5C6656]">{t('appointments.giftCardCreate')}</p>
                    <div className="space-y-2">
                      <div className="space-y-1"><Label className="text-xs">{t('appointments.giftCardValue')}</Label>
                        <Input type="number" min="1" step="0.01" value={saleGiftValue} onChange={e => setSaleGiftValue(e.target.value)} placeholder="e.g. 50.00" className="border-[#E5E7E1] h-9 text-sm" data-testid="gift-value" /></div>
                      <div className="space-y-1"><Label className="text-xs">{t('appointments.recipientName')}</Label>
                        <Input value={saleGiftRecipient} onChange={e => setSaleGiftRecipient(e.target.value)} placeholder="Who is it for?" className="border-[#E5E7E1] h-9 text-sm" data-testid="gift-recipient" /></div>
                      <div className="space-y-1"><Label className="text-xs">{t('appointments.recipientEmail')}</Label>
                        <Input type="email" value={saleGiftEmail} onChange={e => setSaleGiftEmail(e.target.value)} placeholder="Send e-voucher to..." className="border-[#E5E7E1] h-9 text-sm" data-testid="gift-email" /></div>
                      <Button onClick={() => {
                        const val = parseFloat(saleGiftValue);
                        if (!val || val <= 0) { toast.error('Enter a valid value'); return; }
                        setSaleCart(prev => [...prev, { type: 'giftcard', name: `Gift Card ${'\u00A3'}${val.toFixed(2)}`, price: val, recipient: saleGiftRecipient, email: saleGiftEmail }]);
                        setSaleGiftValue(''); setSaleGiftRecipient(''); setSaleGiftEmail('');
                        toast.success('Gift card added to cart');
                      }} className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white h-9 text-sm" data-testid="add-gift-btn">{t('appointments.addToCart')}</Button>
                    </div>
                  </div>
                )}
                {/* Cart */}
                {saleCart.length > 0 && (
                  <div className="p-4 border-t border-[#E5E7E1]">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#5C6656] mb-2">{t('appointments.cart')} ({saleCart.length})</p>
                    {saleCart.map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-[#E5E7E1] last:border-0">
                        <div className="flex items-center gap-2">
                          {item.type === 'giftcard' && <Gift size={14} className="text-[#556B2F]" />}
                          <p className="text-sm text-[#1A2421]">{item.name}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[#1A2421]">{'\u00A3'}{item.price.toFixed(2)}</span>
                          <button onClick={() => setSaleCart(prev => prev.filter((_, idx) => idx !== i))} className="text-[#5C6656] hover:text-[#E2725B]"><XIcon size={14} /></button>
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between pt-3 font-bold text-[#1A2421]">
                      <span>{t('appointments.total')}</span>
                      <span>{'\u00A3'}{saleCart.reduce((s, i) => s + i.price, 0).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <h3 className="text-sm font-bold text-[#1A2421]">{t('appointments.checkout')}</h3>
                <div className="space-y-2">
                  {saleCart.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm"><span className="text-[#5C6656]">{item.name}</span><span className="text-[#1A2421] font-medium">{'\u00A3'}{item.price.toFixed(2)}</span></div>
                  ))}
                  <div className="flex justify-between pt-2 border-t border-[#E5E7E1] font-bold text-[#1A2421]"><span>{t('appointments.total')}</span><span>{'\u00A3'}{saleCart.reduce((s, i) => s + i.price, 0).toFixed(2)}</span></div>
                </div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#5C6656]">{t('appointments.paymentMethod')}</p>
                <div className="grid grid-cols-2 gap-2">
                  {['cash', 'card', 'bank_transfer', 'other'].map(m => (
                    <Button key={m} variant="outline" className="border-[#E5E7E1] capitalize text-sm h-10" data-testid={`pay-${m}`}
                      onClick={async () => {
                        try {
                          const createdVouchers = [];
                          for (const item of saleCart) {
                            if (item.type === 'giftcard') {
                              const { data } = await axios.post(`${API}/vouchers`, {
                                value: item.price, client_id: saleClient?.id || '', client_name: saleClient?.name || 'Walk-in',
                                client_email: saleClient?.email || '', recipient_name: item.recipient || saleClient?.name || '', recipient_email: item.email || saleClient?.email || '',
                                payment_method: m
                              }, { withCredentials: true });
                              createdVouchers.push({ code: data.code, value: data.value, id: data.id, recipient: data.owner_name, email: data.owner_email });
                            }
                          }
                          if (createdVouchers.length > 0) {
                            setSaleComplete({ vouchers: createdVouchers });
                            setSaleEmailTo(createdVouchers[0]?.email || '');
                          } else {
                            toast.success('Sale completed!');
                            setSaleSidebar(false); setSaleCart([]); setSaleClient(null); setSalePayStep(false);
                          }
                        } catch (err) { toast.error(err.response?.data?.detail || 'Sale failed'); }
                      }}>{m.replace('_', ' ')}</Button>
                  ))}
                </div>
                <Button variant="ghost" className="w-full text-[#5C6656]" onClick={() => setSalePayStep(false)}>{t('common.back')}</Button>
              </div>
            )}
            {/* Voucher confirmation screen */}
            {saleComplete && (
              <div className="flex-1 overflow-y-auto p-4 space-y-4 text-center" data-testid="sale-voucher-confirm">
                <div className="w-12 h-12 rounded-full bg-[#E9EDDF] flex items-center justify-center mx-auto"><Check size={24} className="text-[#3E6641]" /></div>
                <h3 className="text-lg font-bold text-[#1A2421]">{t('sales.saleComplete')}</h3>
                {saleComplete.vouchers.map((v, i) => (
                  <div key={i} className="bg-gradient-to-br from-[#556B2F] to-[#3E6641] rounded-xl p-5 text-white" data-testid={`voucher-card-${i}`}>
                    <p className="text-xs opacity-70 mb-1">{t('sales.giftCard')}</p>
                    <p className="text-3xl font-extrabold">{'\u00A3'}{v.value.toFixed(2)}</p>
                    {v.recipient && <p className="text-sm opacity-80 mt-1">For {v.recipient}</p>}
                    <div className="bg-white/20 rounded-lg px-4 py-2 mt-3 inline-block">
                      <p className="text-[10px] opacity-60">{t('appointments.voucherCode')}</p>
                      <p className="text-xl font-extrabold tracking-[4px]" data-testid={`voucher-code-display-${i}`}>{v.code}</p>
                    </div>
                  </div>
                ))}
                <div className="space-y-2 text-left">
                  <Label className="text-xs text-[#5C6656]">{t('sales.sendVoucherEmail')}</Label>
                  <div className="flex gap-2">
                    <Input type="email" value={saleEmailTo} onChange={e => setSaleEmailTo(e.target.value)} placeholder="Recipient email" className="border-[#E5E7E1] h-9 text-sm flex-1" data-testid="voucher-email-input" />
                    <Button className="bg-[#556B2F] hover:bg-[#4A5D23] text-white h-9 text-sm px-4" data-testid="send-voucher-email-btn"
                      onClick={async () => {
                        if (!saleEmailTo) { toast.error('Enter an email'); return; }
                        try {
                          for (const v of saleComplete.vouchers) {
                            await axios.post(`${API}/vouchers/${v.id}/email`, { email: saleEmailTo }, { withCredentials: true });
                          }
                          toast.success(`Voucher emailed to ${saleEmailTo}`);
                        } catch { toast.error('Failed to send email'); }
                      }}><Send size={14} className="mr-1" /> Send</Button>
                  </div>
                </div>
                <Button onClick={() => { setSaleSidebar(false); setSaleCart([]); setSaleClient(null); setSalePayStep(false); setSaleComplete(null); }}
                  className="w-full bg-[#1A2421] hover:bg-[#2D3B36] text-white h-10 mt-2" data-testid="done-sale-btn">{t('sales.done')}</Button>
              </div>
            )}
            {/* Bottom action — sticky above the mobile tab-bar with safe-area padding.
                On phone viewports this must stay reachable even when the browser hides its URL
                bar dynamically; the h-[100dvh] outer wrapper + this padding keep the button visible. */}
            {!salePayStep && saleCart.length > 0 && (
              <div className="p-4 border-t border-[#E5E7E1] bg-white flex-shrink-0" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1rem)' }}>
                <Button onClick={() => setSalePayStep(true)} className="w-full bg-[#1A2421] hover:bg-[#2D3B36] text-white h-11" data-testid="proceed-payment-btn">
                  Proceed to payment · {'\u00A3'}{saleCart.reduce((s, i) => s + i.price, 0).toFixed(2)}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Notification Panel (Fresha-style) */}
      {/* Fresha-style Cancellation Dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={() => setCancelDialog(null)}>
        <DialogContent className="sm:max-w-xl border-[#E5E7E1] p-0">
          <div className="p-6">
            <button onClick={() => setCancelDialog(null)} className="absolute top-4 right-4 px-4 py-1.5 rounded-full border border-[#E5E7E1] text-sm text-[#1A2421] hover:bg-[#F5F7F0]">Close</button>
            <h2 className="text-2xl font-bold text-[#1A2421] mt-2" style={{ fontFamily: 'Outfit' }}>Are you sure you want to cancel?</h2>
            {cancelDialog && (
              <div className="mt-6 flex gap-6 flex-col sm:flex-row">
                <div className="flex-1 space-y-4">
                  <div className="bg-[#F3F0FF] border border-[#E0D8FF] rounded-lg px-4 py-3 flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full border-2 border-[#7C6BC4] flex items-center justify-center flex-shrink-0"><span className="text-[#7C6BC4] text-xs font-bold">i</span></div>
                    <span className="text-sm text-[#1A2421]">No policy was applied to this appointment</span>
                  </div>
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={cancelNotify} onChange={e => setCancelNotify(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-[#E5E7E1] text-[#556B2F] accent-[#556B2F]" />
                    <div>
                      <p className="text-sm text-[#1A2421]">Send a cancellation notification</p>
                      <p className="text-xs text-[#5C6656]">{cancelDialog.source === 'online' ? 'Online bookings always receive cancellation notifications' : 'Optional for manual bookings'}</p>
                    </div>
                  </label>
                  <div>
                    <p className="text-sm font-bold text-[#1A2421] mb-2">Cancellation reason</p>
                    <select value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                      className="w-full h-10 border border-[#E5E7E1] rounded-lg px-3 text-sm text-[#1A2421] bg-white" data-testid="cancel-reason-select">
                      <option value="No reason provided">No reason provided</option>
                      <option value="Duplicate appointment">Duplicate appointment</option>
                      <option value="Appointment made by mistake">Appointment made by mistake</option>
                      <option value="Client not available">Client not available</option>
                      <option value="Client requested cancellation">Client requested cancellation</option>
                      <option value="Staff not available">Staff not available</option>
                      <option value="Business closed">Business closed</option>
                    </select>
                  </div>
                </div>
                <div className="w-full sm:w-56 flex-shrink-0">
                  <div className="bg-white border border-[#E5E7E1] rounded-xl p-5">
                    <h3 className="font-bold text-[#1A2421] mb-3">Cancellation details</h3>
                    {cancelDialog.booking_group_id && sidebarGroup.length > 1 ? (
                      <>
                        <p className="text-xs font-bold text-[#E2725B] mb-2">{sidebarGroup.length} services will be cancelled</p>
                        {sidebarGroup.map((g, i) => (
                          <div key={i} className="flex justify-between text-xs mb-1">
                            <span className="text-[#5C6656] truncate mr-2">{g.service_name}</span>
                            <span className="text-[#1A2421] flex-shrink-0">{'\u00A3'}{(g.price || 0).toFixed(0)}</span>
                          </div>
                        ))}
                        <div className="border-t border-[#E5E7E1] mt-2 pt-2 flex justify-between text-sm">
                          <span className="text-[#5C6656]">Group total</span>
                          <span className="font-medium text-[#1A2421]">{'\u00A3'}{sidebarGroup.reduce((t, g) => t + (g.price || 0), 0).toFixed(0)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-[#5C6656]">Appointment total</span>
                        <span className="font-medium text-[#1A2421]">{'\u00A3'}{(cancelDialog.price || 0).toFixed(0)}</span>
                      </div>
                    )}
                    <div className="border-t border-[#E5E7E1] mt-3 pt-3">
                      <p className="text-xs text-[#5C6656]">No fee will be charged</p>
                    </div>
                    <Button onClick={confirmCancel} className="w-full mt-4 bg-[#E2725B] hover:bg-[#C95A44] text-white rounded-full py-5 font-semibold" data-testid="confirm-cancel-btn">
                      {cancelDialog.booking_group_id && sidebarGroup.length > 1 ? `Cancel ${sidebarGroup.length} services` : 'Cancel appointment'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Notify-client on reschedule dialog (Fresha-style) */}
      {notifyDialog && (
        <NotifyRescheduleDialog
          dialog={notifyDialog}
          onClose={() => { const cb = notifyDialog?.onCancel; setNotifyDialog(null); if (cb) cb(); }}
          onConfirm={(notify) => { const onConfirm = notifyDialog.onConfirm; setNotifyDialog(null); onConfirm(notify); }}
        />
      )}


      {/* No-Show Dialog (Fresha-style) */}
      <Dialog open={!!noShowDialog} onOpenChange={() => setNoShowDialog(null)}>
        <DialogContent className="sm:max-w-2xl border-[#E5E7E1] p-0">
          <div className="p-6 sm:p-8">
            <button onClick={() => setNoShowDialog(null)} className="absolute top-4 right-4 px-4 py-1.5 rounded-full border border-[#E5E7E1] text-sm text-[#1A2421] hover:bg-[#F5F7F0]">Close</button>
            <h2 className="text-2xl font-bold text-[#1A2421] mb-6" style={{ fontFamily: 'Outfit' }}>Are you sure you want to mark this as a no-show?</h2>
            {noShowDialog && (
              <div className="flex gap-6 flex-col sm:flex-row">
                <div className="flex-1 space-y-4">
                  <div className="bg-[#F3F0FF] border border-[#E0D8FF] rounded-lg px-4 py-3 flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full border-2 border-[#7C6BC4] flex items-center justify-center flex-shrink-0"><span className="text-[#7C6BC4] text-xs font-bold">i</span></div>
                    <span className="text-sm text-[#1A2421]">No policy was applied to this appointment</span>
                  </div>
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={noShowNotify} onChange={e => setNoShowNotify(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-[#E5E7E1] text-[#556B2F] accent-[#556B2F]" />
                    <div>
                      <p className="text-sm text-[#1A2421]">Send a no-show notification</p>
                      <p className="text-xs text-[#5C6656]">{noShowDialog.source === 'online' ? 'Online bookings always receive notifications' : 'Client has opted out of notifications'}</p>
                    </div>
                  </label>
                </div>
                <div className="w-full sm:w-56 flex-shrink-0">
                  <div className="bg-white border border-[#E5E7E1] rounded-xl p-5">
                    <h3 className="font-bold text-[#1A2421] mb-3">No-show details</h3>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#5C6656]">Appointment total</span>
                      <span className="font-medium text-[#1A2421]">{'\u00A3'}{(sidebarGroup.length > 1 ? sidebarGroup.reduce((t, g) => t + (g.price || 0), 0) : (noShowDialog.price || 0)).toFixed(0)}</span>
                    </div>
                    <div className="border-t border-[#E5E7E1] mt-3 pt-3">
                      <p className="text-xs text-[#5C6656]">No fee will be charged</p>
                    </div>
                    <Button onClick={confirmNoShow} className="w-full mt-4 bg-[#C0392B] hover:bg-[#A93226] text-white rounded-full py-5 font-semibold" data-testid="confirm-noshow-btn">
                      Set as no-show
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>


      {/* Block / Unblock Client Dialog */}
      <Dialog open={!!blockClientDialog} onOpenChange={() => setBlockClientDialog(null)}>
        <DialogContent className="sm:max-w-lg border-[#E5E7E1] p-0">
          <div className="p-6 sm:p-8">
            <button onClick={() => setBlockClientDialog(null)} className="absolute top-4 right-4 px-4 py-1.5 rounded-full border border-[#E5E7E1] text-sm text-[#1A2421] hover:bg-[#F5F7F0]">Close</button>
            {blockClientDialog?.action === 'block' ? (
              <div className="space-y-5">
                <div>
                  <h2 className="text-xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Block client</h2>
                  <p className="text-sm text-[#5C6656] mt-1">Are you sure you want to block <strong>{blockClientDialog?.clientName}</strong>?</p>
                </div>
                <div className="bg-[#FDE8E8] border border-[#E2725B]/20 rounded-lg px-4 py-3">
                  <p className="text-sm text-[#E2725B] font-medium">Blocking this client will:</p>
                  <ul className="mt-2 space-y-1 text-sm text-[#E2725B]">
                    <li className="flex items-center gap-2"><Ban size={12} /> Prevent them from booking online</li>
                    <li className="flex items-center gap-2"><Ban size={12} /> Blacklist their name, phone and email</li>
                    <li className="flex items-center gap-2"><Ban size={12} /> Mark their profile as blocked</li>
                  </ul>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-[#1A2421]">Reason for blocking</label>
                  <select value={blockReason} onChange={e => setBlockReason(e.target.value)} className="w-full h-10 px-3 border border-[#E5E7E1] rounded-lg text-sm" data-testid="block-reason-select">
                    <option value="">Select a reason...</option>
                    <option value="Repeated no-shows">Repeated no-shows</option>
                    <option value="Abusive behaviour">Abusive behaviour</option>
                    <option value="Late cancellations">Late cancellations</option>
                    <option value="Non-payment">Non-payment</option>
                    <option value="Spam / fake booking">Spam / fake booking</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setBlockClientDialog(null)} className="flex-1 border-[#E5E7E1]">Cancel</Button>
                  <Button className="flex-1 bg-[#E2725B] hover:bg-[#C95A44] text-white" data-testid="confirm-block-btn"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/clients/${blockClientDialog.clientId}/block`, { reason: blockReason }, { withCredentials: true });
                        toast.success(`${blockClientDialog.clientName} has been blocked`);
                        setBlockClientDialog(null);
                        setClientDetail(prev => prev ? { ...prev, is_blocked: true } : null);
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to block'); }
                    }}>Block client</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                <div>
                  <h2 className="text-xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Unblock client</h2>
                  <p className="text-sm text-[#5C6656] mt-1">Are you sure you want to unblock <strong>{blockClientDialog?.clientName}</strong>?</p>
                </div>
                <div className="bg-[#E9EDDF] border border-[#556B2F]/20 rounded-lg px-4 py-3">
                  <p className="text-sm text-[#556B2F]">This will allow the client to book appointments online again. Their previous block reason and history will be cleared.</p>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setBlockClientDialog(null)} className="flex-1 border-[#E5E7E1]">Cancel</Button>
                  <Button className="flex-1 bg-[#556B2F] hover:bg-[#4A5D23] text-white" data-testid="confirm-unblock-btn"
                    onClick={async () => {
                      try {
                        await axios.delete(`${API}/clients/${blockClientDialog.clientId}/block`, { withCredentials: true });
                        toast.success(`${blockClientDialog.clientName} has been unblocked`);
                        setBlockClientDialog(null);
                        setClientDetail(prev => prev ? { ...prev, is_blocked: false } : null);
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to unblock'); }
                    }}>Unblock client</Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>



      {/* Calendar Settings Panel */}
      {calSettingsOpen && (
        <div className="fixed inset-0 z-[60] flex justify-end" data-testid="cal-settings-panel">
          <div className="absolute inset-0 bg-black/20" onClick={() => setCalSettingsOpen(false)} />
          <div className="relative w-[340px] max-w-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7E1]">
              <h2 className="text-lg font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Your calendar settings</h2>
              <button onClick={() => setCalSettingsOpen(false)} className="text-[#5C6656] hover:text-[#1A2421]"><XIcon size={18} /></button>
            </div>
            <div className="p-5 space-y-6 overflow-y-auto flex-1">
              {/* Calendar zoom */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-[#1A2421]">Calendar zoom</label>
                  <span className="text-sm text-[#5C6656]">{slotHeight <= 14 ? 'Small' : slotHeight <= 18 ? 'Medium small' : slotHeight <= 24 ? 'Medium' : slotHeight <= 30 ? 'Medium large' : 'Large'}</span>
                </div>
                <input type="range" min="10" max="40" value={slotHeight}
                  onChange={e => { const v = parseInt(e.target.value); setSlotHeight(v); localStorage.setItem('cal_zoom', v); }}
                  className="w-full h-1.5 bg-[#E5E7E1] rounded-full appearance-none cursor-pointer accent-[#1A2421]"
                  data-testid="cal-zoom-slider" />
                <div className="flex justify-between mt-1">
                  <span className="text-[10px] text-[#5C6656]">Small</span>
                  <span className="text-[10px] text-[#5C6656]">Large</span>
                </div>
              </div>

              <div className="border-t border-[#E5E7E1] pt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-[#1A2421]">Font size</label>
                  <span className="text-sm text-[#5C6656]">{calFontSize}px</span>
                </div>
                <input type="range" min="7" max="16" value={calFontSize}
                  onChange={e => { const v = parseInt(e.target.value); setCalFontSize(v); localStorage.setItem('cal_font_size', v); savePref('cal_font_size', v); }}
                  className="w-full h-1.5 bg-[#E5E7E1] rounded-full appearance-none cursor-pointer accent-[#1A2421]"
                  data-testid="cal-font-slider" />
                <div className="flex justify-between mt-1">
                  <span className="text-[10px] text-[#5C6656]">Small</span>
                  <span className="text-[10px] text-[#5C6656]">Large</span>
                </div>
                <div className="mt-2 p-2.5 rounded-lg border border-[#E5E7E1] bg-[#F9F8F6]">
                  <p className="font-semibold" style={{ fontSize: `${calFontSize}px`, color: '#1A2421' }}>10:00-10:45 Preview Name</p>
                  <p className="uppercase" style={{ fontSize: `${calFontSize - 1}px`, color: '#5C6656' }}>SERVICE NAME</p>
                </div>
              </div>

              <div className="border-t border-[#E5E7E1] pt-4">
                <label className="text-sm font-medium text-[#1A2421] mb-3 block">Confirmed colour</label>
                <div className="grid grid-cols-6 gap-2">
                  {['#556B2F', '#2E86C1', '#8E44AD', '#E67E22', '#1ABC9C', '#2C3E50', '#C0392B', '#27AE60', '#F39C12', '#3498DB', '#E74C3C', '#9B59B6'].map(c => (
                    <button key={c} type="button"
                      onClick={() => { setCalConfirmColor(c); localStorage.setItem('cal_confirm_color', c); savePref('cal_confirm_color', c); }}
                      className={`w-9 h-9 rounded-lg border-2 transition-all ${calConfirmColor === c ? 'border-[#1A2421] scale-110' : 'border-transparent hover:border-[#B0B5A8]'}`}
                      style={{ backgroundColor: c }}
                      data-testid={`color-${c.replace('#', '')}`} />
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <label className="text-xs text-[#5C6656]">Custom:</label>
                  <input type="color" value={calConfirmColor}
                    onChange={e => { setCalConfirmColor(e.target.value); localStorage.setItem('cal_confirm_color', e.target.value); savePref('cal_confirm_color', e.target.value); }}
                    className="w-8 h-8 rounded border border-[#E5E7E1] cursor-pointer" data-testid="custom-color-picker" />
                  <span className="text-xs text-[#5C6656] font-mono">{calConfirmColor}</span>
                </div>
              </div>

              <div className="border-t border-[#E5E7E1] pt-4">
                <p className="text-sm font-medium text-[#1A2421]">Show quick actions on calendar</p>
                <p className="text-xs text-[#5C6656] mt-0.5">Quickly add appointments or blocked time by clicking on a calendar slot</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {notifPanel && (
        <div className="fixed inset-0 z-[60] flex justify-end" data-testid="notification-panel">
          <div className="absolute inset-0 bg-black/20" onClick={() => setNotifPanel(false)} />
          <div className="relative w-[380px] max-w-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7E1]" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 12px)' }}>
              <h2 className="text-base font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('notifications.title')}</h2>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button onClick={async () => {
                    try {
                      await axios.post(`${API}/notifications/mark-read`, { ids: [] }, { withCredentials: true });
                      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                      setUnreadCount(0);
                    } catch {}
                  }} className="text-xs text-[#556B2F] hover:underline" data-testid="mark-all-read">{t('notifications.markAllRead')}</button>
                )}
                <button onClick={() => setNotifPanel(false)} className="text-[#5C6656] hover:text-[#1A2421]"><XIcon size={18} /></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {notifications.length === 0 && (
                <div className="p-8 text-center text-[#5C6656]"><Bell size={32} className="mx-auto mb-2 opacity-30" /><p className="text-sm">{t('notifications.noNotifications')}</p></div>
              )}
              {notifications.map(n => (
                <div key={n.id} className={`flex items-start gap-3 px-4 py-3 border-b border-[#E5E7E1] hover:bg-[#FAFBF7] transition-colors cursor-pointer ${!n.read ? 'bg-[#F5F7F0]' : ''}`}
                  onClick={async () => {
                    if (!n.read) {
                      try { await axios.post(`${API}/notifications/mark-read`, { ids: [n.id] }, { withCredentials: true }); } catch {}
                      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
                      setUnreadCount(prev => Math.max(0, prev - 1));
                    }
                    // Navigate to appointment date and open sidebar
                    if (n.appointment_id && n.appointment_date) {
                      const apptDate = new Date(n.appointment_date + 'T12:00:00');
                      if (!isNaN(apptDate)) setCurrentDate(apptDate);
                      setNotifPanel(false);
                      // Wait for calendar to render with new date, then open the appointment
                      setTimeout(async () => {
                        try {
                          const res = await axios.get(`${API}/appointments/${n.appointment_id}`, { withCredentials: true });
                          if (res.data) openSidebar(res.data);
                        } catch {}
                      }, 500);
                    }
                  }} data-testid={`notif-item-${n.id}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    n.status === 'cancelled' ? 'bg-[#FDE8E8]' :
                    n.status === 'completed' ? 'bg-[#E5E7E1]' :
                    'bg-[#E9EDDF]'
                  }`}>
                    <span className={`text-sm font-bold ${
                      n.status === 'cancelled' ? 'text-[#E2725B]' :
                      n.status === 'completed' ? 'text-[#6B6B66]' :
                      'text-[#556B2F]'
                    }`}>{(n.client_name || n.title || '?')[0].toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-[#1A2421] truncate">{n.title}{n.price ? ` ${money(n.price)}` : ''}</p>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {n.status === 'cancelled' && <span className="text-[9px] font-bold text-[#E2725B] bg-[#FDE8E8] px-1.5 py-0.5 rounded">Cancelled</span>}
                        {n.status === 'completed' && <span className="text-[9px] font-bold text-[#6B6B66] bg-[#E5E7E1] px-1.5 py-0.5 rounded">Done</span>}
                        {!n.read && <div className="w-2 h-2 rounded-full bg-[#556B2F]" />}
                      </div>
                    </div>
                    <p className="text-xs text-[#5C6656] mt-0.5">{n.service_name}</p>
                    <p className="text-[10px] text-[#5C6656]/70 mt-0.5">{n.appointment_date ? (() => { try { return fmt(new Date(n.appointment_date + 'T12:00:00'), 'EEE d MMM'); } catch { return n.appointment_date; } })() : ''} {n.appointment_time ? `at ${n.appointment_time}` : ''} {n.staff_name ? `· ${n.staff_name}` : ''}</p>
                    <p className="text-[10px] text-[#5C6656]/40 mt-0.5">{n.created_at ? (() => {
                      const diff = (Date.now() - new Date(n.created_at).getTime()) / 1000;
                      if (diff < 60) return t('notifications.justNow');
                      if (diff < 3600) return `${Math.floor(diff/60)}${t('notifications.mAgo')}`;
                      if (diff < 86400) return `${Math.floor(diff/3600)}${t('notifications.hAgo')}`;
                      return `${Math.floor(diff/86400)}${t('notifications.dAgo')}`;
                    })() : ''}</p>
                  </div>
                  <ChevronRight size={14} className="text-[#B0B5A8] mt-3 flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Add a note</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <textarea value={noteText} onChange={e => setNoteText(e.target.value)}
              placeholder="Enter any special instructions or details about the appointment here"
              className="w-full h-32 px-3 py-2.5 text-sm border border-[#E5E7E1] rounded-lg resize-none focus:outline-none focus:border-[#556B2F] text-[#1A2421] placeholder:text-[#B0B5A8]"
              data-testid="note-textarea" autoFocus />
            <p className="text-xs text-[#5C6656]">This note will be visible only to your team members</p>
            <div className="flex justify-end">
              <Button onClick={async () => {
                try {
                  await axios.put(`${API}/appointments/${sidebarAppt?.id}/reschedule`, { notes: noteText }, { withCredentials: true });
                  setSidebarAppt(prev => prev ? { ...prev, notes: noteText } : null);
                  setAppointments(prev => prev.map(a => a.id === sidebarAppt?.id ? { ...a, notes: noteText } : a));
                  toast.success('Note saved');
                  setNoteDialogOpen(false);
                } catch { toast.error('Failed to save note'); }
              }} className="bg-[#1A2421] hover:bg-[#0D1210] text-white px-8" data-testid="save-note-btn">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Viewer Dialog */}
      <Dialog open={!!sidebarPdfUrl} onOpenChange={() => setSidebarPdfUrl(null)}>
        <DialogContent className="max-w-3xl h-[85vh] p-0 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7E1]">
            <h3 className="text-sm font-semibold text-[#1A2421]">Invoice</h3>
            <Button size="sm" variant="outline" onClick={() => window.open(sidebarPdfUrl, '_blank')} className="text-xs h-7 border-[#E5E7E1]">
              <ExternalLink size={12} className="mr-1" /> Open in new tab
            </Button>
          </div>
          <iframe src={sidebarPdfUrl} className="flex-1 w-full" title="Invoice PDF" />
        </DialogContent>
      </Dialog>
      {isOwner && <AIAssistantPanel open={aiPanelOpen} onClose={() => setAiPanelOpen(false)} />}
    </div>
  );
}


// ─── Notify client on reschedule (Fresha-style) ───
function NotifyRescheduleDialog({ dialog, onClose, onConfirm }) {
  const { t } = useTranslation();
  const [notify, setNotify] = useState(true);
  if (!dialog) return null;
  const timeLabel = dialog.newTime && dialog.newDate ? ` → ${dialog.newDate} · ${dialog.newTime}` : '';
  return (
    <div className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="notify-reschedule-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('appointments.updateAppointment')}</h3>
          <button onClick={onClose} className="text-[#5C6656] hover:text-[#1A2421]" data-testid="notify-close">✕</button>
        </div>
        <label className="flex items-start gap-2.5 cursor-pointer mb-1">
          <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-[#E5E7E1] text-[#556B2F] accent-[#556B2F]" data-testid="notify-checkbox" />
          <div>
            <p className="text-sm text-[#1A2421]">{t('appointments.notifyAboutReschedule', { name: dialog.clientName })}</p>
            <p className="text-xs text-[#5C6656]">{t('appointments.notifyReschedDesc', { name: dialog.clientName, to: timeLabel })}</p>
          </div>
        </label>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-[#E5E7E1] text-sm text-[#1A2421] hover:bg-[#F5F7F0]" data-testid="notify-cancel">{t('appointments.cancel')}</button>
          <button onClick={() => onConfirm(notify)} className="px-5 py-2 rounded-full bg-[#1A2421] text-white text-sm hover:bg-[#0D1210]" data-testid="notify-update">{t('appointments.update')}</button>
        </div>
      </div>
    </div>
  );
}
